/**
 * Wheel Builder - Alpine.js Components & GraphQL Client
 * 
 * This module provides the interactive wheel builder functionality including:
 * - Tab navigation between Complete, Front, Rear, and Rim builds
 * - Dynamic product fetching based on user selections
 * - Progress tracking and validation
 * - Cart integration with animations
 * - Haptic feedback for mobile devices
 * 
 * @requires Alpine.js
 * @requires Shopify Cart API
 */

// =============================================================================
// HAPTICS MODULE
// =============================================================================
// Haptics support for mobile devices
let hapticModule = null;
let supportsHaptics = false;
/** Single shared promise so `triggerHaptic` can defer until import settles. */
let hapticsLoadPromise = null;
let hapticsImportSettled = false;

// Touch tracking to distinguish scroll from tap
let touchStartY = 0;
let touchStartX = 0;
let touchStartTime = 0;
let isScrolling = false;
const SCROLL_THRESHOLD = 10; // pixels
const TAP_TIME_THRESHOLD = 300; // milliseconds

// Make isScrolling available globally for inline scripts
if (typeof window !== 'undefined') {
  window.wheelbuilderIsScrolling = false;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get Alpine.js data from a DOM element
 * Compatible with Alpine v2 and v3
 * @param {HTMLElement} el - DOM element with Alpine data
 * @returns {Object|null} Alpine data object or null
 */
function getAlpineData(el) {
  if (!el) return null;
  
  // Try Alpine.$data (Alpine v3)
  if (window.Alpine && typeof Alpine.$data === 'function') {
    try {
      return Alpine.$data(el);
    } catch (e) {}
  }
  
  // Try _x_dataStack (Alpine v3 internal)
  if (el._x_dataStack && el._x_dataStack[0]) {
    return el._x_dataStack[0];
  }
  
  // Try __x (Alpine v2)
  if (el.__x && el.__x.$data) {
    return el.__x.$data;
  }
  
  return null;
}

function wheelbuilderReadCfgFromLocation() {
  try {
    const url = new URL(window.location.href);
    const queryCfg = url.searchParams.get("cfg");
    if (queryCfg) return queryCfg;
    if (url.hash && /(^#|[&#])cfg=/.test(url.hash)) {
      const m = url.hash.match(/[?#&]?(?:^#)?cfg=([^&#]+)/);
      if (m && m[1]) return decodeURIComponent(m[1]);
    }
  } catch (_) {}
  return "";
}

function wheelbuilderParseCfgPairs(rawCfg) {
  const out = {};
  if (!rawCfg || typeof rawCfg !== "string") return out;
  rawCfg.split(";").forEach((pair) => {
    const idx = pair.indexOf("=");
    if (idx < 0) return;
    const key = decodeURIComponent(pair.slice(0, idx).trim());
    const val = decodeURIComponent(pair.slice(idx + 1).trim());
    if (key) out[key] = val;
  });
  return out;
}

function wheelbuilderCanonicalTabFromCfg(rawTab) {
  const token = String(rawTab || "")
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  if (!token) return "";
  if (token === "c" || token === "t" || token === "complete" || token === "wheelset") return "complete";
  if (token === "f" || token === "front") return "front";
  if (token === "r" || token === "rear") return "rear";
  if (token === "rim") return "rim";
  return "";
}

function wheelbuilderEnsurePresetLoadingOverlay(rootEl) {
  if (!rootEl || typeof rootEl.querySelector !== "function") return null;
  let overlay = rootEl.querySelector(".wb-preset-loading-mask");
  if (overlay) return overlay;
  overlay = document.createElement("div");
  overlay.className = "wb-preset-loading-mask";
  overlay.setAttribute("aria-hidden", "true");
  overlay.style.cssText = [
    "position:absolute",
    "inset:0",
    "display:none",
    "align-items:center",
    "justify-content:center",
    "background:rgba(255,255,255,0.86)",
    "backdrop-filter:saturate(120%) blur(2px)",
    "z-index:35",
    "pointer-events:auto",
  ].join(";");

  const box = document.createElement("div");
  box.style.cssText = [
    "display:flex",
    "align-items:center",
    "gap:10px",
    "padding:10px 14px",
    "border:1px solid rgba(15,17,20,0.15)",
    "background:#fff",
    "color:#0f1114",
    "font-size:12px",
    "letter-spacing:0.02em",
    "text-transform:uppercase",
  ].join(";");

  const spinner = document.createElement("span");
  spinner.style.cssText = [
    "width:14px",
    "height:14px",
    "border:2px solid rgba(15,17,20,0.25)",
    "border-top-color:#0f1114",
    "border-radius:50%",
    "animation:wheelbuilderPresetSpin 700ms linear infinite",
    "display:inline-block",
  ].join(";");

  const text = document.createElement("span");
  text.textContent = "Loading preset";

  box.appendChild(spinner);
  box.appendChild(text);
  overlay.appendChild(box);

  const currentPosition = window.getComputedStyle(rootEl).position;
  if (!currentPosition || currentPosition === "static") {
    rootEl.style.position = "relative";
  }
  rootEl.appendChild(overlay);

  if (!document.getElementById("wb-preset-loading-mask-style")) {
    const style = document.createElement("style");
    style.id = "wb-preset-loading-mask-style";
    style.textContent = "@keyframes wheelbuilderPresetSpin{to{transform:rotate(360deg)}}";
    document.head.appendChild(style);
  }

  return overlay;
}

function wheelbuilderSetPresetLoadingOverlay(rootEl, enabled) {
  const overlay = wheelbuilderEnsurePresetLoadingOverlay(rootEl);
  if (!overlay) return;
  overlay.style.display = enabled ? "flex" : "none";
}

/** `data-wb-panel-tab` on the form root (theme Liquid); prefer getAttribute for Safari/consistency. */
function wheelbuilderReadPanelTab(formEl) {
  if (!formEl) return "";
  const el =
    typeof formEl.closest === "function"
      ? formEl.closest("[data-wb-panel-tab]") || formEl
      : formEl;
  if (!el || typeof el.getAttribute !== "function") return "";
  const a = el.getAttribute("data-wb-panel-tab");
  if (a != null && String(a).trim() !== "") return String(a).trim();
  const d = el.dataset && el.dataset.wbPanelTab;
  return d != null && String(d).trim() !== "" ? String(d).trim() : "";
}

/**
 * Mobile + desktop: the vertical scrollport for options is always `.drawer-content`
 * (same for wheelset and front/rear/rim — one list, one scroller).
 * @param {HTMLElement|null|undefined} drawerEl `.drawer-content` (or null)
 * @returns {HTMLElement|null}
 */
function wheelbuilderResolveOptionsScrollContainer(drawerEl) {
  if (!drawerEl) return null;
  return drawerEl;
}

/**
 * Completion check for a wheelBuilderForm Alpine data object.
 * Used from wheelBuilderTabs (price row) without calling formData.isEveryOptionSelected() — that method can
 * resolve to the tabs delegate under Alpine proxies and overflow the stack.
 * @param {object|null|undefined} formData
 * @returns {boolean}
 */
function computeWheelBuilderFormEveryOptionSelected(formData) {
  if (!formData || !Array.isArray(formData.completeOptions)) return false;
  const inScopeKeys = formData.availableOptionKeysFromRules;
  const optionsToCheck =
    inScopeKeys && inScopeKeys.size > 0
      ? formData.completeOptions.filter((opt) => inScopeKeys.has(opt.id))
      : formData.completeOptions;
  // [].every(...) is true — never treat "no options to validate" as complete (empty mobile summary).
  if (optionsToCheck.length === 0) return false;
  return optionsToCheck.every((option) => option.value !== "");
}

/** @returns {HTMLElement|null} */
function getVisibleWheelbuilderSummarySheet() {
  const sheets = document.querySelectorAll(".wheelbuilder-mobile-summary-sheet");
  for (const el of sheets) {
    const style = window.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") continue;
    return el;
  }
  return null;
}

/**
 * Full-screen teleported summary: lock sheet scroll to top (stable entry; avoids fighting
 * card reveal animation). Retries while Alpine `x-show` / rows are still painting.
 * @param {ScrollBehavior} [behavior="auto"]
 * @param {number} [attempt=0]
 */
function wheelbuilderScrollMobileSummaryCardToCenter(behavior = "auto", attempt = 0) {
  if (typeof window === "undefined") return;
  const sheet = getVisibleWheelbuilderSummarySheet();
  if (!sheet) {
    if (attempt < 18) {
      requestAnimationFrame(() => wheelbuilderScrollMobileSummaryCardToCenter(behavior, attempt + 1));
    }
    return;
  }
  const scrollPort = sheet.querySelector(".wheelbuilder-mobile-summary-sheet__scroll");
  if (!scrollPort) {
    if (attempt < 18) {
      requestAnimationFrame(() => wheelbuilderScrollMobileSummaryCardToCenter(behavior, attempt + 1));
    }
    return;
  }
  if (Math.abs(scrollPort.scrollTop) <= 2) return;
  scrollPort.scrollTo({ top: 0, behavior });
}

/** `window.innerWidth` and Alpine `isMobileViewport` can disagree (DevTools, delayed resize). Prefer OR. */
function wheelbuilderIsMobileViewport(tabsData) {
  if (typeof window === "undefined") return false;
  const byWidth = window.innerWidth <= 768;
  if (tabsData && typeof tabsData.isMobileViewport === "boolean") {
    return Boolean(tabsData.isMobileViewport) || byWidth;
  }
  return byWidth;
}

/** Mobile drawer wizard (chooser → options → summary), including tablet widths where `isMobileViewport` is false. */
function wheelbuilderMobileWizardActive(tabsData) {
  if (!tabsData) return false;
  return wheelbuilderIsMobileViewport(tabsData) || !!tabsData.mobileShowOptions;
}

/** Mobile summary / loading pipeline. Enable: `localStorage.setItem('wheelbuilderDebugSummary','1')` + reload, or add `?wb_debug_summary=1` to the URL. */
function wheelbuilderDebugSummaryEnabled() {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage?.getItem("wheelbuilderDebugSummary") === "1") return true;
    return /[?&]wb_debug_summary=1(?:&|$)/.test(String(window.location?.search || ""));
  } catch (_) {
    return false;
  }
}

function wbDebugSummary(tag, payload) {
  if (!wheelbuilderDebugSummaryEnabled()) return;
  const t =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now().toFixed(0)
      : String(Date.now());
  if (payload !== undefined) {
    console.log(`[WB summary @${t}ms] ${tag}`, payload);
  } else {
    console.log(`[WB summary @${t}ms] ${tag}`);
  }
}

function wbDebugSummaryForm(form, tag, extra) {
  if (!wheelbuilderDebugSummaryEnabled() || !form) return;
  const tabs = typeof form._getTabsData === "function" ? form._getTabsData() : null;
  const rowCount = Array.isArray(form.mobileSummarySheetRows) ? form.mobileSummarySheetRows.length : 0;
  wbDebugSummary(tag, {
    ...extra,
    selectedTab: form.selectedTab,
    loadingOperations: form.loadingOperations,
    loadingComplete: form.loadingComplete,
    isFetchingProducts: form.isFetchingProducts,
    loading: form.loading,
    canShowMobileSummary: typeof form.canShowMobileSummary === "function" ? form.canShowMobileSummary() : null,
    isReadyForSummary: typeof form.isReadyForSummary === "function" ? form.isReadyForSummary() : null,
    pricingLoading: form.pricing?.loading,
    summaryRows: rowCount,
    everyOptionSelected: typeof form.isEveryOptionSelected === "function" ? form.isEveryOptionSelected() : null,
    wbSheetOpen: typeof form.wbMobileSummarySheetOpen === "function" ? form.wbMobileSummarySheetOpen() : null,
    tabsMobileStep: tabs?.mobileStep,
    tabsIsSummaryReady: tabs?.isSummaryReady,
    tabsIsBuildResolving: tabs?.isBuildResolving,
    progress: tabs?.progress,
  });
}

function wbDebugSummaryTabs(tabs, tag, extra) {
  if (!wheelbuilderDebugSummaryEnabled() || !tabs) return;
  const f = typeof tabs.getActiveFormData === "function" ? tabs.getActiveFormData() : null;
  wbDebugSummary(tag, {
    ...extra,
    mobileStep: tabs.mobileStep,
    mobileShowOptions: tabs.mobileShowOptions,
    mobileForceOptions: tabs.mobileForceOptions,
    isSummaryReady: tabs.isSummaryReady,
    isBuildResolving: tabs.isBuildResolving,
    progress: tabs.progress,
    selectedTab: tabs.selectedTab,
    formSelectedTab: f?.selectedTab,
    formCanShowMobileSummary: typeof f?.canShowMobileSummary === "function" ? f.canShowMobileSummary() : null,
    formWbSheetOpen: typeof f?.wbMobileSummarySheetOpen === "function" ? f.wbMobileSummarySheetOpen() : null,
  });
}

/**
 * Rules index `options` / `configIndices` keys may be snake_case (Liquid) or camelCase (API).
 * Resolve the bucket that exists on `obj` for a form field id (always snake_case from theme).
 */
function wheelbuilderRulesIndexPickKey(obj, fieldId) {
  if (!obj || fieldId == null) return undefined;
  const id = String(fieldId);
  if (Object.prototype.hasOwnProperty.call(obj, id)) return obj[id];
  const camel = id.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  if (camel !== id && Object.prototype.hasOwnProperty.call(obj, camel)) return obj[camel];
  return obj[id];
}

/**
 * Option groups in the current tab. Prefer `.wheelbuilder-option-groups` (per-tab count).
 * @returns {{ n: number, fromIndex: number, fromEnd: number }}
 *   n=0: invalid. fromIndex=-1: element not in list. fromEnd=999: invalid/unknown.
 */
function wheelbuilderOptionGroupListMetrics(element) {
  if (!element) return { n: 0, fromIndex: -1, fromEnd: 999 };
  let groups;
  const optionGroupsRoot = element.closest(".wheelbuilder-option-groups");
  if (optionGroupsRoot) {
    groups = optionGroupsRoot.querySelectorAll("[data-option-group]");
  } else {
    const panel = element.closest("section[role=tabpanel]");
    if (!panel) return { n: 0, fromIndex: -1, fromEnd: 999 };
    groups = panel.querySelectorAll("[data-option-group]");
  }
  if (groups.length < 1) return { n: 0, fromIndex: -1, fromEnd: 999 };
  const i = Array.prototype.indexOf.call(groups, element);
  if (i < 0) return { n: groups.length, fromIndex: -1, fromEnd: 999 };
  return { n: groups.length, fromIndex: i, fromEnd: groups.length - 1 - i };
}

/**
 * 0 = last [data-option-group] in the tab, 1 = second-to-last, …
 * (Legacy alias — prefer `wheelbuilderOptionGroupListMetrics`.)
 */
function wheelbuilderOptionGroupFromEnd(element) {
  return wheelbuilderOptionGroupListMetrics(element).fromEnd;
}

/**
 * Mobile drawer: last N groups (fromEnd 0…3) get a softer vertical anchor (0.3); not the
 * same as bottom-anchored "end" (that is only for fromEnd 0–1, handled inside the scroller).
 */
const WB_SCROLL_BOTTOM_GROUPS = 3;

/**
 * `scrollIntoView` `block` (desktop) — when there are many steps, stop forcing *center* for the
 * lower half of the list: use `nearest` so the drawer doesn’t fight a tall column; last two use `end`.
 * @param {{ n: number, fromIndex: number, fromEnd: number }} m
 * @returns {ScrollLogicalPosition}
 */
function wheelbuilderOptionGroupScrollBlockFromMetrics(m) {
  if (m.n < 1 || m.fromIndex < 0) return "center";
  if (m.fromEnd <= 1) return "end";
  if (m.n <= 4) {
    return m.fromEnd <= 3 ? "nearest" : "center";
  }
  const p = m.n > 1 ? m.fromIndex / (m.n - 1) : 0;
  if (m.fromEnd <= 3) return "nearest";
  if (m.n >= 6 && p > 0.4) return "nearest";
  if (p >= 0.5) return "nearest";
  return "center";
}

/**
 * Desktop `scrollIntoView` `block` (see `wheelbuilderOptionGroupScrollBlockFromMetrics`).
 * @param {HTMLElement} element
 * @returns {ScrollLogicalPosition}
 */
function wheelbuilderOptionGroupScrollIntoViewBlock(element) {
  return wheelbuilderOptionGroupScrollBlockFromMetrics(wheelbuilderOptionGroupListMetrics(element));
}

/** Product JSON / cart-added sheet image helper. */
function wheelbuilderProductImageUrl(product) {
  if (!product) return "";
  if (typeof product.featured_image === "string" && product.featured_image) {
    return product.featured_image;
  }
  if (product.featured_image?.src) return product.featured_image.src;
  if (Array.isArray(product.images) && product.images.length > 0) {
    const img = product.images[0];
    return typeof img === "string" ? img : img?.src || "";
  }
  return "";
}

/** Mobile-only guard for builder cart-added confirmation sheet. */
function wheelbuilderMobileCartAddedEnabled() {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 768;
}

/**
 * Mobile `.drawer-content` scroll: center short groups; top-align tall groups so the
 * accordion heading stays visible when many options exceed the scrollport.
 * @param {HTMLElement} element Usually [data-option-group]
 * @param {HTMLElement} scrollContainer `.drawer-content`
 * @param {ScrollBehavior} [behavior="auto"] Use "auto" for fast programmatic scrolls
 * @param {number} [topOffset=10] Gap above the group when top-aligning (matches scroll-padding-top)
 */
function wheelbuilderMobileDrawerScrollGroupToViewportCenter(
  element,
  scrollContainer,
  behavior = "auto",
  topOffset = 10
) {
  if (!element || !scrollContainer) return;
  const containerH = scrollContainer.clientHeight;
  if (containerH < 48) return;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const elementRect = element.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const currentScroll = scrollContainer.scrollTop;
      const relativeTop = elementRect.top - containerRect.top;
      const elTopInContent = currentScroll + relativeTop;
      const maxScroll = Math.max(0, scrollContainer.scrollHeight - containerH);

      let targetScroll;
      if (elementRect.height > containerH) {
        targetScroll = elTopInContent - topOffset;
      } else {
        const elCenterInContent = elTopInContent + elementRect.height / 2;
        targetScroll = elCenterInContent - containerH * 0.5;
      }

      targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

      if (Math.abs(targetScroll - currentScroll) > 1) {
        scrollContainer.scrollTo({ top: targetScroll, behavior });
      }
    });
  });
}

/**
 * @param {HTMLElement} container
 * @param {Element|null|undefined} restoreFocusEl
 */
function createWheelbuilderFocusTrap(container, restoreFocusEl) {
  const selector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusable = () =>
    Array.from(container.querySelectorAll(selector)).filter(
      (el) => el.offsetWidth > 0 && el.offsetHeight > 0 && !el.closest("[inert]")
    );

  function onKeydown(e) {
    if (e.key !== "Tab") return;
    const nodes = focusable();
    if (nodes.length === 0) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return {
    activate() {
      document.addEventListener("keydown", onKeydown, true);
      const edit = container.querySelector("[data-wb-summary-edit]");
      const nodes = focusable();
      (edit || nodes[0])?.focus({ preventScroll: true });
    },
    deactivate() {
      document.removeEventListener("keydown", onKeydown, true);
      if (restoreFocusEl && typeof restoreFocusEl.focus === "function") {
        try {
          restoreFocusEl.focus({ preventScroll: true });
        } catch (_) {}
      }
    },
  };
}

/** @returns {boolean} */
function wheelbuilderPrefersReducedMotion() {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (_) {
    return false;
  }
}

/**
 * How much of the summary box is visible in the viewport, with a top inset for sticky header (area ratio).
 * @param {HTMLElement} summaryEl
 * @param {number} headerPx
 */
function wheelbuilderMeasureSummaryVisibility(summaryEl, headerPx) {
  const r = summaryEl.getBoundingClientRect();
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const clipTop = Math.max(r.top, headerPx);
  const clipBottom = Math.min(r.bottom, vh);
  const clipLeft = Math.max(r.left, 0);
  const clipRight = Math.min(r.right, vw);
  const visW = Math.max(0, clipRight - clipLeft);
  const visH = Math.max(0, clipBottom - clipTop);
  const visArea = visW * visH;
  const elArea = Math.max(r.width * r.height, 1);
  return {
    ratio: visArea / elArea,
    topClear: r.top >= headerPx - 0.5,
    rect: r,
  };
}

/**
 * Desktop spec: gentle scroll only if summary is under-exposed (<40% area) or clipped under sticky header.
 * @param {HTMLElement} summaryEl
 */
function wheelbuilderScrollDesktopSummaryIntoView(summaryEl) {
  const HEADER = 72;
  const MIN_RATIO = 0.4;
  const motion = wheelbuilderPrefersReducedMotion();

  const applyScroll = (delta) => {
    window.scrollTo({
      top: Math.max(0, window.scrollY + delta),
      behavior: motion ? "auto" : "smooth",
    });
  };

  let pass = 0;
  const run = () => {
    let { ratio, topClear, rect } = wheelbuilderMeasureSummaryVisibility(summaryEl, HEADER);
    if (ratio >= MIN_RATIO && topClear) return;
    if (pass >= 2) return;
    pass += 1;

    let delta = 0;
    if (!topClear) {
      delta = rect.top - HEADER;
    } else if (rect.bottom > window.innerHeight - 8) {
      delta = rect.bottom - window.innerHeight + 24;
    } else if (ratio < MIN_RATIO) {
      delta = rect.top - HEADER - 48;
    }

    applyScroll(delta);

    const again = () => {
      const m = wheelbuilderMeasureSummaryVisibility(summaryEl, HEADER);
      if (m.ratio >= MIN_RATIO && m.topClear) return;
      if (pass >= 2) return;
      run();
    };

    if (motion) {
      requestAnimationFrame(() => requestAnimationFrame(again));
    } else {
      setTimeout(again, 400);
    }
  };

  run();
}

/** Desktop: align summary vertical center with the viewport region below the sticky header (when `.drawer-content` does not overflow). */
function wheelbuilderScrollDesktopSummaryToWindowCenter(summaryEl, behavior = "auto") {
  if (!summaryEl || typeof window === "undefined") return;
  const HEADER = 72;
  const useBehavior = wheelbuilderPrefersReducedMotion() ? "auto" : behavior;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const rect = summaryEl.getBoundingClientRect();
      if (rect.height < 8 && rect.width < 8) return;
      const viewH = window.innerHeight;
      const usableMid = HEADER + (viewH - HEADER) / 2;
      const elMid = rect.top + rect.height / 2;
      const delta = elMid - usableMid;
      if (Math.abs(delta) < 16) return;
      window.scrollTo({
        top: Math.max(0, window.scrollY + delta),
        behavior: useBehavior,
      });
    });
  });
}

/** Intentional no-op: we do not `window.scrollTo` the product hero; only `.drawer-content` scrolls. */
function wheelbuilderScrollBuilderMediaIntoView() {}

async function initHaptics() {
  if (hapticsLoadPromise) return hapticsLoadPromise;
  hapticsLoadPromise = (async () => {
    try {
      const hapticsModule = await import("https://esm.sh/ios-haptics");
      hapticModule = hapticsModule.haptic;
      supportsHaptics = hapticsModule.supportsHaptics;
    } catch (error) {
      console.warn("[WheelBuilder] Haptics not available:", error);
      supportsHaptics = false;
      hapticModule = null;
    } finally {
      hapticsImportSettled = true;
    }
  })();
  return hapticsLoadPromise;
}

// Setup touch event listeners to detect scroll vs tap
function setupTouchDetection() {
  if (typeof window === 'undefined') return;
  
  document.addEventListener('touchstart', (e) => {
    touchStartY = e.touches[0].clientY;
    touchStartX = e.touches[0].clientX;
    touchStartTime = Date.now();
    isScrolling = false;
  }, { passive: true });

  document.addEventListener('touchmove', (e) => {
    if (!touchStartY) return;
    
    const deltaY = Math.abs(e.touches[0].clientY - touchStartY);
    const deltaX = Math.abs(e.touches[0].clientX - touchStartX);
    
    // If movement exceeds threshold, it's a scroll
    if (deltaY > SCROLL_THRESHOLD || deltaX > SCROLL_THRESHOLD) {
      isScrolling = true;
      if (typeof window !== 'undefined') {
        window.wheelbuilderIsScrolling = true;
      }
    }
  }, { passive: true });

  document.addEventListener('touchend', () => {
    // Reset after a short delay
    setTimeout(() => {
      touchStartY = 0;
      touchStartX = 0;
      touchStartTime = 0;
      isScrolling = false;
      if (typeof window !== 'undefined') {
        window.wheelbuilderIsScrolling = false;
      }
    }, 100);
  }, { passive: true });
}

function triggerHaptic(type = 'default', force = false) {
  // Don't trigger haptic if user is scrolling (unless forced)
  if (!force && isScrolling) return;

  /* If the dynamic import hasn't finished yet, the first taps (reset,
     early add-to-cart) used to hit `!hapticModule` and exit silently.
     Re-run once the module promise settles — same types/force flags.
     After `hapticsImportSettled` we never re-queue (avoids infinite
     microtask loops when import fails and `hapticModule` stays null). */
  if (!hapticModule && !hapticsImportSettled && hapticsLoadPromise) {
    hapticsLoadPromise
      .then(() => {
        triggerHaptic(type, force);
      })
      .catch(() => {});
    return;
  }
  
  // iOS haptics via ios-haptics library (priority)
  if (supportsHaptics && hapticModule) {
    try {
      switch (type) {
        case 'confirm':
          hapticModule.confirm();
          break;
        case 'error':
          hapticModule.error();
          break;
        default:
          hapticModule();
      }
      return; // Exit if iOS haptics worked
    } catch (error) {
      // Fall through to navigator.vibrate
    }
  }
  
  // Fallback for Android/Web using navigator.vibrate (very light)
  if (navigator.vibrate) {
    switch (type) {
      case 'confirm':
        /* Stronger pattern when forcing (success / checkout) so post-async
           taps read as clearly as reset's error pattern on Android. */
        if (force) navigator.vibrate([8, 60, 8]);
        else navigator.vibrate(5);
        break;
      case 'error':
        navigator.vibrate([3, 10, 3]); // Light pattern for errors
        break;
      default:
        navigator.vibrate(1); // Barely perceptible for selections
    }
  }
}

// Expose triggerHaptic globally for inline event handlers
if (typeof window !== 'undefined') {
  window.triggerHaptic = triggerHaptic;
}

// Initialize haptics and touch detection on load
if (typeof window !== 'undefined') {
  initHaptics();
  setupTouchDetection();

  /* === bfcache hygiene for the cart CTA =========================
     When the user taps "Checkout" we flip `isCheckingOut` true so
     the button shows the spinner + "Going to checkout…" while the
     browser navigates. If they then tap Back, mobile Safari / Chrome
     restore this page from bfcache instead of re-instantiating it —
     Alpine `init()` does NOT re-run, so any flag we left in flight
     stays true and the button looks frozen.

     On `pageshow` with `event.persisted === true` we walk every
     `wheelBuilderForm` Alpine root and clear the transient checkout
     flag (plus `addToCartSubmitting` / `loading` for symmetry —
     those should be false after a real navigation, but bfcache can
     restore mid-state if the user backed out before the navigation
     committed). We also clear any pending watchdog timer.

     The `[x-data*="wheelBuilderForm"]` selector is intentionally
     loose — wheel builder forms are instantiated in multiple panels
     (wheelset / front / rear / rim), each with their own Alpine
     scope; we want them all clean. `__x` is the Alpine v3 internal
     accessor for the component proxy; we don't have a public
     getter so we fall back to scanning `_x_dataStack`. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    try {
      const roots = document.querySelectorAll('[x-data*="wheelBuilderForm"]');
      roots.forEach((el) => {
        const stack = el._x_dataStack;
        if (!Array.isArray(stack) || !stack.length) return;
        const data = stack[0];
        if (!data) return;
        if (data.isCheckingOut) data.isCheckingOut = false;
        if (data.addToCartSubmitting) data.addToCartSubmitting = false;
        if (data.loading) data.loading = false;
        if (data._checkoutWatchdogId) {
          clearTimeout(data._checkoutWatchdogId);
          data._checkoutWatchdogId = null;
        }
      });
      document.dispatchEvent(new CustomEvent('wheelbuilder:form-state-changed'));
    } catch (_) {
      /* Never let a bfcache cleanup throw — the page is otherwise fine. */
    }
  });
}

// =============================================================================
// SHOPIFY GRAPHQL CLIENT
// =============================================================================
// Lightweight GraphQL client for Storefront API requests

const GRAPHQL_CLIENT_NAME = "GraphQL Client";
const GRAPHQL_ERROR_MESSAGE = "An error occurred while fetching from the API. Review 'graphQLErrors' for details.";
const UNEXPECTED_CONTENT_TYPE = "Response returned unexpected Content-Type:";
const UNKNOWN_ERROR = "An unknown error has occurred. The API did not return a data object or any errors in its response.";

const CONTENT_TYPES = {
  json: "application/json",
  multipart: "multipart/mixed",
};

const SDK_HEADERS = {
  variant: "X-SDK-Variant",
  version: "X-SDK-Version",
};

const SDK_VALUES = {
  variant: "shopify-graphql-client",
  version: "0.10.3",
};

const DEFAULT_RETRY_WAIT = 1000;
const RETRIABLE_STATUS_CODES = [429, 503];
const DEFER_PATTERN = /@(defer)\b/i;
const BOUNDARY_PATTERN = /boundary="?([^=";]+)"?/i;

function prefixErrorMessage(message, prefix = GRAPHQL_CLIENT_NAME) {
  return message.startsWith(`${prefix}`) ? message : `${prefix}: ${message}`;
}

function extractErrorMessage(error) {
  return error instanceof Error ? error.message : JSON.stringify(error);
}

function extractErrorCause(error) {
  return error instanceof Error && error.cause ? error.cause : undefined;
}

function flattenErrors(responses) {
  return responses.flatMap(({ errors }) => errors ?? []);
}

function validateRetries({ client, retries }) {
  if (retries !== undefined && (typeof retries !== "number" || retries < 0 || retries > 3)) {
    throw new Error(
      `${client}: The provided "retries" value (${retries}) is invalid - it cannot be less than 0 or greater than 3`
    );
  }
}

function wrapInKey(key, value) {
  return value &&
    (typeof value !== "object" ||
      Array.isArray(value) ||
      (typeof value === "object" && Object.keys(value).length > 0))
    ? { [key]: value }
    : {};
}

function buildNestedObject(path, value) {
  if (path.length === 0) return value;
  const nested = { [path.pop()]: value };
  return path.length === 0 ? nested : buildNestedObject(path, nested);
}

function deepMerge(target, source) {
  return Object.keys(source || {}).reduce(
    (merged, key) =>
      (typeof source[key] === "object" || Array.isArray(source[key])) && target[key]
        ? ((merged[key] = deepMerge(target[key], source[key])), merged)
        : ((merged[key] = source[key]), merged),
    Array.isArray(target) ? [...target] : { ...target }
  );
}

function mergeObjects(objects) {
  return objects.slice(1).reduce(deepMerge, { ...objects[0] });
}

function createRetryableFetch({
  clientLogger,
  customFetchApi = fetch,
  client = GRAPHQL_CLIENT_NAME,
  defaultRetryWaitTime = DEFAULT_RETRY_WAIT,
  retriableCodes = RETRIABLE_STATUS_CODES,
}) {
  const retryFetch = async (requestParams, attempt, maxRetries) => {
    const nextAttempt = attempt + 1;
    const maxAttempts = maxRetries + 1;
    let response;

    try {
      response = await customFetchApi(...requestParams);
      clientLogger({
        type: "HTTP-Response",
        content: { requestParams, response },
      });

      if (!response.ok && retriableCodes.includes(response.status) && nextAttempt <= maxAttempts) {
        throw new Error();
      }
      return response;
    } catch (error) {
      if (nextAttempt <= maxAttempts) {
        const retryAfter = response?.headers.get("Retry-After");
        await delay(retryAfter ? parseInt(retryAfter, 10) : defaultRetryWaitTime);
        clientLogger({
          type: "HTTP-Retry",
          content: { requestParams, lastResponse: response, retryAttempt: attempt, maxRetries },
        });
        return retryFetch(requestParams, nextAttempt, maxRetries);
      }
      throw new Error(
        prefixErrorMessage(
          `${maxRetries > 0 ? `Attempted maximum number of ${maxRetries} network retries. Last message - ` : ""}${extractErrorMessage(error)}`,
          client
        )
      );
    }
  };
  return retryFetch;
}

async function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function createGraphQLClient({
  headers,
  url,
  customFetchApi = fetch,
  retries = 0,
  logger,
}) {
  validateRetries({ client: GRAPHQL_CLIENT_NAME, retries });

  const config = { headers, url, retries };
  const clientLogger = createLogger(logger);
  const retryableFetch = createRetryableFetch({
    customFetchApi,
    clientLogger,
    defaultRetryWaitTime: DEFAULT_RETRY_WAIT,
  });
  const fetchFunction = createFetchFunction(retryableFetch, config);
  const requestFunction = createRequestFunction(fetchFunction);
  const requestStreamFunction = createRequestStreamFunction(fetchFunction);

  return {
    config,
    fetch: fetchFunction,
    request: requestFunction,
    requestStream: requestStreamFunction,
  };
}

function createLogger(logger) {
  return (logEntry) => {
    if (logger) logger(logEntry);
  };
}

async function parseJSONResponse(response) {
  const { errors, data, extensions } = await response.json();
  return {
    ...wrapInKey("data", data),
    ...wrapInKey("extensions", extensions),
    ...(errors || !data
      ? {
          errors: {
            networkStatusCode: response.status,
            message: prefixErrorMessage(errors ? GRAPHQL_ERROR_MESSAGE : UNKNOWN_ERROR),
            ...wrapInKey("graphQLErrors", errors),
            response,
          },
        }
      : {}),
  };
}

function createFetchFunction(retryableFetch, { url, headers, retries }) {
  return async (query, options = {}) => {
    const { variables, headers: customHeaders, url: customUrl, retries: customRetries } = options;
    const body = JSON.stringify({ query, variables });

    validateRetries({ client: GRAPHQL_CLIENT_NAME, retries: customRetries });

    const mergedHeaders = Object.entries({ ...headers, ...customHeaders }).reduce(
      (acc, [key, value]) => {
        acc[key] = Array.isArray(value) ? value.join(", ") : value.toString();
        return acc;
      },
      {}
    );

    if (!mergedHeaders[SDK_HEADERS.variant] && !mergedHeaders[SDK_HEADERS.version]) {
      mergedHeaders[SDK_HEADERS.variant] = SDK_VALUES.variant;
      mergedHeaders[SDK_HEADERS.version] = SDK_VALUES.version;
    }

    return retryableFetch(
      [
        customUrl ?? url,
        {
          method: "POST",
          headers: mergedHeaders,
          body,
        },
      ],
      1,
      customRetries ?? retries
    );
  };
}

function createRequestFunction(fetchFunction) {
  return async (...args) => {
    if (DEFER_PATTERN.test(args[0])) {
      throw new Error(
        prefixErrorMessage("This operation will result in a streamable response - use requestStream() instead.")
      );
    }
    try {
      const response = await fetchFunction(...args);
      const { status, statusText } = response;
      const contentType = response.headers.get("content-type") || "";

      return response.ok
        ? contentType.includes(CONTENT_TYPES.json)
          ? parseJSONResponse(response)
          : {
              errors: {
                networkStatusCode: status,
                message: prefixErrorMessage(`${UNEXPECTED_CONTENT_TYPE} ${contentType}`),
                response,
              },
            }
        : {
            errors: {
              networkStatusCode: status,
              message: prefixErrorMessage(statusText),
              response,
            },
          };
    } catch (error) {
      return {
        errors: {
          message: extractErrorMessage(error),
        },
      };
    }
  };
}

async function* decodeStream(stream) {
  const decoder = new TextDecoder();
  if (stream.body[Symbol.asyncIterator]) {
    for await (const chunk of stream.body) {
      yield decoder.decode(chunk);
    }
  } else {
    const reader = stream.body.getReader();
    try {
      let result;
      while (!(result = await reader.read()).done) {
        yield decoder.decode(result.value);
      }
    } finally {
      reader.cancel();
    }
  }
}

function createBoundaryIterator(stream, boundary) {
  return {
    async *[Symbol.asyncIterator]() {
      try {
        let buffer = "";
        for await (const chunk of stream) {
          buffer += chunk;
          if (buffer.indexOf(boundary) > -1) {
            const lastBoundaryIndex = buffer.lastIndexOf(boundary);
            const parts = buffer
              .slice(0, lastBoundaryIndex)
              .split(boundary)
              .filter((part) => part.trim().length > 0)
              .map((part) => part.slice(part.indexOf("\r\n\r\n") + 4).trim());
            if (parts.length > 0) yield parts;
            buffer = buffer.slice(lastBoundaryIndex + boundary.length);
            if (buffer.trim() === "--") buffer = "";
          }
        }
      } catch (error) {
        throw new Error(`Error occurred while processing stream payload - ${extractErrorMessage(error)}`);
      }
    },
  };
}

function createSimpleIterator(response) {
  return {
    async *[Symbol.asyncIterator]() {
      yield {
        ...(await parseJSONResponse(response)),
        hasNext: false,
      };
    },
  };
}

function parseMultipartChunks(chunks) {
  return chunks
    .map((chunk) => {
      try {
        return JSON.parse(chunk);
      } catch (error) {
        throw new Error(`Error in parsing multipart response - ${extractErrorMessage(error)}`);
      }
    })
    .map((chunk) => {
      const { data, incremental, hasNext, extensions, errors } = chunk;
      if (!incremental) {
        return {
          data: data || {},
          ...wrapInKey("errors", errors),
          ...wrapInKey("extensions", extensions),
          hasNext,
        };
      }
      const incrementalData = incremental.map(({ data: incData, path, errors: incErrors }) => ({
        data: incData && path ? buildNestedObject([...path], incData) : {},
        ...wrapInKey("errors", incErrors),
      }));
      return {
        data: incrementalData.length === 1 ? incrementalData[0].data : mergeObjects(incrementalData.map(({ data }) => data)),
        ...wrapInKey("errors", flattenErrors(incrementalData)),
        hasNext,
      };
    });
}

function validateResponse(errors, data) {
  if (errors.length > 0) {
    throw new Error(GRAPHQL_ERROR_MESSAGE, {
      cause: { graphQLErrors: errors },
    });
  }
  if (Object.keys(data).length === 0) {
    throw new Error(UNKNOWN_ERROR);
  }
}

function createStreamIterator(response, contentType) {
  const boundaryMatch = (contentType ?? "").match(BOUNDARY_PATTERN);
  const boundary = `--${boundaryMatch ? boundaryMatch[1] : "-"}`;

  if (!response.body?.getReader && !response.body[Symbol.asyncIterator]) {
    throw new Error("API multipart response did not return an iterable body", {
      cause: response,
    });
  }

  const decodedStream = decodeStream(response);
  let accumulatedData = {};
  let extensions;

  return {
    async *[Symbol.asyncIterator]() {
      try {
        let hasNext = true;
        for await (const chunks of createBoundaryIterator(decodedStream, boundary)) {
          const parsed = parseMultipartChunks(chunks);
          extensions = parsed.find((p) => p.extensions)?.extensions ?? extensions;
          const errors = flattenErrors(parsed);
          accumulatedData = mergeObjects([accumulatedData, ...parsed.map(({ data }) => data)]);
          hasNext = parsed.slice(-1)[0].hasNext;
          validateResponse(errors, accumulatedData);
          yield {
            ...wrapInKey("data", accumulatedData),
            ...wrapInKey("extensions", extensions),
            hasNext,
          };
        }
        if (hasNext) {
          throw new Error("Response stream terminated unexpectedly");
        }
      } catch (error) {
        const cause = extractErrorCause(error);
        yield {
          ...wrapInKey("data", accumulatedData),
          ...wrapInKey("extensions", extensions),
          errors: {
            message: prefixErrorMessage(extractErrorMessage(error)),
            networkStatusCode: response.status,
            ...wrapInKey("graphQLErrors", cause?.graphQLErrors),
            response,
          },
          hasNext: false,
        };
      }
    },
  };
}

function createRequestStreamFunction(fetchFunction) {
  return async (...args) => {
    if (!DEFER_PATTERN.test(args[0])) {
      throw new Error(
        prefixErrorMessage("This operation does not result in a streamable response - use request() instead.")
      );
    }
    try {
      const response = await fetchFunction(...args);
      const { statusText } = response;
      if (!response.ok) {
        throw new Error(statusText, { cause: response });
      }
      const contentType = response.headers.get("content-type") || "";
      switch (true) {
        case contentType.includes(CONTENT_TYPES.json):
          return createSimpleIterator(response);
        case contentType.includes(CONTENT_TYPES.multipart):
          return createStreamIterator(response, contentType);
        default:
          throw new Error(`${UNEXPECTED_CONTENT_TYPE} ${contentType}`, { cause: response });
      }
    } catch (error) {
      return {
        async *[Symbol.asyncIterator]() {
          const cause = extractErrorCause(error);
          yield {
            errors: {
              message: prefixErrorMessage(extractErrorMessage(error)),
              ...wrapInKey("networkStatusCode", cause?.status),
              ...wrapInKey("response", cause),
            },
            hasNext: false,
          };
        },
      };
    }
  };
}

function validateStoreDomain({ client, storeDomain }) {
  try {
    if (!storeDomain || typeof storeDomain !== "string") {
      throw new Error();
    }
    const trimmed = storeDomain.trim();
    const url = trimmed.match(/^https?:/) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(url);
    parsed.protocol = "https";
    return parsed.origin;
  } catch {
    throw new Error(`${client}: a valid store domain ("${storeDomain}") must be provided`);
  }
}

function validateApiVersion({ client, currentSupportedApiVersions, apiVersion, logger }) {
  const errorPrefix = `${client}: the provided apiVersion ("${apiVersion}")`;
  const supportedVersions = `Currently supported API versions: ${currentSupportedApiVersions.join(", ")}`;
  if (!apiVersion || typeof apiVersion !== "string") {
    throw new Error(`${errorPrefix} is invalid. ${supportedVersions}`);
  }
  const trimmed = apiVersion.trim();
  if (!currentSupportedApiVersions.includes(trimmed)) {
    if (logger) {
      logger({
        type: "Unsupported_Api_Version",
        content: { apiVersion, supportedApiVersions: currentSupportedApiVersions },
      });
    } else {
      console.warn(`${errorPrefix} is likely deprecated or not supported. ${supportedVersions}`);
    }
  }
}

function formatQuarter(quarter) {
  const month = quarter * 3 - 2;
  return month === 10 ? month : `0${month}`;
}

function calculateApiVersion(year, quarter, offset) {
  const month = quarter - offset;
  return month <= 0 ? `${year - 1}-${formatQuarter(month + 4)}` : `${year}-${formatQuarter(month)}`;
}

function getCurrentQuarter() {
  const now = new Date();
  const month = now.getUTCMonth();
  const year = now.getUTCFullYear();
  const quarter = Math.floor(month / 3 + 1);
  return {
    year,
    quarter,
    version: `${year}-${formatQuarter(quarter)}`,
  };
}

function getSupportedApiVersions() {
  const { year, quarter, version } = getCurrentQuarter();
  const nextVersion = quarter === 4 ? `${year + 1}-01` : `${year}-${formatQuarter(quarter + 1)}`;
  return [
    calculateApiVersion(year, quarter, 3),
    calculateApiVersion(year, quarter, 2),
    calculateApiVersion(year, quarter, 1),
    version,
    nextVersion,
    "unstable",
  ];
}

function createHeaderGetter(headers) {
  return (customHeaders) => ({
    ...(customHeaders ?? {}),
    ...headers,
  });
}

function createApiUrlGetter({ getApiUrl, defaultApiUrl }) {
  return (customVersion) => (customVersion ? getApiUrl(customVersion) : defaultApiUrl);
}

function createRequestBuilder({ getHeaders, getApiUrl }) {
  return (query, options) => {
    const args = [query];
    if (options && Object.keys(options).length > 0) {
      const { variables, apiVersion, headers, retries } = options;
      args.push({
        ...(variables ? { variables } : {}),
        ...(headers ? { headers: getHeaders(headers) } : {}),
        ...(apiVersion ? { url: getApiUrl(apiVersion) } : {}),
        ...(retries ? { retries } : {}),
      });
    }
    return args;
  };
}

const STOREFRONT_CLIENT_NAME = "storefront-api-client";
const STOREFRONT_CLIENT_VERSION = "0.3.3";
const STOREFRONT_ACCESS_TOKEN_HEADER = "X-Shopify-Storefront-Access-Token";
const STOREFRONT_PRIVATE_TOKEN_HEADER = "Shopify-Storefront-Private-Token";
const STOREFRONT_SDK_VARIANT_HEADER = "X-SDK-Variant";
const STOREFRONT_SDK_VERSION_HEADER = "X-SDK-Version";
const STOREFRONT_SDK_SOURCE_HEADER = "X-SDK-Variant-Source";
const STOREFRONT_CLIENT_LABEL = "Storefront API Client";

function validatePrivateTokenUsage(privateToken) {
  if (privateToken && typeof window !== "undefined") {
    throw new Error(
      `${STOREFRONT_CLIENT_LABEL}: private access tokens and headers should only be used in a server-to-server implementation. Use the public API access token in nonserver environments.`
    );
  }
}

function validateTokenPresence(publicToken, privateToken) {
  if (!publicToken && !privateToken) {
    throw new Error(`${STOREFRONT_CLIENT_LABEL}: a public or private access token must be provided`);
  }
  if (publicToken && privateToken) {
    throw new Error(`${STOREFRONT_CLIENT_LABEL}: only provide either a public or private access token`);
  }
}

function createStorefrontClient({
  storeDomain,
  apiVersion,
  publicAccessToken,
  privateAccessToken,
  clientName,
  retries = 0,
  customFetchApi,
  logger,
}) {
  const supportedVersions = getSupportedApiVersions();
  const validatedDomain = validateStoreDomain({
    client: STOREFRONT_CLIENT_LABEL,
    storeDomain,
  });
  const validationConfig = {
    client: STOREFRONT_CLIENT_LABEL,
    currentSupportedApiVersions: supportedVersions,
    logger,
  };
  validateApiVersion({ ...validationConfig, apiVersion });
  validateTokenPresence(publicAccessToken, privateAccessToken);
  validatePrivateTokenUsage(privateAccessToken);

  const getApiUrl = createApiUrlBuilder(validatedDomain, apiVersion, validationConfig);
  const config = {
    storeDomain: validatedDomain,
    apiVersion: "2025-04",
    ...(publicAccessToken ? { publicAccessToken } : { privateAccessToken }),
    headers: {
      "Content-Type": CONTENT_TYPES.json,
      Accept: CONTENT_TYPES.json,
      [STOREFRONT_SDK_VARIANT_HEADER]: STOREFRONT_CLIENT_NAME,
      [STOREFRONT_SDK_VERSION_HEADER]: STOREFRONT_CLIENT_VERSION,
      ...(clientName ? { [STOREFRONT_SDK_SOURCE_HEADER]: clientName } : {}),
      ...(publicAccessToken
        ? { [STOREFRONT_ACCESS_TOKEN_HEADER]: publicAccessToken }
        : { [STOREFRONT_PRIVATE_TOKEN_HEADER]: privateAccessToken }),
    },
    apiUrl: getApiUrl(),
    clientName,
  };
  const client = createGraphQLClient({
    headers: config.headers,
    url: config.apiUrl,
    retries,
    customFetchApi,
    logger,
  });
  const getHeaders = createHeaderGetter(config.headers);
  const getApiUrlWithVersion = createApiUrlGetter({ getApiUrl, defaultApiUrl: config.apiUrl });
  const buildRequest = createRequestBuilder({ getHeaders, getApiUrl: getApiUrlWithVersion });

  return Object.freeze({
    config,
    getHeaders,
    getApiUrl: getApiUrlWithVersion,
    fetch: (...args) => client.fetch(...buildRequest(...args)),
    request: (...args) => client.request(...buildRequest(...args)),
    requestStream: (...args) => client.requestStream(...buildRequest(...args)),
  });
}

function createApiUrlBuilder(domain, defaultVersion, validationConfig) {
  return (customVersion) => {
    if (customVersion) {
      validateApiVersion({ ...validationConfig, apiVersion: customVersion });
    }
    const version = (customVersion ?? defaultVersion).trim();
    return `${domain}/api/${version}/graphql.json`;
  };
}

function normalizeShopDomainForStorefront(d) {
  if (!d || typeof d !== "string") return "";
  return d.replace(/^https?:\/\//i, "").split("/")[0].trim();
}

/** Lazy client so token + domain come from theme (`theme.liquid`) and match the live shop (avoids 401). */
let storefrontClientInstance = null;
let storefrontClientCacheKey = "";

function getStorefrontClient() {
  const win = typeof window !== "undefined" ? window : null;
  const tokenFromTheme =
    win && typeof win.__STOREFRONT_ACCESS_TOKEN__ === "string" && win.__STOREFRONT_ACCESS_TOKEN__.trim()
      ? win.__STOREFRONT_ACCESS_TOKEN__.trim()
      : "";
  const publicAccessToken = tokenFromTheme || "3e04c4f9f0dc8b0641e4ff7cfe7b98fb";
  const domainFromWindow = normalizeShopDomainForStorefront(win && win.__SHOPIFY_PERMANENT_DOMAIN__);
  const storeDomain = domainFromWindow || "pybg0i-c0.myshopify.com";
  const cacheKey = `${storeDomain}|${publicAccessToken}|2025-07`;
  if (!storefrontClientInstance || cacheKey !== storefrontClientCacheKey) {
    storefrontClientCacheKey = cacheKey;
    storefrontClientInstance = createStorefrontClient({
      storeDomain,
      apiVersion: "2025-07",
      publicAccessToken,
    });
  }
  return storefrontClientInstance;
}

const WHEELBUILDER_CHUNK_SIZE = 1000;
const WHEELBUILDER_FETCH_PAGE_SIZE = 2000;
const WHEELBUILDER_MAX_RULES = 10000;

/** Cache rules per model handle so we don't re-fetch when navigating back */
const wheelBuilderRulesCache = new Map();
/** Memoize (priorSelections, optionId) -> uniqueValues to avoid re-filtering 10k rules */
const wheelBuilderFilterCache = new Map();

function yieldToMain() {
  return new Promise((r) => requestAnimationFrame(r));
}

function toRule(mo) {
  const fields = Object.entries(mo.fields || {}).map(([key, value]) => ({
    key,
    value: typeof value === "string" ? value : String(value ?? ""),
  }));
  // Export expects front_wheel_product_handle / rear_wheel_product_handle in rule.fields
  const frontHandle = (mo.front_handle != null && String(mo.front_handle).trim()) ? String(mo.front_handle).trim() : "";
  const rearHandle = (mo.rear_handle != null && String(mo.rear_handle).trim()) ? String(mo.rear_handle).trim() : "";
  if (frontHandle) {
    if (!fields.some((f) => f.key === "front_wheel_product_handle")) fields.push({ key: "front_wheel_product_handle", value: frontHandle });
    if (!fields.some((f) => f.key === "front_handle")) fields.push({ key: "front_handle", value: frontHandle });
  }
  if (rearHandle) {
    if (!fields.some((f) => f.key === "rear_wheel_product_handle")) fields.push({ key: "rear_wheel_product_handle", value: rearHandle });
    if (!fields.some((f) => f.key === "rear_handle")) fields.push({ key: "rear_handle", value: rearHandle });
  }
  const rule = {
    handle: mo.handle,
    type: "wheelset_rule",
    fields,
    _fieldMap: Object.fromEntries(fields.map((f) => [f.key, f])),
  };
  return rule;
}

function ensureRuleFieldMaps(rules) {
  (rules || []).forEach((r) => {
    if (!r._fieldMap && r.fields) {
      r._fieldMap = Object.fromEntries((r.fields || []).map((f) => [f.key, f]));
    }
  });
  return rules;
}

/**
 * Convert raw API/server errors into user-facing messages.
 */
function toUserFriendlyError(raw) {
  if (!raw || typeof raw !== "string") return "Spoke chart’s catching its breath—wheel options didn’t load. Try again in a moment.";
  try {
    const parsed = JSON.parse(raw);
    const details = parsed.details || parsed.error || parsed.message || "";
    if (details.includes("column") && details.includes("does not exist")) {
      return "We’re updating wheel data behind the scenes. Give us a minute and refresh.";
    }
    if (details.includes("connect") || details.includes("ECONNREFUSED") || details.includes("timeout")) {
      return "Can’t reach the wheel desk right now—check your connection and roll again.";
    }
  } catch (_) {}
  if (raw.includes("500") || raw.includes("Failed to fetch")) {
    return "Spoke chart’s catching its breath—wheel options didn’t load. Try again in a moment.";
  }
  return "Spoke chart’s catching its breath—wheel options didn’t load. Try again in a moment.";
}

/**
 * Convert product/cart errors into user-facing messages.
 */
function toProductError(raw) {
  if (!raw || typeof raw !== "string") return "That didn’t land—give it another shot.";
  if (raw.includes("handle") && raw.includes("not found")) return "This combo isn’t on the rack right now. Try a different mix.";
  if (raw.includes("variant") || raw.includes("sold out") || raw.includes("unavailable")) return "This build’s off the menu for the moment—tweak an option and try again.";
  if (raw.includes("cart") || raw.includes("add")) return "Couldn’t roll that into your cart—tap again and we’ll try once more.";
  return "That didn’t land—give it another shot.";
}

/**
 * Fetch all meta objects for rim model from meta-objects-api (Postgres). Paginated; max WHEELBUILDER_MAX_RULES.
 * @param {Object|null} selectionParams - unused (bulk fetch: model only)
 */
function getEcomBaseAndPath(baseUrl, apiPath) {
  const base = String(baseUrl || "").trim().replace(/\/+$/, "");
  const path = (apiPath || "/api/product-database/meta-objects").replace(/^\//, "");
  return { base, path };
}

/**
 * Detect the warehouse region the storefront is configured for.
 * Order of precedence:
 *   1. URL `?wb_region=` override — explicit QA flag, beats everything else.
 *   2. `[data-wb-region]` on the page — rendered server-side by Liquid from
 *      `localization.country.iso_code`. Authoritative for real visitors.
 *   3. `window.Shopify.country` — Markets-set; fallback if step 2 is missing
 *      (older themes, custom contexts, JS-only embeds).
 *   4. Default: "us" — anywhere not Canada / EU falls back to the US warehouse.
 *
 * Maps ISO country code → server region name (`canada` | `us` | `eu`).
 */
const WHEELBUILDER_DEFAULT_REGION = "us";
function wbCountryToRegion(country) {
  const c = String(country || "").toUpperCase().trim();
  if (c === "CA") return "canada";
  if (c === "US") return "us";
  const EU_OR_GROUPED = new Set([
    "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE",
    "GB","UK","CH","NO","IS","LI",
  ]);
  if (EU_OR_GROUPED.has(c)) return "eu";
  return "";
}
function wbDetectRegion() {
  try {
    const urlOverride = (typeof location !== "undefined"
      ? new URLSearchParams(location.search).get("wb_region")
      : "")?.trim()?.toLowerCase();
    if (urlOverride) return urlOverride;
    const overrideEl = typeof document !== "undefined" ? document.querySelector?.("[data-wb-region]") : null;
    const dataOverride = overrideEl?.getAttribute?.("data-wb-region")?.trim()?.toLowerCase();
    if (dataOverride) return dataOverride;
    const fromShopify = wbCountryToRegion(window?.Shopify?.country);
    if (fromShopify) return fromShopify;
    return WHEELBUILDER_DEFAULT_REGION;
  } catch (_) {
    return WHEELBUILDER_DEFAULT_REGION;
  }
}

/**
 * Fetch precomputed rules index (options + configIndices + configs + unavailable) for fast client filtering.
 * Replaces loading 10k meta objects when server has /rules endpoint.
 */
async function fetchRulesIndexFromEcomEngine(baseUrl, apiKey, modelHandle = "", apiPath = "", bearerToken = "", region = "") {
  try {
    const base = String(baseUrl || "").trim().replace(/\/+$/, "");
    const pathBase = (apiPath || "/api/product-database/meta-objects").replace(/^\//, "");
    const path = pathBase.replace(/\/?$/, "") + "/rules";
    const url = new URL(`${base}/${path}`);
    if (modelHandle) {
      url.searchParams.set("rim_model", modelHandle);
      url.searchParams.set("model", modelHandle);
    }
    if (region && String(region).trim()) {
      url.searchParams.set("region", String(region).trim().toLowerCase());
    }
    const headers = {
      "Content-Type": "application/json",
      "X-Workspace": "wheels",
      // Bypass ngrok free-tier browser interstitial for storefront fetch() calls.
      "ngrok-skip-browser-warning": "true",
    };
    if (bearerToken?.trim()) headers["Authorization"] = `Bearer ${bearerToken.trim()}`;
    if (apiKey?.trim()) headers["X-Storefront-Api-Key"] = apiKey.trim();
    const res = await fetch(url.toString(), { headers });
    if (!res.ok) {
      if (res.status === 503) return { rulesIndex: null, errors: "Rules index not ready" };
      return { rulesIndex: null, errors: await res.text() };
    }
    const data = await res.json();
    if (!data.options || !data.configs) {
      return { rulesIndex: null, errors: "Invalid rules response" };
    }
    return { rulesIndex: data, errors: null };
  } catch (e) {
    return { rulesIndex: null, errors: e?.message || String(e) };
  }
}

/**
 * Lookup single meta object by selection params. Used when rules index has 1 matching config.
 */
async function fetchLookupFromEcomEngine(baseUrl, apiKey, apiPath, bearerToken, selectionParams) {
  try {
    const base = String(baseUrl || "").trim().replace(/\/+$/, "");
    const pathBase = (apiPath || "/api/product-database/meta-objects").replace(/^\//, "");
    const path = pathBase.replace(/\/?$/, "") + "/lookup";
    const url = new URL(`${base}/${path}`);
    for (const [k, v] of Object.entries(selectionParams || {})) {
      if (v != null && String(v).trim()) url.searchParams.set(k, String(v).trim());
    }
    const headers = {
      "Content-Type": "application/json",
      "X-Workspace": "wheels",
      "ngrok-skip-browser-warning": "true",
    };
    if (bearerToken?.trim()) headers["Authorization"] = `Bearer ${bearerToken.trim()}`;
    if (apiKey?.trim()) headers["X-Storefront-Api-Key"] = apiKey.trim();
    const res = await fetch(url.toString(), { headers });
    if (!res.ok) return { metaObject: null, errors: await res.text() };
    const data = await res.json();
    if (data.ambiguous) return { metaObject: null, ambiguous: true, errors: data.message };
    return { metaObject: data.metaObject || null, errors: null };
  } catch (e) {
    return { metaObject: null, errors: e?.message || String(e) };
  }
}

async function fetchFromEcomEngine(baseUrl, apiKey, modelHandle = "", apiPath = "", bearerToken = "", selectionParams = null) {
  try {
    const { base, path } = getEcomBaseAndPath(baseUrl, apiPath);
    const headers = {
      "Content-Type": "application/json",
      "X-Workspace": "wheels",
      "ngrok-skip-browser-warning": "true",
    };
    if (bearerToken && bearerToken.trim()) {
      headers["Authorization"] = `Bearer ${bearerToken.trim()}`;
    }
    if (apiKey && apiKey.trim()) {
      headers["X-Storefront-Api-Key"] = apiKey.trim();
    }

    const allRules = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore && allRules.length < WHEELBUILDER_MAX_RULES) {
      const url = new URL(`${base}/${path}`);
      if (modelHandle) {
        url.searchParams.set("searchModel", modelHandle);
        url.searchParams.set("rim_model", modelHandle);
        if (path.includes("product-database")) {
          url.searchParams.set("model", modelHandle);
        }
      }
      if (selectionParams && typeof selectionParams === "object") {
        for (const [key, val] of Object.entries(selectionParams)) {
          if (val != null && String(val).trim()) url.searchParams.set(key, String(val).trim());
        }
      }
      if (path.includes("product-database")) {
        url.searchParams.set("limit", String(WHEELBUILDER_FETCH_PAGE_SIZE));
        url.searchParams.set("offset", String(offset));
      }

      const res = await fetch(url.toString(), { headers });
      if (!res.ok) {
        const errBody = await res.text();
        const userMsg = toUserFriendlyError(errBody);
        console.error("[WheelBuilder] Meta Objects API error:", res.status, errBody);
        return {
          rules: allRules.length > 0 ? allRules : [],
          errors: userMsg,
        };
      }
      const data = await res.json();
      const metaObjects = data.metaObjects ?? [];
      const pageRules = metaObjects.map(toRule);
      allRules.push(...pageRules);
      offset += pageRules.length;
      hasMore = pageRules.length >= WHEELBUILDER_FETCH_PAGE_SIZE;
      if (hasMore) await yieldToMain();
    }
    return { rules: allRules, errors: null };
  } catch (error) {
    const errorMessage = error?.message || error?.toString() || "";
    const userMsg = toUserFriendlyError(errorMessage);
    console.error("[WheelBuilder] Meta Objects API fetch error:", errorMessage);
    return { rules: [], errors: userMsg };
  }
}

/**
 * Normalize any handle/label to a comparable key (lowercase, alphanumeric only).
 * Generic — no product-specific names. `deal-al` / `Deal AL` / `deal_al` → `dealal`.
 */
function wbHandleKey(raw) {
  return String(raw || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

/**
 * Rim rule contract: a `rim_rule` metaobject belongs to a product line when its
 * handle starts with the product handle, e.g. product `deal-al` owns
 * `deal-al-29-32` (and legacy compact `dealal-29-32`). Purely handle-based, so
 * no line is special-cased in code.
 */
function wbRimRuleHandleMatchesLine(ruleHandle, productHandle) {
  const lineKey = wbHandleKey(productHandle);
  if (!lineKey) return false;
  return wbHandleKey(ruleHandle).startsWith(lineKey);
}

function wbGetTabsModelRaw(tabsData) {
  const modelRaw = tabsData?.model;
  if (!modelRaw) return "";
  if (typeof modelRaw === "string") return modelRaw.trim();
  return String(modelRaw.handle || modelRaw.value || "").trim();
}

/** Rim tab line identity: product.handle only (never custom.wheelset_model). */
function wbGetTabsRimModelRaw(tabsData) {
  const raw = tabsData?.rimModel;
  if (!raw) return "";
  if (typeof raw === "string") return raw.trim();
  return String(raw.handle || raw.value || "").trim();
}

function wbRimRuleModelRawFromNode(map) {
  return (
    map.model ||
    map.wheelset_model ||
    map.model_handle ||
    map.rim_model ||
    ""
  );
}

async function fetchBuilderRules(ruleType = "wheelset_rule", handleFilter = "") {
  try {
    const storefrontClient = getStorefrontClient();

    let allResults = [];
    let hasMore = true;
    let cursor = null;

    while (hasMore) {
      let response;
      try {
        response = await storefrontClient.request(
          `query GetBuilderRules($type: String!, $entryCount: Int!, $cursor: String) {
            metaobjects(type: $type, first: $entryCount, after: $cursor) {
              edges {
                node {
                  handle
                  type
                  fields {
                    key
                    value
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }`,
          {
            variables: {
              type: ruleType,
              entryCount: 250,
              cursor,
            },
          }
        );
      } catch (requestError) {
        const errorMsg = requestError?.message || requestError?.toString() || 'Unknown request error';
        return { rules: [], errors: errorMsg };
      }

      if (response instanceof Error) {
        return { rules: [], errors: response.message || response.toString() };
      }

      const { data, errors } = response || {};

      if (errors) {
        let errorMessage = '';
        if (Array.isArray(errors)) {
          errorMessage = errors.map(e => {
            if (typeof e === 'string') return e;
            if (e?.message) return e.message;
            if (e?.extensions?.code) return `${e.extensions.code}: ${e.message || JSON.stringify(e)}`;
            return JSON.stringify(e);
          }).join(', ');
        } else if (typeof errors === 'object') {
          errorMessage = errors?.message || JSON.stringify(errors);
        } else {
          errorMessage = String(errors);
        }
        return { rules: [], errors: errorMessage || 'Unknown GraphQL error' };
      }

      if (!data) {
        return { rules: [], errors: 'No data returned from API' };
      }

      const { metaobjects } = data ?? {};
      const { edges, pageInfo } = metaobjects ?? {};
      if (edges) {
        allResults = allResults.concat(edges.map((edge) => edge.node));
      }
      hasMore = pageInfo?.hasNextPage;
      cursor = pageInfo?.endCursor;
    }

    if (handleFilter) {
      const normalizeKey = wbHandleKey;
      const modelKey = normalizeKey(handleFilter);
      const candidateKeys = new Set([modelKey]);

      allResults = allResults.filter((node) => {
        // rim_rule contract: metaobject handle starts with the product handle.
        //   product.handle "deal-al" → rim_rule handles "deal-al-29-32"
        //   (legacy compact "dealal-29-32" also matches via normalized key).
        // Handle-based only — no line is special-cased.
        if (ruleType === "rim_rule") {
          return (
            wbRimRuleHandleMatchesLine(node.handle, handleFilter) &&
            node.type === ruleType
          );
        }

        const map = Object.fromEntries(
          (node.fields || []).map((f) => [f.key, f.value])
        );
        let ruleModel = wbRimRuleModelRawFromNode(map);
        ruleModel = ruleModel || map.handle || "";
        if (typeof ruleModel === "object" && ruleModel !== null) {
          ruleModel = ruleModel.handle || ruleModel.value || "";
        }
        const nodeKey = normalizeKey(ruleModel);
        const equal = candidateKeys.has(nodeKey);
        const contains = Array.from(candidateKeys).some(
          (candidate) =>
            nodeKey.includes(candidate) || candidate.includes(nodeKey)
        );
        return (equal || contains) && node.type === ruleType;
      });
    }

    return { rules: allResults };
  } catch (error) {
    const errorMessage = error?.message || error?.toString() || `Failed to fetch ${ruleType} rules`;
    console.error(`[WheelBuilder] Error fetching ${ruleType}:`, errorMessage);
    return { rules: [], errors: errorMessage };
  }
}

// =============================================================================
// Copy / microcopy (theme: `window.WHEELBUILDER_I18N` from wheelset-builder-v2.liquid)
// =============================================================================
/** Matches `--wb-cart-check-*` on `.wheelbuilder-cart-btn.loading` in wheel-builder.css.
 *  Celebration starts as soon as /cart/add succeeds (drawer refresh is background). */
const WHEELBUILDER_ADDTOCART_CHECK_MS = 560;

const WHEELBUILDER_DEFAULT_COPY = {
  peekCtaStart: "START BUILDING",
  peekCtaReview: "ADD TO CART",
  peekCtaBuilding: "BUILDING…",
  peekCtaContinue: "KEEP GOING",
  peekCtaCheckout: "CHECKOUT",
  building: "Building…",
  addingToCart: "Adding…",
  /* Shown between the user tapping "Checkout" and the new page beginning
     to paint. Without this, the button label stays "CHECKOUT" during the
     navigation window and the tap feels unacknowledged (a common UX
     complaint on slower networks). Reuses the same spinner overlay
     machinery as addingToCart / building so visual rhythm is preserved. */
  checkingOut: "Checking out…",
  validationHintTemplate: "Please select {{ option }} first",
  summaryEditOption: "Change {{ option }}",
  mobileFooterStart: "Start with your build",
  mobileFooterContinue: "Keep rolling",
  mobileFooterUnable: "Unable to build this setup",
};

function mergeWheelbuilderCopy(local = {}) {
  const g = typeof window !== "undefined" && window.WHEELBUILDER_I18N && typeof window.WHEELBUILDER_I18N === "object" ? window.WHEELBUILDER_I18N : {};
  return { ...WHEELBUILDER_DEFAULT_COPY, ...g, ...local };
}

// =============================================================================
// ALPINE.JS COMPONENT: WHEEL BUILDER TABS
// =============================================================================
// Manages tab navigation, progress tracking, and rule fetching
const wheelBuilderTabs = ({ labels, model, rimModel = "", ecomEngineUrl = "", ecomEngineApiKey = "", ecomEngineApiPath = "", ecomEngineBearerToken = "", wbCopy: wbCopyOpt = null }) => ({
  wbCopy: mergeWheelbuilderCopy(wbCopyOpt || undefined),
  selectedId: null,
  selectedTab: null,
  rules: [],
  fetchingRules: false,
  ruleFetchError: null,
  model,
  rimModel: rimModel || "",
  ecomEngineUrl: ecomEngineUrl || "",
  ecomEngineApiKey: ecomEngineApiKey || "",
  ecomEngineApiPath: ecomEngineApiPath || "",
  ecomEngineBearerToken: ecomEngineBearerToken || "",
  resizeHandler: null,
  progress: 0,
  progressRailSteps: 0,
  progressRailVisible: false,
  _progressRailObserver: null,
  isMobileViewport: window.innerWidth <= 768,
  mobileShowOptions: false, // Mobile wizard: false = nav page, true = options page
  selectedTabLabel: '', // Store label for mobile header
  
  // ========================================================================
  // MOBILE DRAWER — 2 states (peek | expanded)
  // Peek keeps product imagery visible. Expanded focuses on the builder flow.
  // ========================================================================
  drawerState: window.innerWidth > 768 ? 'expanded' : 'peek',
  drawerOpen: true,
  isDragging: false,
  startY: 0,
  startTime: 0,
  currentY: 0,
  collapsedOffset: 122,
  hasShownSummary: false,
  /** When true on mobile, show options again while build stays complete (full-screen summary dismissed). */
  mobileForceOptions: false,
  isScreenTransitioning: false,
  screenTransitionTimer: null,
  /** 'forward' (chooser→options→summary) | 'backward' (reverse). Drives the
   *  CSS direction multiplier `--wb-wizard-dir` so page swaps always slide
   *  in from where the user is "coming from". Always set BEFORE flipping
   *  mobileShowOptions / mobileForceOptions so Alpine's transition picks up
   *  the right direction on the very first frame. */
  wizardDirection: 'forward',
  /** Live cart line-item count for the footer cart badge. Updated from
   *  /cart.js on init and on `cart:updated` broadcasts. */
  cartItemCount: 0,
  _cartCountFetchInFlight: false,
  /** Bumped on every `wheelbuilder:form-state-changed` so tabs-scope peek
   *  CTAs re-read form busy/added flags across Alpine trees (parity with hub). */
  _formStateEpoch: 0,
  fetchCartItemCount() {
    if (typeof window === 'undefined') return;
    if (this._cartCountFetchInFlight) return;
    this._cartCountFetchInFlight = true;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        this.cartItemCount = Number(cart?.item_count) || 0;
      })
      .catch(() => {})
      .finally(() => { this._cartCountFetchInFlight = false; });
  },
  _mobileSummaryA11yActive: false,
  _mobileSummaryEscapeHandler: null,
  _mobileSummarySheetTrap: null,
  _mobileSummaryPrevFocus: null,
  _mobileSummaryA11yDeferTimer: null,
  urlPresetConfig: null,
  _urlPresetApplied: false,
  _urlPresetAttemptCount: 0,
  _urlPresetRulesHandler: null,
  _suppressInitialOpenFirstDropdown: false,
  _presetLoadingFailsafeId: null,
  _prevIsSummaryReady: false,
  /** Set when the teleported summary sheet is confirmed in the DOM (drives Alpine x-show; DOM alone is not reactive). */
  summarySheetPainted: false,
  /** ResizeObserver so mobile options scroll height stays correct after layout / tab / drawer changes. */
  _mobileDrawerScrollRO: null,

  init() {
    this.urlPresetConfig = this.parseUrlPresetConfig();
    this._urlPresetApplied = false;
    this._urlPresetAttemptCount = 0;
    if (this._presetLoadingFailsafeId) {
      clearTimeout(this._presetLoadingFailsafeId);
      this._presetLoadingFailsafeId = null;
    }
    this.calculateCollapsedOffset();
    // Cart badge: fetch once now, refresh on any cart update / add.
    this.fetchCartItemCount();
    this._cartCountListener = () => this.fetchCartItemCount();
    document.addEventListener('cart:updated', this._cartCountListener);
    document.addEventListener('cart:items-added', this._cartCountListener);
    document.addEventListener('cart:refresh', this._cartCountListener);
    
    window.addEventListener('resize', () => {
      this.calculateCollapsedOffset();
    }, { passive: true });

    if (typeof IntersectionObserver !== "undefined") {
      const builderSection = this.$root.closest("section.page-width.wheelbuilder") || this.$root;
      this._progressRailObserver = new IntersectionObserver((entries) => {
        for (const e of entries) {
          this.progressRailVisible = e.isIntersecting;
        }
      }, { threshold: 0 });
      this._progressRailObserver.observe(builderSection);
    }
    
    // Apply initial drawer state class
    this.$nextTick(() => {
      this.applyDrawerStateClass();

      const presetTabKey = this.urlPresetConfig?.tab;
      const presetIdx =
        presetTabKey && labels.indexOf(presetTabKey) >= 0 ? labels.indexOf(presetTabKey) : 0;
      const initialId = this.$id("tab", presetIdx + 1);
      /* If we landed with a `?cfg=` preset, suppress the initial-tab `openFirstDropdown`
         that `select()` schedules ~100ms later. Without this, the deferred openFirstDropdown
         races with `_applyUrlPresetConfig`'s explicit accordion management and *overwrites*
         it: the user sees the first accordion (which on a preset is usually already filled
         with the preset's spoke / hub / rim_size) snap open, while the actually-next-needed
         accordion stays collapsed. The flag was previously set only inside
         `_applyUrlPresetConfig` when cfg.tab differs from the current tab — but in the common
         "preset matches default tab" case (the most popular preset URLs), nothing set it,
         and this race was the visible mobile bug. Setting it here unconditionally for any
         preset closes that hole; `_applyUrlPresetConfig` is then the sole owner of which
         accordion is open after preset apply. The flag is consumed (cleared) inside
         `select()` when it fires the 100ms timer; if the timer never fires (rare), the
         flag is also reset by `_abortUrlPresetToCleanState` (line 3342). */
      if (this.urlPresetConfig) {
        this._suppressInitialOpenFirstDropdown = true;
      }
      this.select(initialId, presetIdx, { skipInitialLayoutScroll: true });
      if (this.$refs?.tablistMobile && window.innerWidth <= 768) {
        const mobileId = this.$id("tab-mobile", 1);
        setTimeout(() => {
          this.setIndicatorPosition(mobileId, this.$refs.tablistMobile);
        }, 100);
      }
    });
    this.fetchRules();
    {
      const wbStore = typeof Alpine !== "undefined" && Alpine.store ? Alpine.store("wheelbuilder") : null;
      if (wbStore) wbStore.isMobileViewport = this.isMobileViewport;
    }
    this.resizeHandler = () => {
      this.isMobileViewport = window.innerWidth <= 768;
      const wbStore = typeof Alpine !== "undefined" && Alpine.store ? Alpine.store("wheelbuilder") : null;
      if (wbStore) wbStore.isMobileViewport = this.isMobileViewport;
      if (!this.isMobileViewport) {
        this._teardownMobileDrawerScrollResizeObserver();
        this._clearAllDrawerContentScrollStyles();
        this.summarySheetPainted = false;
        this._teardownMobileSummarySheetA11y();
        this.mobileForceOptions = false;
        this.setDrawerState('expanded');
      } else if (!this.mobileShowOptions) {
        this.setDrawerState('peek');
      }
      this.setIndicatorPosition(this.selectedId);
      if (this.$refs?.tablistMobile && window.innerWidth <= 768) {
        const mobileTabId = this.selectedId?.replace('tab-', 'tab-mobile-') || this.$id("tab-mobile", 1);
        this.setIndicatorPosition(mobileTabId, this.$refs.tablistMobile);
      }
      this.$nextTick(() => {
        requestAnimationFrame(() => this.syncMobileDrawerContentScrollLayout());
      });
    };
    window.addEventListener("resize", this.resizeHandler, { passive: true });
    
    document.addEventListener('wheelbuilder:update-progress', () => {
      this.updateProgress();
      this.syncMobileSheet();
    });
    
    document.addEventListener('wheelbuilder:selections-cleared', () => {
      this.progress = 0;
      this.hasShownSummary = false;
      // Do not reset mobileShowOptions/selectedTabLabel here: this event also fires when
      // switching build type (Front/Rear/etc.) in select(); we must keep the options
      // page and title visible. Only backToMobileNav() returns to the chooser.
    });

    document.addEventListener('wheelbuilder:refetch-with-selections', () => {
      /* Rules load once per model (Postgres bulk or Shopify GraphQL); narrowing is client-side */
    });

    document.addEventListener('wheelbuilder:form-state-changed', () => {
      this._formStateEpoch = (this._formStateEpoch || 0) + 1;
      if (typeof this.syncMobileFooterStore === 'function') this.syncMobileFooterStore();
      this.$nextTick(() => {
        if (this.isMobileViewport) this.syncMobileSheet();
      });
    });

    document.addEventListener("wheelbuilder:rules-loaded", () => {
      this.$nextTick(() => {
        requestAnimationFrame(() => this.syncMobileDrawerContentScrollLayout());
      });
    });

    /* Do NOT register document-level touchmove + preventDefault on .drawer-content: it blocks
       native scrolling on iOS/WebKit and can leave the options list stuck non-scrollable. */

    this._touchCancelUnlock = () => {
      if (this.isDragging) this.handleDragCancel();
    };
    document.addEventListener("touchcancel", this._touchCancelUnlock, { passive: true });

    if (this.urlPresetConfig) {
      this.$nextTick(() => {
        wheelbuilderSetPresetLoadingOverlay(this.$root || this.$el, true);
      });
      this._presetLoadingFailsafeId = setTimeout(() => {
        this._finishUrlPresetLoading();
      }, 4500);
      this._urlPresetRulesHandler = () => {
        this.$nextTick(() => {
          requestAnimationFrame(() => this._applyUrlPresetConfig());
        });
      };
      document.addEventListener("wheelbuilder:rules-loaded", this._urlPresetRulesHandler);
      this.$nextTick(() => {
        requestAnimationFrame(() => this._applyUrlPresetConfig());
      });
    }
    
    // Sync drawerState → drawerOpen + CSS classes
    this.$watch('drawerState', (state) => {
      this.drawerOpen = true;
      this.applyDrawerStateClass();
      const panel = this.getDrawerPanel();
      if (panel && !this.isDragging) {
        panel.style.removeProperty('--drawer-drag');
      }
      this.syncMobileFooterStore();
      this.$nextTick(() => {
        requestAnimationFrame(() => this.syncMobileDrawerContentScrollLayout());
      });
    });

    // Keep Alpine store in sync so form (child) can show/hide mobile footer and summary page
    this.syncMobileFooterStore = () => {
      try {
        const store = typeof Alpine !== 'undefined' && Alpine.store && Alpine.store('wheelbuilder');
        if (store) {
          /* Keep in lockstep with the viewport: teleport x-show uses this flag: if it drifts from window width, the sheet stays hidden while the drawer is in summary (blank body + footer). */
          if (typeof window !== 'undefined') {
            store.isMobileViewport = window.innerWidth <= 768;
            this.isMobileViewport = store.isMobileViewport;
          }
          /* Let CSS (.is-peek) hide the bar in peek; do not also gate on drawerState here or x-show can
             hide the footer while expanded (e.g. after tab/sync timing), leaving Front/Rear with no CTA. */
          store.showMobileFooter = this.mobileShowOptions || this.mobileStep === 'summary';
          /* Summary step follows option completion; teleports/a11y still wait on `isSummaryReady` (`canShowMobileSummary`). */
          store.showSummaryPage = this.mobileStep === 'summary';
        }
      } catch (e) {}
    };
    this.syncMobileFooterStore();
    this.$watch('mobileShowOptions', () => {
      this.syncMobileFooterStore();
      this.$nextTick(() => {
        requestAnimationFrame(() => this.syncMobileDrawerContentScrollLayout());
      });
    });
    this.$watch('mobileStep', (to, from) => {
      this.syncMobileFooterStore();
      if (from && to !== from && window.innerWidth <= 768) {
        /* Step rank decides direction without us having to plumb it from every
         * caller: chooser < options < summary. Lets the rule fire even on
         * indirect step flips (option cleared from summary, build resolved). */
        const rank = { chooser: 0, options: 1, summary: 2, 'cart-added': 3 };
        const goingForward = (rank[to] ?? 0) > (rank[from] ?? 0);
        this.setWizardDirection(goingForward ? 'forward' : 'backward');
        /* Never height-tween the panel. Screen-transition flag only enables
           absolute leave stacking for headers — content uses opacity/transform. */
        if (to === 'summary' || from === 'summary' || to === 'cart-added' || from === 'cart-added') {
          this.beginScreenTransition(320);
        }
      }
    });
    this.$watch('progress', () => this.syncMobileFooterStore());

    this._prevIsSummaryReady = this.isSummaryReady;
    this.$watch(
      () => this.isSummaryReady,
      (ready) => {
        if (ready && !this._prevIsSummaryReady) {
          this.mobileForceOptions = false;
          // Desktop completion: trigger one summary scroll pass when readiness flips true.
          if (typeof window !== "undefined" && window.innerWidth > 768) {
            this.$nextTick(() => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  this.scrollToSummary();
                });
              });
            });
          }
        }
        this._prevIsSummaryReady = ready;
      }
    );

    this.$watch(
      () =>
        wheelbuilderMobileWizardActive(this) &&
        this.mobileStep === "summary" &&
        this.isSummaryReady &&
        !this.mobileForceOptions,
      (open) => {
        if (this._mobileSummaryA11yDeferTimer) {
          clearTimeout(this._mobileSummaryA11yDeferTimer);
          this._mobileSummaryA11yDeferTimer = null;
        }
        if (open) {
          this.summarySheetPainted = false;
          this.refreshSelectedFormSummaryRows();
          /* Teleport + x-show can paint after isSummaryReady flips; a single 100ms tick often ran too early
             (logs: summary step true, sheet still display:none). Poll after rAF until visible or timeout. */
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                let tries = 0;
                const maxTries = 40;
                const poll = () => {
                  this._mobileSummaryA11yDeferTimer = null;
                  const stillOpen =
                    wheelbuilderMobileWizardActive(this) &&
                    this.mobileStep === "summary" &&
                    this.isSummaryReady &&
                    !this.mobileForceOptions;
                  if (!stillOpen) {
                    this.summarySheetPainted = false;
                    return;
                  }
                  if (getVisibleWheelbuilderSummarySheet()) {
                    this.summarySheetPainted = true;
                    wbDebugSummaryTabs(this, "summary sheet: visible in DOM → setup a11y", { tries });
                    this._setupMobileSummarySheetA11y();
                    return;
                  }
                  tries += 1;
                  if (tries >= maxTries) {
                    wbDebugSummaryTabs(this, "summary sheet: NOT visible after poll", { tries: maxTries });
                    return;
                  }
                  this._mobileSummaryA11yDeferTimer = setTimeout(poll, 50);
                };
                poll();
              });
            });
          });
        } else {
          this.summarySheetPainted = false;
          this._teardownMobileSummarySheetA11y();
        }
      }
    );
    
    this.$nextTick(() => {
      setTimeout(() => {
        this.updateProgress();
        this.syncMobileSheet();
      }, 100);
    });

    this.$nextTick(() => {
      this._ensureMobileDrawerScrollResizeObserver();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.syncMobileDrawerContentScrollLayout());
      });
    });

    if (wheelbuilderDebugSummaryEnabled()) {
      wbDebugSummary(
        "HOW TO SHARE LOGS",
        "localStorage.setItem('wheelbuilderDebugSummary','1') then reload, or add ?wb_debug_summary=1 to the URL. Filter the console by [WB summary]. Disable: localStorage.removeItem('wheelbuilderDebugSummary')"
      );
      this.$watch("mobileStep", (to, from) => {
        wbDebugSummaryTabs(this, "tabs mobileStep changed", { from, to });
      });
      this.$watch("isSummaryReady", (to, from) => {
        wbDebugSummaryTabs(this, "tabs isSummaryReady changed", { from, to });
      });
      this.$watch("isBuildResolving", (to, from) => {
        wbDebugSummaryTabs(this, "tabs isBuildResolving changed", { from, to });
      });
    }
  },
  
  applyDrawerStateClass() {
    const panel = this.getDrawerPanel();
    if (!panel) return;
    panel.classList.remove('is-peek', 'is-expanded');
    panel.classList.add(`is-${this.drawerState}`);
  },
  
  setDrawerState(state) {
    if (['peek', 'expanded'].includes(state)) {
      this.drawerState = state;
      if (state === 'peek') {
        this.$nextTick(() => requestAnimationFrame(() => this._refreshPeekCtaPaint()));
      }
    }
  },

  /** Peek CTA can paint blank after expanded→peek mid add/checkout — see hubBuilderTabs. */
  _refreshPeekCtaPaint() {
    if (typeof window === 'undefined' || window.innerWidth > 768) return;
    const panel = this.getDrawerPanel();
    if (!panel || !panel.classList.contains('is-peek')) return;
    const btn = panel.querySelector('.drawer-cta-compact .wheelbuilder-cart-btn');
    if (!btn) return;
    btn.querySelectorAll('.wheelbuilder-cart-btn__text, .wheelbuilder-cart-btn__status').forEach((el) => {
      if (el.style.opacity === '0') el.style.removeProperty('opacity');
    });
    if (btn.classList.contains('loading')) {
      const cart = btn.querySelector('.wheelbuilder-cart-btn__cart');
      if (cart) {
        cart.style.display = 'block';
        cart.style.opacity = '1';
        cart.style.transform = 'scale(1)';
        const poly = cart.querySelector('polyline');
        if (poly) poly.style.strokeDashoffset = '0';
      }
    }
  },

  getDrawerPanel() {
    const r = this.$root || this.$el;
    return this.$refs?.mobileDrawerPanel || (r && typeof r.querySelector === "function" ? r.querySelector(".wheelbuilder-panel") : null);
  },

  /** Tabpanels for this wheel builder only (hub_builder and other sections also use role="tabpanels"). */
  getTabpanelsContainer() {
    const root = this.$root || this.$el;
    if (!root || typeof root.querySelector !== "function") return null;
    return root.querySelector('[role="tabpanels"]');
  },

  beginScreenTransition(duration = 440) {
    this.isScreenTransitioning = true;
    if (this.screenTransitionTimer) {
      clearTimeout(this.screenTransitionTimer);
    }
    this.screenTransitionTimer = setTimeout(() => {
      this.isScreenTransitioning = false;
      this.screenTransitionTimer = null;
    }, duration);
  },

  /** Set BEFORE flipping mobileShowOptions / mobileForceOptions so the CSS
   *  variable `--wb-wizard-dir` resolves before Alpine snapshots enter-from. */
  setWizardDirection(dir) {
    this.wizardDirection = dir === 'backward' ? 'backward' : 'forward';
  },

  openDrawer() {
    this.setDrawerState('expanded');
    if (window.innerWidth <= 768 && this.isSummaryReady) {
      this.mobileForceOptions = false;
    }
    if (window.innerWidth <= 768 && this.mobileShowOptions) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.syncMobileDrawerContentScrollLayout();
          const scrollRoot = wheelbuilderResolveOptionsScrollContainer(this.getActiveDrawerContent());
          if (this.isSummaryReady) {
            this.scrollToSummary();
          } else if (scrollRoot) {
            /* Page 2 should land at top when expanding from peek — not mid-list */
            scrollRoot.scrollTo({ top: 0, behavior: 'instant' });
          }
        }, 180);
      });
    }
  },

  getActiveDrawerContent() {
    const fromVisible = this.getActiveFormElement()?.querySelector(".drawer-content");
    if (fromVisible) return fromVisible;
    const section = this.getPanelSectionForSelectedTab();
    return section?.querySelector(".drawer-content") ?? null;
  },

  /** Scrollport for page-2 options: `.drawer-content` (mobile + desktop). */
  getActiveMobileOptionsScrollRoot() {
    return wheelbuilderResolveOptionsScrollContainer(this.getActiveDrawerContent());
  },

  _clearActiveDrawerContentScrollStyles() {
    const el = this.getActiveDrawerContent();
    if (!el) return;
    el.style.removeProperty("max-height");
    el.style.removeProperty("overflow-y");
    el.style.removeProperty("-webkit-overflow-scrolling");
  },

  _clearAllDrawerContentScrollStyles() {
    const root = this.$root || this.$el;
    if (!root || typeof root.querySelectorAll !== "function") return;
    root.querySelectorAll(".drawer-content").forEach((el) => {
      el.style.removeProperty("max-height");
      el.style.removeProperty("overflow-y");
      el.style.removeProperty("-webkit-overflow-scrolling");
    });
  },

  _ensureMobileDrawerScrollResizeObserver() {
    if (typeof ResizeObserver === "undefined") return;
    if (this._mobileDrawerScrollRO) return;
    const panel = this.getDrawerPanel();
    if (!panel) return;
    this._mobileDrawerScrollRO = new ResizeObserver(() => {
      this.syncMobileDrawerContentScrollLayout();
    });
    this._mobileDrawerScrollRO.observe(panel);
    const tp = this.getTabpanelsContainer();
    if (tp) this._mobileDrawerScrollRO.observe(tp);
  },

  _teardownMobileDrawerScrollResizeObserver() {
    if (this._mobileDrawerScrollRO) {
      this._mobileDrawerScrollRO.disconnect();
      this._mobileDrawerScrollRO = null;
    }
  },

  /**
   * Flex/% heights inside a transformed drawer often yield no overflow on iOS/WebKit.
   * Set an explicit max-height from viewport geometry so `.drawer-content` can scroll.
   */
  syncMobileDrawerContentScrollLayout(retry = 0) {
    if (typeof window === "undefined") return;
    if (window.innerWidth > 768) {
      this._clearAllDrawerContentScrollStyles();
      return;
    }
    const panel = this.getDrawerPanel();
    if (!panel || !panel.classList.contains("is-expanded") || panel.classList.contains("is-chooser") || this.mobileStep === "cart-added") {
      this._clearActiveDrawerContentScrollStyles();
      return;
    }
    if (!this.mobileShowOptions) {
      this._clearActiveDrawerContentScrollStyles();
      return;
    }
    const drawer = this.getActiveDrawerContent();
    if (!drawer) return;
    const dcs = window.getComputedStyle(drawer);
    if (dcs.display === "none" || dcs.visibility === "hidden") return;

    const panelRect = panel.getBoundingClientRect();
    const drawerRect = drawer.getBoundingClientRect();
    const footer = panel.querySelector(".wheelbuilder-mobile-panel-footer");
    let footerH = 0;
    if (footer) {
      const fs = window.getComputedStyle(footer);
      if (fs.display !== "none" && fs.visibility !== "hidden") {
        footerH = footer.getBoundingClientRect().height;
      }
    }
    const insetBottom = 12;
    const availableBottom = panelRect.bottom - footerH - insetBottom;
    let maxH = Math.floor(availableBottom - drawerRect.top);
    if (maxH < 80 && retry < 10) {
      this.$nextTick(() => {
        requestAnimationFrame(() => this.syncMobileDrawerContentScrollLayout(retry + 1));
      });
      return;
    }
    if (maxH < 80) return;
    maxH = Math.max(120, maxH);
    drawer.style.setProperty("max-height", `${maxH}px`, "important");
    drawer.style.setProperty("overflow-y", "auto", "important");
    drawer.style.setProperty("-webkit-overflow-scrolling", "touch");
  },

  /**
   * Active form: pick a **visible** tabpanel whose `data-wb-panel-tab` matches `selectedTab`,
   * so we never attach scroll/progress/summary to a `display:none` section (breaks scroll & page 3).
   * Fall back to DOM index (`labels`), then first visible panel (Alpine transition edge cases).
   */
  getActiveFormElement() {
    const tabpanels = this.getTabpanelsContainer();
    if (!tabpanels) return null;

    const selected = this.selectedTab;
    const sections = Array.from(tabpanels.querySelectorAll(":scope > section"));

    if (!selected) {
      const visiblePanel = sections.find((p) => window.getComputedStyle(p).display !== "none");
      return visiblePanel?.querySelector('[x-data^="wheelBuilderForm"]') || null;
    }

    for (const panel of sections) {
      if (window.getComputedStyle(panel).display === "none") continue;
      const form = panel.querySelector('[x-data^="wheelBuilderForm"]');
      if (form && wheelbuilderReadPanelTab(form) === selected) {
        return form;
      }
    }

    const tabIndex = labels.indexOf(selected);
    if (tabIndex >= 0 && sections[tabIndex]) {
      const form = sections[tabIndex].querySelector('[x-data^="wheelBuilderForm"]');
      if (form) return form;
    }

    const visiblePanel = sections.find((p) => window.getComputedStyle(p).display !== "none");
    return visiblePanel?.querySelector('[x-data^="wheelBuilderForm"]') || null;
  },

  /** `<section role="tabpanel">` that owns the active form (visible when possible). */
  getActivePanelSection() {
    const form = this.getActiveFormElement();
    const sec = form?.closest?.("section[role='tabpanel']");
    if (sec) return sec;
    return this.getPanelSectionForSelectedTab();
  },

  /** Tabpanel `<section>` for `selectedTab` (same index as `labels`). */
  getPanelSectionForSelectedTab() {
    const tabpanels = this.getTabpanelsContainer();
    if (!tabpanels || !this.selectedTab) return null;
    const tabIndex = labels.indexOf(this.selectedTab);
    if (tabIndex < 0) return null;
    const sections = tabpanels.querySelectorAll(":scope > section");
    return sections[tabIndex] || null;
  },

  /** Form for the currently selected tab (by index). Used when tabpanels are hidden (e.g. peek). */
  getFormElementBySelectedTab() {
    const section = this.getPanelSectionForSelectedTab();
    return section?.querySelector('[x-data^="wheelBuilderForm"]') || null;
  },

  getActiveFormData() {
    return getAlpineData(this.getActiveFormElement());
  },

  /** Shared `.wheelbuilder-mobile-panel-footer` (sibling of tabpanels) — bind actions to the visible tab’s form. */
  wbShowMobilePanelFooter() {
    if (typeof window !== "undefined" && window.innerWidth > 768) return false;
    if (this.drawerState !== "expanded" || !this.mobileShowOptions) return false;
    const f = this.getActiveFormData();
    if (!f || typeof f.wbMobileSummarySheetOpen !== "function") return false;
    return !f.wbMobileSummarySheetOpen();
  },

  runActiveClearSelections() {
    const f = this.getActiveFormData();
    if (f && typeof f.clearSelections === "function") f.clearSelections();
  },

  runActiveMobileCartClick() {
    const f = this.getActiveFormData();
    if (!f) return;
    /* Re-entry guard: if a previous tap already kicked off the
       checkout navigation, ignore subsequent taps so we don't fire
       another haptic / queue another `location.href`. `goToCheckout`
       itself also self-debounces, but stopping here also avoids
       Alpine re-running the click handler chain unnecessarily. */
    if (f.isCheckingOut) return;
    if (f.addedToCart) {
      if (typeof f.goToCheckout === "function") f.goToCheckout();
      return;
    }
    if (f.addToCartSubmitting) return;
    if (
      typeof f.isReadyForSummary === "function" &&
      f.isReadyForSummary() &&
      !f.unavailable &&
      !f.error &&
      !f.loading &&
      !f.isFetchingProducts &&
      !f.isBuildResolving
    ) {
      if (typeof f.addToCart === "function") f.addToCart();
    }
  },

  activeMobileCartDisabled() {
    const f = this.getActiveFormData();
    if (!f) return true;
    if (f.addedToCart) return false;
    return Boolean(
      !f.isReadyForSummary?.() ||
        f.unavailable ||
        f.error ||
        f.addToCartSubmitting ||
        f.isFetchingProducts ||
        f.isBuildResolving,
    );
  },

  /** Split pay-row: tap on the price column runs the same cart CTA handler. */
  runPayRowCartClick(event) {
    const row = event?.currentTarget;
    if (!row?.classList?.contains("has-price")) return;
    if (event.target.closest(".wheelbuilder-cart-btn")) return;
    const btn = row.querySelector(".wheelbuilder-cart-btn");
    if (!btn || btn.disabled) return;
    btn.click();
  },

  formatOptionLabel(id) {
    if (!id) return 'your build';
    return id
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  },

  get nextRequiredOptionLabel() {
    const formData = this.getActiveFormData();
    const nextOption = formData?.completeOptions?.find((option) => option.value === '');
    return nextOption ? this.formatOptionLabel(nextOption.id) : 'your build';
  },

  /** Comma-separated list of option labels still to select (for "Keep going: Hub, Rim, ...") */
  get remainingOptionLabels() {
    const formData = this.getActiveFormData();
    const options = formData?.completeOptions;
    if (!options || !Array.isArray(options)) return '';
    const empty = options.filter((opt) => !opt.value || opt.value === '');
    if (empty.length === 0) return '';
    return empty.map((opt) => this.formatOptionLabel(opt.id)).join(', ');
  },

  /** Next option name only for peek CTA (e.g. "Hub") */
  get peekRemainingShort() {
    const formData = this.getActiveFormData();
    const options = formData?.completeOptions;
    if (!options || !Array.isArray(options)) return '';
    const next = options.find((opt) => !opt.value || opt.value === '');
    return next ? this.formatOptionLabel(next.id) : '';
  },

  get isBuildResolving() {
    const formData = this.getActiveFormData();
    /* Use option completion, not `progress` (DOM-derived) — one frame of progress < 100 hid resolving
     * state and left every CTA label x-show false (empty button). */
    if (!formData || !this.isEveryOptionSelected()) return false;

    /* Omit `formData.loading` — that flag is only the /cart/add request; it is not “building” the config. */
    const coreBusy = !!(
      formData.isFetchingProducts ||
      formData.loadingOperations > 0 ||
      formData.pricing?.loading
    );
    /* Any gap after the last option until the real “Add to cart + price” row is valid = resolving
     * (desktop empty button, mobile stuck label). Align with `isReadyForSummary()` + pipeline busy. */
    if (typeof formData.isReadyForSummary === "function") {
      return coreBusy || !formData.isReadyForSummary();
    }
    return !!(coreBusy || !formData.loadingComplete);
  },

  get isSummaryReady() {
    const formData = this.getActiveFormData();
    /* Mobile wizard page 3: must not require pricing — complete wheelset keeps pricing.loading true until
       both front+rear variants exist (see wheelBuilderForm pricing getter), which blocked summary forever. */
    return !!formData?.canShowMobileSummary?.();
  },

  /**
   * Drives `[data-complete]` on `.wheelbuilder-panel`. CSS uses it to `display:none !important` the in-drawer
   * option list when the build is “done” — but the teleported summary only shows when `wbMobileSummarySheetOpen()`.
   * If those disagree (tablet/split view, tab/panel mismatch, sheet not yet painted), hiding options via
   * data-complete alone leaves a blank drawer. In the mobile wizard (`mobileShowOptions`), only set
   * data-complete when the sheet gate is true; desktop tab mode keeps the original behavior.
   */
  get panelDataCompleteAttr() {
    if (!this.isSummaryReady || this.mobileForceOptions) return false;
    if (!this.mobileShowOptions) return true;
    const f = this.getActiveFormData();
    if (!f || typeof f.wbMobileSummarySheetOpen !== "function") return false;
    if (!f.wbMobileSummarySheetOpen()) return false;
    return !!this.summarySheetPainted;
  },

  /** Mirrors wheelBuilderForm completion (price row lives outside form x-data). */
  isEveryOptionSelected() {
    return computeWheelBuilderFormEveryOptionSelected(getAlpineData(this.getActiveFormElement()));
  },

  get mobileStep() {
    if (!this.mobileShowOptions) return 'chooser';
    /* Step 4 — cart confirmation page inside the drawer. */
    const f = this.getActiveFormData();
    if (f?.addedToCart && wheelbuilderIsMobileViewport(this)) return 'cart-added';
    if (this.mobileForceOptions) return 'options';
    /* Summary chrome as soon as the active form is complete — not when `canShowMobileSummary()` is true.
     * That flag drops during product/pricing fetches (`loadingOperations` / fetches), which used to flip
     * mobileStep back to `options` and feel like: options page → loading → summary → sheet. */
    if (this.isEveryOptionSelected()) return 'summary';
    return 'options';
  },

  wbDrawerCartAddedVisible() {
    return this.mobileStep === 'cart-added';
  },

  /**
   * Full-screen teleported summary already has a title + actions. Hiding this row avoids a duplicate
   * “page” (drawer header + empty body) stacked behind the sheet.
   */
  wbDrawerSummaryHeaderVisible() {
    return this.mobileStep === 'summary';
  },

  closeMobileSummarySheet() {
    if (!wheelbuilderMobileWizardActive(this)) return;
    this.mobileForceOptions = true;
    /* A11y teardown runs from $watch when sheet visibility becomes false */
  },

  /** Summary drawer back: reset build (same as footer reset) then return to options. */
  backFromMobileSummary() {
    if (!wheelbuilderMobileWizardActive(this)) return;
    const f = this.getActiveFormData();
    if (f && typeof f.clearSelections === "function") {
      try { f.clearSelections(true); } catch (_) { /* defensive: never block nav */ }
    }
    this.hasShownSummary = false;
    this.mobileForceOptions = true;
    this.$nextTick(() => this.scrollDrawerToTop("auto"));
  },

  /** Rebuild `mobileSummarySheetRows` on the active tab’s form before the teleported sheet paints. */
  refreshSelectedFormSummaryRows() {
    const el = this.getActiveFormElement();
    const fd = getAlpineData(el);
    if (fd && typeof fd.syncMobileSummarySheetRows === "function") {
      fd.syncMobileSummarySheetRows();
    }
  },

  _setupMobileSummarySheetA11y() {
    if (this._mobileSummaryA11yActive || !wheelbuilderMobileWizardActive(this)) return;
    this._mobileSummaryA11yActive = true;
    wbDebugSummaryTabs(this, "summary sheet a11y: body lock + drawer inert");
    this._mobileSummaryPrevFocus = document.activeElement;
    document.body.classList.add("wheelbuilder-mobile-summary-sheet-open");
    document.documentElement.classList.add("wheelbuilder-mobile-summary-sheet-open");
    document.body.style.overflow = "hidden";
    const panel = this.getDrawerPanel();
    panel?.setAttribute("inert", "");
    this._mobileSummaryEscapeHandler = (e) => {
      if (e.key !== "Escape") return;
      const sheet = getVisibleWheelbuilderSummarySheet();
      if (!sheet) return;
      e.preventDefault();
      e.stopPropagation();
      this.closeMobileSummarySheet();
    };
    document.addEventListener("keydown", this._mobileSummaryEscapeHandler, true);
    this.$nextTick(() => {
      setTimeout(() => {
        this.refreshSelectedFormSummaryRows();
        const sheet = getVisibleWheelbuilderSummarySheet();
        if (!sheet || !this._mobileSummaryA11yActive) return;
        this._mobileSummarySheetTrap?.deactivate();
        this._mobileSummarySheetTrap = createWheelbuilderFocusTrap(sheet, this._mobileSummaryPrevFocus);
        this._mobileSummarySheetTrap.activate();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            wheelbuilderScrollMobileSummaryCardToCenter(
              wheelbuilderPrefersReducedMotion() ? "auto" : "smooth"
            );
          });
        });
      }, 80);
    });
  },

  _teardownMobileSummarySheetA11y() {
    if (!this._mobileSummaryA11yActive) return;
    this._mobileSummaryA11yActive = false;
    wbDebugSummaryTabs(this, "summary sheet a11y: teardown (body unlock)");
    document.body.classList.remove("wheelbuilder-mobile-summary-sheet-open");
    document.documentElement.classList.remove("wheelbuilder-mobile-summary-sheet-open");
    document.body.style.overflow = "";
    this.getDrawerPanel()?.removeAttribute("inert");
    if (this._mobileSummaryEscapeHandler) {
      document.removeEventListener("keydown", this._mobileSummaryEscapeHandler, true);
      this._mobileSummaryEscapeHandler = null;
    }
    if (this._mobileSummarySheetTrap) {
      this._mobileSummarySheetTrap.deactivate();
      this._mobileSummarySheetTrap = null;
    }
    this._mobileSummaryPrevFocus = null;
  },

  /**
   * Single always-on peek label. Closed drawer must NOT reuse the expanded
   * footer's multi-span x-show tree (span-y clip + checkout fill-mode → blank pill).
   * Depends on `_formStateEpoch` so tabs re-read after form ATC/checkout.
   */
  get peekCtaLabel() {
    void this._formStateEpoch;
    const c = this.wbCopy || WHEELBUILDER_DEFAULT_COPY;
    const f = this.getActiveFormData();
    if (f?.isCheckingOut) return c.checkingOut || "Checking out…";
    if (f?.addToCartSubmitting) return c.addingToCart || "Adding…";
    if (f?.loading) return c.addingToCart || "Adding…";
    if (f?.addedToCart) return c.peekCtaCheckout || "CHECKOUT";
    if (f?.error) return c.mobileFooterUnable || "This setup needs another tweak";
    if (f?.unavailable) return c.soldOut || "Sold out";
    if (!this.mobileShowOptions) return c.peekCtaStart || "Start";
    if (this.isBuildResolving) return c.building || c.peekCtaBuilding || "Building…";
    /* peekCtaReview copy is "ADD TO CART" in theme defaults — keep that contract. */
    if (this.isSummaryReady) return c.peekCtaReview || "ADD TO CART";
    if (this.progress > 0 && this.progress < 100) return c.peekCtaContinue || "Continue";
    if (this.progress >= 100) return c.peekCtaReview || "ADD TO CART";
    return c.peekCtaStart || "Start";
  },

  get peekCtaIsCheckout() {
    void this._formStateEpoch;
    const f = this.getActiveFormData();
    if (!f?.addedToCart) return false;
    if (f.loading || f.isCheckingOut || f.addToCartSubmitting) return false;
    if (f.cartBtnBusyOverlayVisible) return false;
    return true;
  },

  get peekCtaShowLabel() {
    void this._formStateEpoch;
    const f = this.getActiveFormData();
    if (f?.loading) return false;
    if (f?.cartBtnBusyOverlayVisible) return false;
    return true;
  },

  /**
   * Closed-drawer compact CTA click handler.
   * Mirrors the mobile-footer cart button states so the peek button can act on its label:
   *  - addedToCart  → go to checkout
   *  - summary ready (not busy / not unavailable) → add to cart in place
   *  - otherwise → open the drawer so user can continue building
   */
  runDrawerCtaClick() {
    const f = this.getActiveFormData();
    /* Re-entry guard — same rationale as runActiveMobileCartClick. The
       peek button (closed-drawer mirror) and the expanded footer button
       both call into this scope; without the guard a quick tap-on-peek
       followed by drawer-open + tap-on-footer would double-fire. */
    if (f && f.isCheckingOut) return;
    if (f && f.addedToCart) {
      if (typeof f.goToCheckout === "function") f.goToCheckout();
      return;
    }
    if (
      f &&
      this.isSummaryReady &&
      !this.isBuildResolving &&
      typeof f.isReadyForSummary === "function" &&
      f.isReadyForSummary() &&
      !f.unavailable &&
      !f.error &&
      !f.loading &&
      !f.isFetchingProducts &&
      !f.addToCartSubmitting
    ) {
      if (typeof f.addToCart === "function") {
        f.addToCart();
        return;
      }
    }
    this.openDrawer();
  },

  /** Mobile drawer header (pages 2–3): summary step always shows the tab name — sheet/cart own pricing copy. */
  get mobileWizardHeaderTitle() {
    if (this.isBuildResolving) return (this.wbCopy || WHEELBUILDER_DEFAULT_COPY).building;
    return 'Configure options';
  },

  scrollDrawerToTop(behavior = 'smooth') {
    const scrollRoot = this.getActiveMobileOptionsScrollRoot();
    if (!scrollRoot) return;

    scrollRoot.scrollTo({ top: 0, behavior });
  },

  setDesktopSummarySpacerCollapsed(collapsed) {
    if (typeof window === "undefined" || window.innerWidth <= 768) return;
    const panel = this.getDrawerPanel();
    if (!panel) return;
    panel.classList.toggle("wb-summary-space-collapsed", !!collapsed);
  },

  syncMobileSheet() {
    const shouldTrackSummary = this.isMobileViewport ? this.mobileShowOptions : true;

    if (!shouldTrackSummary) {
      this.hasShownSummary = false;
      this.setDesktopSummarySpacerCollapsed(false);
      return;
    }

    if (this.isSummaryReady) {
      this.setDesktopSummarySpacerCollapsed(false);
      if (this.isMobileViewport) {
        this.refreshSelectedFormSummaryRows();
        /* Only auto-expand the first time summary becomes ready. Later
           form-state updates (checkout tap, price refresh, etc.) must not
           yank the drawer open when the user deliberately left it peek'd. */
        if (!this.hasShownSummary) {
          this.setDrawerState('expanded');
        }
      }
      if (!this.hasShownSummary) {
        this.hasShownSummary = true;
        this.$nextTick(() => {
          requestAnimationFrame(() => this.scrollToSummary());
        });
      }
      return;
    }

    this.hasShownSummary = false;
    this.setDesktopSummarySpacerCollapsed(false);
  },
  
  // Auto-scroll within the drawer to the NEXT unselected option after a selection
  autoScrollToNextOption(selectedInput) {
    const currentGroup = selectedInput.closest('[data-option-group]');
    if (!currentGroup) return;
    
    const tabpanelsContainer = this.getTabpanelsContainer();
    if (!tabpanelsContainer) return;
    const panelSection =
      this.getActivePanelSection() ||
      Array.from(tabpanelsContainer.querySelectorAll(":scope > section")).find(
        (p) => window.getComputedStyle(p).display !== "none"
      );
    if (!panelSection) return;

    const allGroups = Array.from(panelSection.querySelectorAll("[data-option-group]"));
    const currentIndex = allGroups.indexOf(currentGroup);
    
    let nextGroup = null;
    for (let i = currentIndex + 1; i < allGroups.length; i++) {
      const checked = allGroups[i].querySelector('input[type="radio"]:checked');
      if (!checked || !checked.value) {
        nextGroup = allGroups[i];
        break;
      }
    }
    
    if (!nextGroup) return;

    const desktop = window.innerWidth > 768;
    // Wait for accordion to expand, then use the existing drawer-aware scroller
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.scrollToPanel(nextGroup, "auto", 30, {
          nextGroupEnter: desktop,
          allowScrollUp: false,
        });
      });
    }, 350);
  },
  
  calculateCollapsedOffset() {
    // Calculate collapsed offset: Window Height - peek chrome (~122px with 48px CTA)
    if (window.innerWidth <= 768) {
      this.collapsedOffset = window.innerHeight - 122;
    }
  },
  
  toggleDrawer() {
    const next = this.drawerState === 'peek' ? 'expanded' : 'peek';
    this.setDrawerState(next);
    triggerHaptic();
  },

  /** Clears drag when the browser aborts the touch (no reliable touchend). */
  handleDragCancel() {
    if (window.innerWidth > 768) return;
    if (!this.isDragging) return;
    this.isDragging = false;
    const panel = this.getDrawerPanel();
    if (panel) {
      panel.classList.remove("is-dragging", "is-overdrag");
      panel.style.removeProperty("--drawer-drag");
      panel.style.transition = "";
    }
    this.startY = 0;
    this.startTime = 0;
  },

  handleDragStart(e) {
    // Only handle drag on mobile
    if (window.innerWidth > 768) return;
    
    this.startY = e.touches[0].clientY;
    this.startTime = Date.now();
    this.isDragging = true;
    this.currentY = 0;

    // Disable CSS transitions
    const panel = this.getDrawerPanel();
    if (panel) {
      panel.classList.add('is-dragging');
      panel.style.transition = 'none';
    }
    
    e.preventDefault();
  },
  
  handleDragMove(e) {
    if (window.innerWidth > 768) return;
    if (!this.isDragging) return;
    
    const touchCurrentY = e.touches[0].clientY;
    let deltaY = touchCurrentY - this.startY;
    
    // Rubber-band at limits
    const atTop = this.drawerState === 'expanded' && deltaY < 0;
    const atBottom = this.drawerState === 'peek' && deltaY > 0;
    if (atTop || atBottom) {
      const sign = deltaY < 0 ? -1 : 1;
      const absDelta = Math.abs(deltaY);
      deltaY = sign * Math.log2(1 + absDelta) * 12;
      
      const panel = this.getDrawerPanel();
      if (panel) panel.classList.add('is-overdrag');
    } else {
      const panel = this.getDrawerPanel();
      if (panel) panel.classList.remove('is-overdrag');
    }
    
    const panel = this.getDrawerPanel();
    if (panel) {
      panel.style.setProperty('--drawer-drag', `${deltaY}px`);
    }
    
    e.preventDefault();
  },
  
  handleDragEnd(e) {
    if (window.innerWidth > 768) return;
    if (!this.isDragging) return;
    
    this.isDragging = false;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchEndY - this.startY;
    const dragDuration = Date.now() - this.startTime;
    const velocity = dragDuration > 0 ? deltaY / dragDuration : 0; // signed velocity
    
    const panel = this.getDrawerPanel();
    if (panel) {
      panel.classList.remove('is-dragging');
      panel.classList.remove('is-overdrag');
      panel.style.transition =
        "transform 0.42s cubic-bezier(0.32, 0.72, 0, 1)";
      panel.style.removeProperty('--drawer-drag');
    }
    
    // 2-state: peek ↔ expanded (distance or fast fling on drawer header)
    const SNAP_DISTANCE = 36;
    const FLICK_VELOCITY = 0.38;
    
    const moved = Math.abs(deltaY) > SNAP_DISTANCE || Math.abs(velocity) > FLICK_VELOCITY;
    
    if (moved) {
      if (deltaY > 0) {
        if (this.drawerState === 'expanded') this.setDrawerState('peek');
      } else {
        if (this.drawerState === 'peek') this.setDrawerState('expanded');
      }
      /* `force: true` — a drag-release commit is by definition a finger-in-
         motion event, so the scroll-gate would always block it. The user
         just executed a deliberate fling/swipe; the snap must always tick. */
      triggerHaptic('confirm', true);
    }
    
    setTimeout(() => {
      if (panel) panel.style.transition = '';
    }, 480);
    
    this.currentY = 0;
    this.startY = 0;
    this.startTime = 0;
  },
  
  // Auto-Scroll: full-screen summary → center card in sheet scrollport; desktop → vertically center the gray summary card in the options scrollport
  scrollToSummary() {
    if (this.isDragging) return;

    const activeForm = this.getActiveFormElement();
    if (!activeForm) return;

    const scrollRoot = wheelbuilderResolveOptionsScrollContainer(this.getActiveDrawerContent());
    const desktopShell =
      activeForm.querySelector(".wheelbuilder-summary-desktop-shell") ||
      activeForm.querySelector(".wheelbuilder-summary");
    const motion = wheelbuilderPrefersReducedMotion() ? "auto" : "smooth";

    const run = () => {
      const sheet = getVisibleWheelbuilderSummarySheet();
      if (sheet) {
        wheelbuilderScrollMobileSummaryCardToCenter(
          wheelbuilderPrefersReducedMotion() ? "auto" : "smooth"
        );
        if (scrollRoot) {
          scrollRoot.scrollTo({ top: 0, behavior: "auto" });
        }
        return;
      }

      if (scrollRoot && desktopShell && scrollRoot.contains(desktopShell)) {
        if (typeof window !== "undefined" && window.innerWidth > 768) {
          const summaryCard = desktopShell.querySelector(".wheelbuilder-summary.wheelbuilder-summary--card");
          const scrollTarget =
            summaryCard && scrollRoot.contains(summaryCard) ? summaryCard : desktopShell;
          const rect = scrollTarget.getBoundingClientRect();
          const closeEnough = Math.abs(rect.top) < window.innerHeight * 0.5;
          scrollTarget.scrollIntoView({
            behavior: closeEnough ? motion : "auto",
            block: "center",
            inline: "nearest",
          });
          // Keep large spacer during travel, then quickly collapse it once summary is in place.
          const settleDelay = closeEnough ? 220 : 120;
          setTimeout(() => {
            this.setDesktopSummarySpacerCollapsed(true);
          }, settleDelay);
          return;
        }
        const drawerScrollable = scrollRoot.scrollHeight > scrollRoot.clientHeight + 12;
        if (drawerScrollable) {
          wheelbuilderMobileDrawerScrollGroupToViewportCenter(desktopShell, scrollRoot, motion);
        }
        return;
      }

      if (scrollRoot) {
        scrollRoot.scrollTo({ top: 0, behavior: "auto" });
      }
    };

    /* Mobile: rAF so teleport/x-show can paint; centering helper retries if sheet is still display:none */
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          run();
        });
      });
      return;
    }

    setTimeout(run, 200);
  },
  
  // Scroll to accordion group using scrollIntoView (fixes sticky header issue)
  scrollToAccordionGroup(element) {
    if (!element) return;
    if (window.innerWidth <= 768) {
      const drawer = element.closest(".drawer-content");
      if (drawer) {
        const scrollPort = wheelbuilderResolveOptionsScrollContainer(drawer);
        wheelbuilderMobileDrawerScrollGroupToViewportCenter(element, scrollPort || drawer, "auto");
        return;
      }
    }
    element.scrollIntoView({
      block: "center",
      behavior: "smooth",
      inline: "nearest",
    });
  },

  syncVprogressRailFixedLayout() {
    if (typeof document === "undefined") return;
    document.documentElement.style.removeProperty("--wb-vprogress-fixed-right");
  },

  progressRailDotIndices() {
    const n = Number(this.progressRailSteps) || 0;
    if (n <= 0) return [];
    return Array.from({ length: n }, (_, i) => i);
  },

  progressRailFilledSteps() {
    const n = Number(this.progressRailSteps) || 0;
    if (n <= 0) return 0;
    const p = Number(this.progress) || 0;
    return Math.min(n, Math.max(0, Math.round((p / 100) * n)));
  },

  updateProgress() {
    if (labels.indexOf(this.selectedTab) === -1) {
      this.progress = 0;
      this.progressRailSteps = 0;
      return;
    }

    const tabpanelsContainer = this.getTabpanelsContainer();
    if (!tabpanelsContainer) {
      this.progress = 0;
      this.progressRailSteps = 0;
      return;
    }

    let currentSection = this.getActivePanelSection();
    if (!currentSection) {
      const tabIndex = labels.indexOf(this.selectedTab);
      const sections = tabpanelsContainer.querySelectorAll(":scope > section");
      currentSection = sections[tabIndex] || null;
    }

    if (!currentSection) {
      this.progress = 0;
      this.progressRailSteps = 0;
      return;
    }

    const groups = currentSection.querySelectorAll("[data-option-group]");
    
    if (groups.length === 0) {
      this.progress = 0;
      this.progressRailSteps = 0;
      return;
    }

    this.progressRailSteps = groups.length;
    
    let filled = 0;
    groups.forEach(group => {
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked && checked.value) filled++;
    });
    
    const newProgress = Math.round((filled / groups.length) * 100);
    const prevProgress = this.progress;
    this.progress = newProgress;

    if (
      typeof window !== 'undefined' &&
      window.innerWidth <= 768 &&
      newProgress === 100 &&
      prevProgress < 100 &&
      this.mobileShowOptions &&
      !this.mobileForceOptions
    ) {
      /* User just completed the last option — moving forward to the summary
       * pane. Direction set explicitly so the watcher doesn't have to guess. */
      this.setWizardDirection('forward');
      this.beginScreenTransition(440);
    }
    
    if (window.innerWidth > 768 && newProgress === 100) {
      // Desktop: Focus Add to Cart button when config is complete
      this.$nextTick(() => {
        const addToCartBtn = currentSection.querySelector('.wheelbuilder-btn-add');
        if (addToCartBtn && !addToCartBtn.disabled) {
          addToCartBtn.focus();
        }
      });
    }
  },

  destroy() {
    this._teardownMobileSummarySheetA11y();
    if (this._cartCountListener) {
      document.removeEventListener('cart:updated', this._cartCountListener);
      document.removeEventListener('cart:items-added', this._cartCountListener);
      document.removeEventListener('cart:refresh', this._cartCountListener);
      this._cartCountListener = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    if (this._touchCancelUnlock) {
      document.removeEventListener("touchcancel", this._touchCancelUnlock);
      this._touchCancelUnlock = null;
    }
    if (this._urlPresetRulesHandler) {
      document.removeEventListener("wheelbuilder:rules-loaded", this._urlPresetRulesHandler);
      this._urlPresetRulesHandler = null;
    }
    if (this._presetLoadingFailsafeId) {
      clearTimeout(this._presetLoadingFailsafeId);
      this._presetLoadingFailsafeId = null;
    }
    if (this._progressRailObserver) {
      this._progressRailObserver.disconnect();
      this._progressRailObserver = null;
    }
    wheelbuilderSetPresetLoadingOverlay(this.$root || this.$el, false);
  },

  async fetchRules(selectionParams = null) {
    this.fetchingRules = true;
    this.ruleFetchError = null;

    try {
      const wheelsetModel = wbGetTabsModelRaw(this);
      const rimLineModel = wbGetTabsRimModelRaw(this);
      if (!wheelsetModel && !rimLineModel) {
        this.wheelsetRules = [];
        this.wheelsetRulesIndex = null;
        this.rimRules = [];
        this.rules = [];
        return;
      }

      const selectionsKey = selectionParams && Object.keys(selectionParams || {}).length
        ? JSON.stringify(Object.keys(selectionParams).sort().reduce((o, k) => ({ ...o, [k]: selectionParams[k] }), {}))
        : "";
      const wbRegion = wbDetectRegion();
      const cacheKey =
        wbHandleKey(wheelsetModel) +
        ":rim:" +
        wbHandleKey(rimLineModel) +
        (wbRegion ? ":region:" + wbRegion : "") +
        (selectionsKey ? ":" + selectionsKey : "");
      const cached = !selectionsKey ? wheelBuilderRulesCache.get(cacheKey) : null;

      if (cached) {
        this.wheelsetRules = ensureRuleFieldMaps(cached.wheelsetRules || []);
        this.wheelsetRulesIndex = cached.wheelsetRulesIndex || null;
        this.rimRules = ensureRuleFieldMaps(cached.rimRules);
        this.rules = [...this.wheelsetRules, ...this.rimRules];
        const store = typeof Alpine !== 'undefined' && Alpine.store && Alpine.store('wheelbuilder');
        if (store) {
          store.derivedWheelOptions = cached.derivedWheelOptions;
          store.derivedRimOptions = cached.derivedRimOptions;
        }
      } else {
        let wheelResult;
        let rimResult;

        if (this.ecomEngineUrl && this.ecomEngineUrl.trim()) {
          const ecomBase = this.ecomEngineUrl.trim();
          const ecomKey = (this.ecomEngineApiKey || "").trim();
          const ecomPath = (this.ecomEngineApiPath || "").trim() || undefined;
          const ecomToken = (this.ecomEngineBearerToken || "").trim() || undefined;
          rimResult = rimLineModel
            ? await fetchBuilderRules("rim_rule", rimLineModel)
            : { rules: [], errors: null };
          if (wheelsetModel) {
            const rulesIndexRes = await fetchRulesIndexFromEcomEngine(
              ecomBase,
              ecomKey,
              wheelsetModel,
              ecomPath,
              ecomToken,
              wbRegion
            );
            if (rulesIndexRes.rulesIndex) {
              this.wheelsetRulesIndex = rulesIndexRes.rulesIndex;
              this.wheelsetRules = [];
              wheelResult = { rules: [], errors: null };
            } else {
              this.wheelsetRulesIndex = null;
              console.warn("[WheelBuilder] Rules index unavailable, falling back to full meta objects fetch:", rulesIndexRes.errors || "unknown");
              wheelResult = await fetchFromEcomEngine(
                ecomBase,
                ecomKey,
                wheelsetModel,
                ecomPath,
                ecomToken,
                null
              );
            }
          } else {
            this.wheelsetRulesIndex = null;
            wheelResult = { rules: [], errors: null };
          }
        } else {
          this.wheelsetRulesIndex = null;
          [wheelResult, rimResult] = await Promise.all([
            wheelsetModel
              ? fetchBuilderRules("wheelset_rule", wheelsetModel)
              : Promise.resolve({ rules: [], errors: null }),
            rimLineModel
              ? fetchBuilderRules("rim_rule", rimLineModel)
              : Promise.resolve({ rules: [], errors: null }),
          ]);
        }

        const errors = [];
        if (wheelResult.errors) {
          const errorMsg = typeof wheelResult.errors === 'string'
            ? wheelResult.errors
            : (wheelResult.errors?.message || JSON.stringify(wheelResult.errors));
          errors.push(`Wheelset rules: ${errorMsg}`);
        }
        if (rimResult.errors) {
          const errorMsg = typeof rimResult.errors === 'string'
            ? rimResult.errors
            : (rimResult.errors?.message || JSON.stringify(rimResult.errors));
          errors.push(`Rim rules: ${errorMsg}`);
        }

        if (errors.length > 0) {
          const raw = errors.join('; ');
          this.ruleFetchError = toUserFriendlyError(raw);
          console.error('[WheelBuilder] Rule fetch errors:', errors);
        } else {
          this.ruleFetchError = null;
        }

        this.wheelsetRules = ensureRuleFieldMaps(wheelResult.rules || []);
        this.rimRules = ensureRuleFieldMaps(rimResult.rules || []);
        this.rules = [...this.wheelsetRules, ...this.rimRules];
        const store = typeof Alpine !== 'undefined' && Alpine.store && Alpine.store('wheelbuilder');
        let derivedWheel = null;
        let derivedRim = null;
        if (store) {
          derivedWheel = this.wheelsetRulesIndex
            ? { options: this.wheelsetRulesIndex.options }
            : await deriveOptionsFromRules(this.wheelsetRules);
          derivedRim = await deriveOptionsFromRules(this.rimRules);
          store.derivedWheelOptions = derivedWheel;
          store.derivedRimOptions = derivedRim;
        }
        if (!selectionsKey) {
          wheelBuilderRulesCache.set(cacheKey, {
            wheelsetRules: this.wheelsetRules,
            wheelsetRulesIndex: this.wheelsetRulesIndex,
            rimRules: this.rimRules,
            derivedWheelOptions: derivedWheel,
            derivedRimOptions: derivedRim,
          });
        }
      }
    } catch (error) {
      const errorMessage = error?.message || 'Unknown error occurred';
      this.ruleFetchError = toUserFriendlyError(errorMessage);
      console.error('[WheelBuilder] fetchRules exception:', error);
      this.wheelsetRules = [];
      this.wheelsetRulesIndex = null;
      this.rimRules = [];
      this.rules = [];
    } finally {
      this.fetchingRules = false;
      document.dispatchEvent(new CustomEvent('wheelbuilder:rules-loaded'));
    }
  },

  getRelevantRules() {
    return this.selectedTab === "rim" ? this.rimRules : this.wheelsetRules;
  },

  _labelForTabKey(tabKey) {
    const key = String(tabKey || "").trim();
    const translationKey = `products.wheelset_builder.tabs.${key}`;
    if (key === "complete") return "Wheelset";
    return (
      window.translations?.[translationKey] ||
      (key === "front"
        ? "Front only"
        : key === "rear"
          ? "Rear only"
          : key === "rim"
            ? "Rim only"
            : key
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" "))
    );
  },

  // Mobile wizard: Select nav and show options page
  selectMobileNav(id, tabIndex) {
    const tabKey = labels[tabIndex];
    this.selectedTabLabel = this._labelForTabKey(tabKey);
    this.setWizardDirection('forward');
    this.beginScreenTransition(440);
    this.mobileShowOptions = true;
    this.hasShownSummary = false;
    this.select(id, tabIndex, { fromMobileChooser: true });
    
    // Expand drawer when configuring options
    if (window.innerWidth <= 768 && this.drawerState !== 'expanded') {
      this.setDrawerState('expanded');
    }
    
    // Second page always starts from top: scroll drawer content to top after panel is visible
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.syncMobileDrawerContentScrollLayout();
        requestAnimationFrame(() => {
          const scrollRoot = this.getActiveMobileOptionsScrollRoot();
          if (scrollRoot) {
            scrollRoot.scrollTo({ top: 0, behavior: 'auto' });
          }
        });
      });
      if (this.$refs?.tablistMobile) {
        this.$refs.tablistMobile.style.setProperty("--wb-tab-active-index", tabIndex);
        
        // Update indicator position for mobile page 2 tabs
        const mobileTabs = this.$refs.tablistMobile.querySelectorAll('button[role="tab"]');
        if (mobileTabs[tabIndex]) {
          const tab = mobileTabs[tabIndex];
          const tabRect = tab.getBoundingClientRect();
          const listRect = this.$refs.tablistMobile.getBoundingClientRect();
          const offset = tabRect.left - listRect.left + this.$refs.tablistMobile.scrollLeft;
          this.$refs.tablistMobile.style.setProperty("--wb-tab-indicator-width", `${tabRect.width}px`);
          this.$refs.tablistMobile.style.setProperty("--wb-tab-indicator-offset", `${offset}px`);
        }
      }
    });
  },

  backToMobileNav() {
    /* Clear the active form's selections when the user backs out of the options
       page so returning to the chooser starts a fresh build (and the closed-drawer
       CTA reverts from "ADD TO CART"/"KEEP ROLLING" to "LET'S BUILD"). Silent flag
       suppresses the reset-button's error haptic since the back tap is intentional. */
    const f = this.getActiveFormData();
    if (f && typeof f.clearSelections === "function") {
      try { f.clearSelections(true); } catch (_) { /* defensive: never block nav */ }
    }
    this.setWizardDirection('backward');
    this.beginScreenTransition(440);
    this.mobileShowOptions = false;
    this.selectedTabLabel = '';
    this.setDrawerState('expanded');
    this.$nextTick(() => this.scrollDrawerToTop('auto'));
  },

  _normPresetValue(val) {
    if (val == null) return "";
    return String(val)
      .normalize("NFKC")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  },

  parseUrlPresetConfig() {
    const rawCfg = wheelbuilderReadCfgFromLocation();
    if (!rawCfg) return null;
    const cfg = wheelbuilderParseCfgPairs(rawCfg);
    const tabRaw = cfg.tab ?? cfg.t ?? "";
    let tab = wheelbuilderCanonicalTabFromCfg(tabRaw);
    const spoke = String(cfg.spoke ?? cfg.s ?? "").trim();
    const hub = String(cfg.hub ?? cfg.h ?? "").trim();
    const rimSize = String(cfg.rim_size ?? cfg.rimsize ?? cfg.rs ?? "").trim();
    const holeCount = String(cfg.hole_count ?? cfg.holes ?? cfg.hc ?? "").trim();
    if (!tab && (rimSize || holeCount)) tab = "rim";
    if (!tab && !spoke && !hub && !rimSize && !holeCount) return null;
    return { tab, spoke, hub, rimSize, holeCount };
  },

  _finishUrlPresetLoading() {
    if (this._presetLoadingFailsafeId) {
      clearTimeout(this._presetLoadingFailsafeId);
      this._presetLoadingFailsafeId = null;
    }
    wheelbuilderSetPresetLoadingOverlay(this.$root || this.$el, false);
  },

  _abortUrlPresetToCleanState(reason = "") {
    console.warn("[WheelBuilder] URL preset ignored; launching clean builder state.", reason);
    this.urlPresetConfig = null;
    this._urlPresetApplied = true;
    this._urlPresetAttemptCount = 0;
    if (this._urlPresetRulesHandler) {
      document.removeEventListener("wheelbuilder:rules-loaded", this._urlPresetRulesHandler);
      this._urlPresetRulesHandler = null;
    }
    this._suppressInitialOpenFirstDropdown = false;

    this.resetAllForms();
    const defaultTabIndex = 0;
    this.select(this.$id("tab", defaultTabIndex + 1), defaultTabIndex, { skipInitialLayoutScroll: true });
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      this.mobileShowOptions = false;
      this.selectedTabLabel = "";
      this.mobileForceOptions = false;
      this.hasShownSummary = false;
      this.setDrawerState("expanded");
    }
    this._finishUrlPresetLoading();
  },

  _resolvePresetOptionValue(formEl, optionId, requestedValue) {
    if (!requestedValue || !optionId) return null;
    const requestedNorm = this._normPresetValue(requestedValue);
    if (!requestedNorm) return null;

    const candidates = [];
    const seen = new Set();
    const pushCandidate = (v) => {
      const s = String(v == null ? "" : v).trim();
      if (!s) return;
      const k = this._normPresetValue(s);
      if (!k || seen.has(k)) return;
      seen.add(k);
      candidates.push(s);
    };

    const options = this.wheelsetRulesIndex?.options;
    const indexedValues = wheelbuilderRulesIndexPickKey(options, optionId);
    if (Array.isArray(indexedValues)) indexedValues.forEach(pushCandidate);

    const group = formEl?.querySelector?.(`[data-option-group="${optionId}"]`);
    if (group) {
      group.querySelectorAll("[data-value]").forEach((el) => {
        pushCandidate(el.getAttribute("data-value"));
      });
    }

    const matched = candidates.find((v) => this._normPresetValue(v) === requestedNorm);
    return matched || null;
  },

  _isPresetOptionActionable(formEl, formData, optionId) {
    if (!optionId) return false;
    const option = (formData.completeOptions || []).find((o) => o && o.id === optionId);
    if (!option || option.value) return false;
    if (typeof formData.isInputGroupDisabled === "function" && formData.isInputGroupDisabled(optionId)) return false;
    const groupEl = formEl?.querySelector?.(`[data-option-group="${optionId}"]`);
    if (!groupEl) return true;
    const groupStyle = window.getComputedStyle(groupEl);
    return groupStyle.display !== "none" && groupStyle.visibility !== "hidden";
  },

  _pickPresetNextOption(formEl, formData) {
    const options = Array.isArray(formData.completeOptions) ? formData.completeOptions : [];
    const tab = String(this.selectedTab || "").trim();
    const priorityByTab = {
      complete: ["front_rim", "rear_rim", "front_spacing", "rear_spacing", "brake_mount", "freehub_driver", "hub_color", "spoke_color"],
      wheelset: ["front_rim", "rear_rim", "front_spacing", "rear_spacing", "brake_mount", "freehub_driver", "hub_color", "spoke_color"],
      front: ["front_rim", "front_spacing", "brake_mount", "hub_color", "spoke_color"],
      rear: ["rear_rim", "rear_spacing", "brake_mount", "freehub_driver", "hub_color", "spoke_color"],
      rim: ["rim_size", "hole_count"],
    };
    const priority = priorityByTab[tab] || [];

    for (const id of priority) {
      if (this._isPresetOptionActionable(formEl, formData, id)) {
        return options.find((o) => o && o.id === id) || null;
      }
    }

    const firstActionable = options.find((o) => o && this._isPresetOptionActionable(formEl, formData, o.id));
    if (firstActionable) return firstActionable;
    return options.find((o) => o && !o.value) || null;
  },

  _applyUrlPresetConfig() {
    if (!this.urlPresetConfig || this._urlPresetApplied) return;
    if (this._urlPresetAttemptCount > 20) {
      this._abortUrlPresetToCleanState("preset resolution timed out");
      return;
    }
    this._urlPresetAttemptCount += 1;

    const cfg = this.urlPresetConfig;
    if (cfg.tab) {
      const tabIndex = labels.indexOf(cfg.tab);
      if (tabIndex >= 0 && this.selectedTab !== cfg.tab) {
        this._suppressInitialOpenFirstDropdown = true;
        /* Desktop tab button ids use whichChild+1 (tab-1 … tab-4); tabIndex is 0-based. */
        this.select(this.$id("tab", tabIndex + 1), tabIndex, { skipInitialLayoutScroll: true });
      }
    }

    const formEl = this.getFormElementBySelectedTab() || this.getActiveFormElement();
    const formData = getAlpineData(formEl);
    if (!formEl || !formData || !Array.isArray(formData.completeOptions)) {
      setTimeout(() => this._applyUrlPresetConfig(), 80);
      return;
    }

    const spokeValue = cfg.spoke ? this._resolvePresetOptionValue(formEl, "spoke_type", cfg.spoke) : null;
    const hubValue = cfg.hub ? this._resolvePresetOptionValue(formEl, "hub_model", cfg.hub) : null;
    const rimSizeValue = cfg.rimSize ? this._resolvePresetOptionValue(formEl, "rim_size", cfg.rimSize) : null;
    const holeCountValue = cfg.holeCount ? this._resolvePresetOptionValue(formEl, "hole_count", cfg.holeCount) : null;

    const hasSpokeOption = formData.completeOptions.some((o) => o && o.id === "spoke_type");
    const hasHubOption = formData.completeOptions.some((o) => o && o.id === "hub_model");
    const hasRimSizeOption = formData.completeOptions.some((o) => o && o.id === "rim_size");
    const hasHoleCountOption = formData.completeOptions.some((o) => o && o.id === "hole_count");
    const wantsSpoke = Boolean(cfg.spoke && hasSpokeOption);
    const wantsHub = Boolean(cfg.hub && hasHubOption);
    const wantsRimSize = Boolean(cfg.rimSize && hasRimSizeOption);
    const wantsHoleCount = Boolean(cfg.holeCount && hasHoleCountOption);
    const shouldApplySpoke = Boolean(spokeValue && hasSpokeOption);
    const shouldApplyHub = Boolean(hubValue && hasHubOption);
    const shouldApplyRimSize = Boolean(rimSizeValue && hasRimSizeOption);
    const shouldApplyHoleCount = Boolean(holeCountValue && hasHoleCountOption);

    if (
      (wantsSpoke && !shouldApplySpoke) ||
      (wantsHub && !shouldApplyHub) ||
      (wantsRimSize && !shouldApplyRimSize) ||
      (wantsHoleCount && !shouldApplyHoleCount)
    ) {
      if (this._urlPresetAttemptCount >= 6) {
        this._abortUrlPresetToCleanState("preset value(s) not found in this builder config");
        return;
      }
      setTimeout(() => this._applyUrlPresetConfig(), 80);
      return;
    }

    if (!shouldApplySpoke && !shouldApplyHub && !shouldApplyRimSize && !shouldApplyHoleCount) {
      if (cfg.tab) {
        this._urlPresetApplied = true;
        this._finishUrlPresetLoading();
      }
      return;
    }

    if (formData._constraintsDebounceId) {
      clearTimeout(formData._constraintsDebounceId);
      formData._constraintsDebounceId = null;
    }
    if (formData._applySelectionDebounceId) {
      clearTimeout(formData._applySelectionDebounceId);
      formData._applySelectionDebounceId = null;
    }
    if (formData._refetchDebounceId) {
      clearTimeout(formData._refetchDebounceId);
      formData._refetchDebounceId = null;
    }
    formData._handleValueSetGen = (formData._handleValueSetGen || 0) + 1;
    formData._presetApplying = true;

    formData.error = null;
    formData.unavailable = false;
    formData.priceCalculated = false;
    formData.showProductUnavailableMessage = false;
    formData.addedToCart = false;
    formData.products = { front: null, rear: null };

    formData.completeOptions.forEach((opt) => {
      opt.value = "";
      opt.expanded = false;
    });
    formData.completeOptions.forEach((opt) => {
      if (shouldApplySpoke && opt.id === "spoke_type") opt.value = spokeValue;
      if (shouldApplyHub && opt.id === "hub_model") opt.value = hubValue;
      if (shouldApplyRimSize && opt.id === "rim_size") opt.value = rimSizeValue;
      if (shouldApplyHoleCount && opt.id === "hole_count") opt.value = holeCountValue;
    });
    for (let i = 0; i < formData.completeOptions.length; i++) {
      if (!formData.completeOptions[i].value) {
        formData.completeOptions[i].expanded = true;
        break;
      }
    }
    /* Full preset (every option already chosen): still expand the first row so choice tiles stay visible — same as visiting the tab without cfg (CSS prevents stretched tiles). */
    const opts = formData.completeOptions || [];
    if (opts.length && opts.every((o) => o && String(o.value || "").trim())) {
      opts.forEach((o, i) => {
        if (o) o.expanded = i === 0;
      });
    }

    if (typeof formData.applyRuleAvailabilityConstraints === "function") {
      formData.applyRuleAvailabilityConstraints();
    }
    if (typeof formData.syncMobileSummarySheetRows === "function") {
      formData.syncMobileSummarySheetRows();
    }
    if (typeof formData.notifyFormStateChanged === "function") {
      formData.notifyFormStateChanged();
    }
    document.dispatchEvent(new CustomEvent("wheelbuilder:update-progress", { bubbles: true }));

    setTimeout(() => {
      formData._presetApplying = false;
      if (typeof formData._debouncedApplySelection === "function") {
        formData._debouncedApplySelection();
      }

      if (typeof window !== "undefined" && window.innerWidth <= 768) {
        this.selectedTabLabel = this._labelForTabKey(this.selectedTab || cfg.tab || "complete");
        this.mobileShowOptions = true;
        this.hasShownSummary = false;
        this.mobileForceOptions = true;
        if (this.drawerState !== "expanded") this.setDrawerState("expanded");
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.syncMobileDrawerContentScrollLayout();
          });
        });
      }

      const focusNextPresetStep = () => {
        const isMobilePresetFlow = typeof window !== "undefined" && window.innerWidth <= 768;
        const nextOption = this._pickPresetNextOption(formEl, formData);
        if (nextOption && nextOption.id && typeof formData.activateOptionGroup === "function") {
          if (isMobilePresetFlow) {
            /* Mobile preset flow: force-open target option only. No auto-scroll. */
            (formData.completeOptions || []).forEach((entry) => {
              if (entry) entry.expanded = entry.id === nextOption.id;
            });
            this.mobileShowOptions = true;
            this.mobileForceOptions = true;
            if (this.drawerState !== "expanded") this.setDrawerState("expanded");
          } else {
            formData.activateOptionGroup(nextOption.id, { scroll: true });
          }
          return;
        }
        if (isMobilePresetFlow) return;
        const builder = document.querySelector("#wheelset-builder") || document.querySelector(".wheelbuilder");
        if (builder) {
          const headerOffset = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue("--header-height") || "0",
            10
          );
          const top = builder.getBoundingClientRect().top + window.pageYOffset - headerOffset - 20;
          window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        }
      };

      /** Rim cfg preset: Alpine watchers can run after _presetApplying clears — keep first row open with tiles. */
      const ensureRimFullPresetAccordion = () => {
        const opts = formData.completeOptions || [];
        if (
          this.selectedTab === "rim" &&
          opts.length &&
          opts.every((o) => o && String(o.value || "").trim())
        ) {
          opts.forEach((o, i) => {
            if (o) o.expanded = i === 0;
          });
        }
      };

      focusNextPresetStep();
      queueMicrotask(() => {
        requestAnimationFrame(ensureRimFullPresetAccordion);
      });
      const isMobilePresetFlow = typeof window !== "undefined" && window.innerWidth <= 768;
      if (isMobilePresetFlow) {
        setTimeout(() => {
          ensureRimFullPresetAccordion();
          this._finishUrlPresetLoading();
        }, 80);
      } else {
        /* Desktop can still get a delayed tab-init pass; run a lightweight second pass without
           forcing another panel scroll if the correct next group is already open. */
        setTimeout(() => {
          const nextOption = (formData.completeOptions || []).find((o) => o && !o.value);
          const nextId = nextOption?.id || "";
          const alreadyOpen = nextId
            ? (formData.completeOptions || []).some((o) => o && o.id === nextId && o.expanded)
            : false;
          if (!alreadyOpen) focusNextPresetStep();
          ensureRimFullPresetAccordion();
          this._finishUrlPresetLoading();
        }, 180);
      }
    }, 0);

    this._urlPresetApplied = true;
  },

  select(id, tabIndexOverride = null, options = {}) {
    const fromMobileChooser = options && typeof options === 'object' && options.fromMobileChooser === true;
    const skipInitialLayoutScroll =
      options && typeof options === 'object' && options.skipInitialLayoutScroll === true;
    // Handle mobile tab IDs (tab-mobile-0, tab-mobile-1, etc.)
    let tabIndex = tabIndexOverride;
    if (tabIndex === null) {
      if (id && id.includes('tab-mobile-')) {
        const parts = id.split('tab-mobile-');
        tabIndex = parseInt(parts[1]) || 0;
      } else if (id && id.includes('tab-')) {
        // Handle desktop tab IDs (tab-1, tab-2, etc.)
        const parts = id.split('-');
        const lastPart = parts[parts.length - 1];
        tabIndex = parseInt(lastPart) - 1;
        if (isNaN(tabIndex) || tabIndex < 0) {
          // Fallback: try to find tab by matching ID
          const allTabs = document.querySelectorAll('.wheelbuilder-tab');
          tabIndex = Array.from(allTabs).findIndex(tab => tab.id === id);
          if (tabIndex === -1) tabIndex = 0;
        }
      } else {
        // Fallback to first tab
        tabIndex = 0;
      }
    }
    
    // Ensure tabIndex is valid
    if (tabIndex < 0 || tabIndex >= labels.length) {
      tabIndex = 0;
    }
    
    const previousTab = this.selectedTab;
    const newTab = labels[tabIndex];
    const isReturningToSameTab = previousTab === newTab;
    
    // Update both selectedId and selectedTab
    // Desktop tab buttons use $id('tab', whichChild) → 1-based suffix; mobile uses index0 → 0-based.
    const desktopTabId = this.$id('tab', tabIndex + 1);
    const mobileTabId = this.$id('tab-mobile', tabIndex);
    
    // Use the ID that was clicked, or default to desktop ID
    this.selectedId = id || desktopTabId;
    this.selectedTab = newTab;
    
    // Haptic feedback for tab selection
    triggerHaptic();
    
    // Reset ALL forms when switching tabs (not when returning to same tab)
    if (previousTab && previousTab !== labels[tabIndex]) {
      // Reset progress
      this.progress = 0;
      
      // Clear ALL forms - every single form in the wheelbuilder
      this.resetAllForms();
      
      // Dispatch events
      document.dispatchEvent(new CustomEvent('wheelbuilder:tab-changed'));
      document.dispatchEvent(new CustomEvent('wheelbuilder:selections-cleared'));
      // Do not set mobileShowOptions = false here: user may have just chosen Front/Rear from
      // the chooser and should stay on the options page with title and footer visible.
    }
    
    // Update tab indicator and recalculate progress after tab switch
    this.$nextTick(() => {
      if (this.$refs?.tablist) {
        this.$refs.tablist.style.setProperty("--wb-tab-active-index", tabIndex);
      }
      // Also update mobile tablist if it exists
      if (this.$refs?.tablistMobile) {
        this.$refs.tablistMobile.style.setProperty("--wb-tab-active-index", tabIndex);
        // Sync mobile tab IDs
        const mobileTabs = this.$refs.tablistMobile.querySelectorAll('button');
        mobileTabs.forEach((tab, idx) => {
          const mobileId = this.$id('tab-mobile', idx);
          if (tab.id !== mobileId) {
            tab.id = mobileId;
          }
        });
        // Update mobile tab indicator position - use mobile tab ID
        this.$nextTick(() => {
          setTimeout(() => {
            const mobileTabId = this.$id('tab-mobile', tabIndex);
            // Try to set position using the mobile tab ID
            this.setIndicatorPosition(mobileTabId, this.$refs.tablistMobile);
            
            // Fallback: if tab not found, use tab index directly
            const mobileTabs = this.$refs.tablistMobile.querySelectorAll('button[role="tab"]');
            if (mobileTabs[tabIndex]) {
              const tab = mobileTabs[tabIndex];
              const tabRect = tab.getBoundingClientRect();
              const listRect = this.$refs.tablistMobile.getBoundingClientRect();
              const offset = tabRect.left - listRect.left + this.$refs.tablistMobile.scrollLeft;
              this.$refs.tablistMobile.style.setProperty("--wb-tab-indicator-width", `${tabRect.width}px`);
              this.$refs.tablistMobile.style.setProperty("--wb-tab-indicator-offset", `${offset}px`);
            }
          }, 100);
        });
      }
      // Update desktop tab indicator position
      this.setIndicatorPosition(id);
      
      // Handle scrolling based on whether returning to same tab or switching
      setTimeout(() => {
        this.syncMobileDrawerContentScrollLayout();
        const activeDrawerContent = this.getActiveDrawerContent();

        if (skipInitialLayoutScroll) {
          if (this._suppressInitialOpenFirstDropdown) {
            this._suppressInitialOpenFirstDropdown = false;
          } else {
            this.openFirstDropdown();
          }
        } else if (isReturningToSameTab && !fromMobileChooser) {
          // Returning to same tab: scroll to next unselected option (not when entering page 2 from chooser)
          if (window.innerWidth <= 768 && activeDrawerContent) {
            this.scrollToNextUnselectedOption(activeDrawerContent);
          } else {
            // Desktop: scroll to next unselected option
            this.scrollToNextUnselectedOption();
          }
        } else {
          // Switching to different tab: scroll to top and open first dropdown
          if (window.innerWidth <= 768 && activeDrawerContent) {
            const scrollRoot = wheelbuilderResolveOptionsScrollContainer(activeDrawerContent);
            scrollRoot.scrollTo({ top: 0, behavior: 'instant' });
          } else if (window.innerWidth > 768) {
            // Desktop: scroll nav section into view
            const navSection = document.querySelector('.wheelbuilder-nav-section');
            if (navSection) {
              const header = document.querySelector('header, .header, [data-header]');
              const headerHeight = header ? header.offsetHeight : 80;
              const elementPosition = navSection.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20;
              window.scrollTo({
                top: Math.max(0, offsetPosition),
                behavior: 'smooth'
              });
            }
          }
          // Open first dropdown and scroll to it
          this.openFirstDropdownAndScroll();
        }
      }, 100);
      
      // Update progress for the current tab
      setTimeout(() => {
        this.updateProgress();
        this.syncMobileSheet();
      }, 100);
    });
  },
  
  // Reset forms in this builder layout only (not every wheelBuilderForm on the document).
  resetAllForms() {
    const root = this.$root || this.$el;
    const allForms =
      root && typeof root.querySelectorAll === "function"
        ? root.querySelectorAll('[x-data^="wheelBuilderForm"]')
        : [];
    
    allForms.forEach(form => {
      // 1. Uncheck ALL radio inputs in DOM first
      const radios = form.querySelectorAll('input[type="radio"]');
      radios.forEach(radio => {
        radio.checked = false;
      });
      
      // 2. Reset Alpine data - this will automatically update accordions via x-model
      const alpineData = getAlpineData(form);
      
      if (alpineData) {
        // Reset completeOptions - clear ALL values, close ALL except first
        // The x-model binding on accordions will sync the expanded state
        if (alpineData.completeOptions && Array.isArray(alpineData.completeOptions)) {
          alpineData.completeOptions.forEach((opt, idx) => {
            opt.value = '';
            opt.expanded = idx === 0; // Only first accordion open
          });
        }
        
        // Reset products
        if (alpineData.products) {
          alpineData.products.front = null;
          alpineData.products.rear = null;
        }
        alpineData.unavailable = false;
        alpineData.error = null;
        alpineData.priceCalculated = false;
        alpineData.addedToCart = false;
        alpineData.addToCartSubmitting = false;
        
        // Also call resetProductsAndAvailability if it exists
        if (typeof alpineData.resetProductsAndAvailability === 'function') {
          alpineData.resetProductsAndAvailability();
        }
      }
      
      // 3. Also explicitly close accordions in case x-model doesn't sync immediately
      const optionGroups = form.querySelectorAll('[data-option-group]');
      optionGroups.forEach((group, groupIdx) => {
        const groupData = getAlpineData(group);
        
        if (groupData && typeof groupData.expanded !== 'undefined') {
          groupData.expanded = groupIdx === 0; // Only first one open
        }
      });
    });
  },

  isSelected(id) {
    if (!id) return false;
    
    // Get current tab index
    const currentTabIndex = labels.indexOf(this.selectedTab);
    if (currentTabIndex === -1) return false;
    
    // Handle both desktop and mobile tab IDs
    if (id.includes('tab-mobile-')) {
      const parts = id.split('tab-mobile-');
      const tabIndex = parseInt(parts[1]);
      if (!isNaN(tabIndex)) {
        return currentTabIndex === tabIndex;
      }
    } else if (id.includes('tab-') && !id.includes('tab-mobile-')) {
      // Handle desktop tab IDs (tab-1, tab-2, etc.)
      const parts = id.split('-');
      const lastPart = parts[parts.length - 1];
      const tabIndex = parseInt(lastPart) - 1;
      if (!isNaN(tabIndex) && tabIndex >= 0) {
        return currentTabIndex === tabIndex;
      }
    }
    
    // Fallback: check if ID matches selectedId or if it's the current tab's ID
    if (this.selectedId === id) return true;
    
    // Try to match by generating expected IDs
    const expectedDesktopId = this.$id('tab', currentTabIndex + 1);
    const expectedMobileId = this.$id('tab-mobile', currentTabIndex);
    return id === expectedDesktopId || id === expectedMobileId;
  },

  whichChild(child, parent) {
    return Array.from(parent.children).indexOf(child) + 1;
  },

  setIndicatorPosition(id, listOverride = null) {
    const tab = document.getElementById(id);
    const list = listOverride || this.$refs?.tablist;
    if (!tab || !list) {
      // If tab not found by ID, try to find it by data-tab-index
      if (list && id) {
        const tabIndex = id.includes('tab-mobile-') ? parseInt(id.split('tab-mobile-')[1]) : 
                        id.includes('tab-') ? parseInt(id.split('-').pop()) - 1 : null;
        if (tabIndex !== null && tabIndex >= 0) {
          const tabs = list.querySelectorAll('button[role="tab"]');
          if (tabs[tabIndex]) {
            const foundTab = tabs[tabIndex];
            const tabRect = foundTab.getBoundingClientRect();
            const listRect = list.getBoundingClientRect();
            const offset = tabRect.left - listRect.left + list.scrollLeft;
            list.style.setProperty("--wb-tab-indicator-width", `${tabRect.width}px`);
            list.style.setProperty("--wb-tab-indicator-offset", `${offset}px`);
            return;
          }
        }
      }
      return;
    }
    const tabRect = tab.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    const offset = tabRect.left - listRect.left + list.scrollLeft;
    list.style.setProperty("--wb-tab-indicator-width", `${tabRect.width}px`);
    list.style.setProperty("--wb-tab-indicator-offset", `${offset}px`);
  },

  // Scroll to next unselected option (when returning to same tab)
  scrollToNextUnselectedOption(scrollContainer = null) {
    const tabpanelsContainer = this.getTabpanelsContainer();
    if (!tabpanelsContainer) return;

    const panelSection =
      this.getActivePanelSection() ||
      Array.from(tabpanelsContainer.querySelectorAll(":scope > section")).find(
        (panel) => window.getComputedStyle(panel).display !== "none"
      );

    if (!panelSection) return;

    const optionGroups = panelSection.querySelectorAll("[data-option-group]");
    if (optionGroups.length === 0) return;
    
    // Find the first unselected option group
    let nextUnselectedGroup = null;
    for (const group of optionGroups) {
      const checkedInput = group.querySelector('input[type="radio"]:checked');
      if (!checkedInput || !checkedInput.value) {
        nextUnselectedGroup = group;
        break;
      }
    }
    
    // If all options are selected, scroll to the last one
    if (!nextUnselectedGroup && optionGroups.length > 0) {
      nextUnselectedGroup = optionGroups[optionGroups.length - 1];
    }
    
    if (!nextUnselectedGroup) return;
    
    const drawer = scrollContainer || nextUnselectedGroup.closest(".drawer-content");
    const actualScrollContainer = wheelbuilderResolveOptionsScrollContainer(drawer);
    
    // Scroll to the group
    if (window.innerWidth <= 768 && actualScrollContainer) {
      wheelbuilderMobileDrawerScrollGroupToViewportCenter(
        nextUnselectedGroup,
        actualScrollContainer,
        "auto"
      );
    } else {
      nextUnselectedGroup.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
    
    // Open the dropdown if it's closed
    const groupData = getAlpineData(nextUnselectedGroup);
    if (groupData && typeof groupData.expanded !== 'undefined') {
      groupData.expanded = true;
    }
  },

  // Open first dropdown (when switching to different tab)
  openFirstDropdown() {
    const tabpanelsContainer = this.getTabpanelsContainer();
    if (!tabpanelsContainer) return;

    /* Prefer panel by selectedTab index — visibility/x-show can lag one frame; wrong panel
       left openFirstDropdown expanding the Complete tab while Front/Rear UI shows empty accordions. */
    const panelSection =
      this.getPanelSectionForSelectedTab() ||
      this.getActivePanelSection() ||
      Array.from(tabpanelsContainer.querySelectorAll(":scope > section")).find(
        (panel) => window.getComputedStyle(panel).display !== "none"
      );

    if (!panelSection) return;

    /* === Open first UNFILLED, not blindly the first option ===
       Old behavior was `index === 0` — which on a preset URL deep-link meant we'd
       pop open an accordion that was already filled in by the preset (spoke / hub /
       rim_size), leaving the actually-next-needed accordion collapsed. The user
       reported this as "selected dropdowns are not closed" on mobile preset cards.
       New rule: scan completeOptions in order, open the first one whose value is
       still empty. Disabled / unavailable groups (e.g. constraint-locked) are
       skipped — `isInputGroupDisabled` is the source of truth for that. If every
       option already has a value (full preset / returning to a complete build), we
       fall back to opening index 0 so the user sees choice tiles instead of a
       collapsed-only stack — same behavior as `ensureRimFullPresetAccordion`. */
    const form = panelSection.querySelector('[x-data^="wheelBuilderForm"]');
    const formData = form ? getAlpineData(form) : null;
    const opts = formData && Array.isArray(formData.completeOptions) ? formData.completeOptions : [];

    let targetIdx = -1;
    if (opts.length) {
      for (let i = 0; i < opts.length; i++) {
        const o = opts[i];
        if (!o) continue;
        if (String(o.value || "").trim()) continue;
        if (typeof formData.isInputGroupDisabled === "function" && formData.isInputGroupDisabled(o.id)) continue;
        targetIdx = i;
        break;
      }
      if (targetIdx === -1) targetIdx = 0;
      opts.forEach((option, index) => {
        if (!option) return;
        option.expanded = index === targetIdx;
      });
    }

    /* DOM fallback for sections whose Alpine form didn't resolve (rare race during
       initial `init` before form `x-data` evaluates). Mirrors the targetIdx chosen
       above so the visible accordion matches the data layer. */
    const groups = panelSection.querySelectorAll("[data-option-group]");
    if (!opts.length && groups.length) {
      const firstGroup = groups[0];
      const groupData = getAlpineData(firstGroup);
      if (groupData && typeof groupData.expanded !== "undefined") {
        groupData.expanded = true;
      }
    }
  },

  // Open first dropdown AND scroll to top (for page 2 mobile)
  openFirstDropdownAndScroll() {
    // Open the dropdown first
    this.openFirstDropdown();
    
    // Then scroll to top on mobile
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        const scrollRoot = this.getActiveMobileOptionsScrollRoot();
        if (scrollRoot) {
          scrollRoot.scrollTo({ top: 0, behavior: "smooth" });
        }
      }, 150); // Wait for dropdown to open
    }
  },

});

// =============================================================================
// PRODUCT API
// =============================================================================

/** In-memory cache for product fetches to reduce 429 rate limits. TTL 2 minutes. */
const productFetchCache = new Map();
const PRODUCT_CACHE_TTL_MS = 120000;

function getCachedProduct(handle) {
  const entry = productFetchCache.get(handle);
  if (!entry) return null;
  if (Date.now() - entry.at > PRODUCT_CACHE_TTL_MS) {
    productFetchCache.delete(handle);
    return null;
  }
  return entry.result;
}

function setCachedProduct(handle, result) {
  productFetchCache.set(handle, { result, at: Date.now() });
}

/**
 * Fetch product data by handle from Shopify's product.js endpoint.
 * Retries on 429 with exponential backoff. Fetches sequentially when called in series.
 * @param {string} handle - Product handle
 * @returns {Promise<{product?: Object, errors?: string[]}>}
 */
async function fetchProductByHandle(handle) {
  if (!handle) {
    return {
      errors: ["Empty product handle provided"],
    };
  }

  const cached = getCachedProduct(handle);
  if (cached) return cached;

  const url = `/products/${handle}.js`;
  const maxRetries = 3;
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise((r) => setTimeout(r, delayMs));
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const response = await fetch(url, {
        method: "GET",
        signal: controller.signal,
        headers: {
          Accept: "application/json",
        },
      });

      clearTimeout(timeoutId);

      if (response.status === 429 && attempt < maxRetries) {
        lastError = `Rate limited (429), retrying...`;
        continue;
      }

      if (!response.ok) {
        if (response.status === 404) {
          return {
            errors: [`Product not found: ${handle}`],
            product: {
              available: false,
              inventory_quantity: 0,
              variants: [{ available: false }],
            },
          };
        }
        return {
          errors: [`HTTP error ${response.status}: ${response.statusText}`],
          product: {
            available: false,
            inventory_quantity: 0,
            variants: [{ available: false }],
          },
        };
      }

      const product = await response.json();
      const result = { product };
      setCachedProduct(handle, result);
      return result;
    } catch (error) {
      lastError = error.message || "Unknown error fetching product";
      if (error.name !== "AbortError") {
        console.warn(`Error fetching product ${handle} (attempt ${attempt + 1}):`, error);
      }
      if (attempt === maxRetries) {
        return {
          errors: [lastError],
          product: {
            available: false,
            inventory_quantity: 0,
            variants: [{ available: false }],
          },
        };
      }
    }
  }

  return {
    errors: [lastError || "Failed to fetch product"],
    product: {
      available: false,
      inventory_quantity: 0,
      variants: [{ available: false }],
    },
  };
}

// =============================================================================
// Pay-row price display (USD / CAD / EUR label left of amount)
// =============================================================================
function wbResolveBuilderCurrency() {
  return String(
    window.__WHEEL_BUILDER_CURRENCY__ ||
      window.__HUB_BUILDER_CURRENCY__ ||
      window.Shopify?.currency?.active ||
      document.documentElement.getAttribute("data-currency") ||
      document.querySelector('meta[property="og:price:currency"]')?.getAttribute("content") ||
      "USD"
  ).toUpperCase();
}

function wbResolveBuilderLocale() {
  const country =
    window.__WHEEL_BUILDER_COUNTRY__ ||
    window.__HUB_BUILDER_COUNTRY__ ||
    window.Shopify?.country ||
    "";
  const baseLocale = window.Shopify?.locale || "en";
  return country ? `${baseLocale}-${country}` : baseLocale;
}

function wbPayRowCurrencyLabel(currencyCode) {
  switch (String(currencyCode || "").toUpperCase()) {
    case "USD":
      return "USD";
    case "CAD":
      return "CAD";
    case "EUR":
      return "€";
    default:
      return null;
  }
}

function wbFormatAmountOnly(value) {
  return new Intl.NumberFormat(wbResolveBuilderLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function wbBuildPayRowPriceFields(numericValue, currencyCode) {
  const code = String(currencyCode || wbResolveBuilderCurrency()).toUpperCase();
  const label = wbPayRowCurrencyLabel(code);
  const amount = wbFormatAmountOnly(numericValue);

  if (!label) {
    try {
      return {
        currencyLabel: null,
        priceAmount: null,
        price: new Intl.NumberFormat(wbResolveBuilderLocale(), {
          style: "currency",
          currency: code,
        }).format(Number(numericValue || 0)),
      };
    } catch (_) {
      return { currencyLabel: null, priceAmount: null, price: `${code} ${amount}` };
    }
  }

  return {
    currencyLabel: label,
    priceAmount: amount,
    price: `${label}\u00a0${amount}`,
  };
}

// =============================================================================
// ALPINE.JS COMPONENT: WHEEL BUILDER FORM
// =============================================================================
// Manages form state, product fetching, pricing, and cart operations
const wheelBuilderForm = ({ optionKeys = [], derivedOptionsTab = "", wbCopy: wbCopyOpt = null }) => {
  const initialKeys = Array.isArray(optionKeys) ? optionKeys : [];
  return {
  wbCopy: mergeWheelbuilderCopy(wbCopyOpt || undefined),
  completeOptions: initialKeys.map((key, index) => ({
    id: key,
    value: "",
    expanded: window.innerWidth > 768 ? index === 0 : false,
  })),
  /** Plain rows for teleported mobile summary `x-for` (synced from completeOptions; getters are unreliable here). */
  mobileSummarySheetRows: [],
  derivedOptionsTab,
  derivedOptionsReady: false,
  derivedOptionsData: null,
  products: {
    front: null,
    rear: null,
  },
  unavailable: false,
  loading: false,
  isFetchingProducts: false,
  error: null,
  priceCalculated: false,
  showProductUnavailableMessage: false,
  loadingOperations: 0,
  loadingComplete: true,
  addedToCart: false,
  addToCartSubmitting: false,
  mobileCartAddedOpen: false,
  mobileCartAddedLines: [],
  _mobileCartAddedEscapeHandler: null,
  /* Transient "Going to checkout…" busy state. Flipped true inside
     `goToCheckout()` so the cart-CTA's existing busy-overlay machinery
     (spinner + status copy) lights up the instant the user taps, before
     `window.location.href` actually unloads the page. Resets to false
     on init in case the user lands back here via the bfcache. */
  isCheckingOut: false,
  _constraintsDebounceId: null,
  _applySelectionDebounceId: null,
  _handleValueSetGen: 0,

  async init() {
    this.showProductUnavailableMessage = false;
    this.loadingOperations = 0;
    this.loadingComplete = true;
    this.addedToCart = false;
    this.addToCartSubmitting = false;
    this.mobileCartAddedOpen = false;
    this.mobileCartAddedLines = [];
    this.isCheckingOut = false;
    this.watchOptions();
    this.$watch("loadingComplete", () => this.setMessageVisibility());
    this.$watch("products", () => this.setMessageVisibility(), { deep: true });
    
    // Track previous completion state for scroll trigger
    this.wasComplete = false;
    
    // Watch for changes in selections: re-apply rule constraints (filter stored rules client-side)
    // Register before derived-options hydration so the first `completeOptions` assignment syncs the mobile sheet rows.
    this.$watch("completeOptions", () => {
      this.syncMobileSummarySheetRows();
      clearTimeout(this._constraintsDebounceId);
      this._constraintsDebounceId = setTimeout(() => {
        this.applyRuleAvailabilityConstraints();
      }, 16);
      // Server-side progressive filtering: refetch with current selections (ECOM Engine only)
      clearTimeout(this._refetchDebounceId);
      this._refetchDebounceId = setTimeout(() => {
        const priorSelections = {};
        (this.completeOptions || []).forEach((o) => {
          if (o?.value && o?.id) priorSelections[o.id] = o.value;
        });
        const selKey = JSON.stringify(priorSelections);
        const hadSelections = this._lastRefetchSelectionsKey && this._lastRefetchSelectionsKey !== "{}";
        const hasSelections = Object.keys(priorSelections).length > 0;
        const changed = selKey !== (this._lastRefetchSelectionsKey ?? null);
        if (changed && (hasSelections || hadSelections)) {
          this._lastRefetchSelectionsKey = selKey;
          document.dispatchEvent(new CustomEvent('wheelbuilder:refetch-with-selections', { detail: { selections: priorSelections } }));
        }
      }, 120);
      // Reset addedToCart when user changes any selection
      if (this.addedToCart) {
        this.addedToCart = false;
      }
      if (this.mobileCartAddedOpen) {
        this.closeMobileCartAddedSheet();
      }
      if (this.addToCartSubmitting) {
        this.addToCartSubmitting = false;
      }
      
      this.wasComplete = this.isEveryOptionSelected();
      this.notifyFormStateChanged();
    }, { deep: true });

    if (this.derivedOptionsTab) {
      const checkDerived = () => {
        const store = typeof Alpine !== 'undefined' && Alpine.store && Alpine.store('wheelbuilder');
        if (!store) return;
        const opts = store.getDerivedOptionsForTab(this.derivedOptionsTab);
        const keys = store.getKeysForTab(this.derivedOptionsTab);
        if (keys && keys.length > 0) {
          this.derivedOptionsReady = true;
          wheelBuilderFilterCache.clear();
          const existingByKey = Object.fromEntries(
            (this.completeOptions || []).filter((o) => o?.value).map((o) => [o.id, o.value])
          );
          this.completeOptions = keys.map((k, i) => ({
            id: k,
            value: existingByKey[k] ?? '',
            /* First row open on desktop — same when cfg preset filled everything (tiles visible, not summary-only headers). */
            expanded: window.innerWidth > 768 ? i === 0 : false,
          }));
          this.derivedOptionsData = opts;
          this.$nextTick(() => this.applyRuleAvailabilityConstraints());
        }
      };
      checkDerived();
      document.addEventListener('wheelbuilder:rules-loaded', checkDerived);
    }
    
    // Listen for cart updates - reset checkout button if cart is empty
    this.cartUpdateHandler = this.onCartUpdate.bind(this);
    document.addEventListener('cart:refresh', this.cartUpdateHandler);
    document.addEventListener('cart:updated', this.cartUpdateHandler);
    
    // Re-apply constraints and fetch products when rules finish loading (async)
    this.rulesLoadedHandler = () => {
      this.applyRuleAvailabilityConstraints();
      if (this.isEveryOptionSelected?.() && (this.selectedTab === "wheelset" || this.selectedTab === "complete" || this.selectedTab === "front" || this.selectedTab === "rear")) {
        this._debouncedApplySelection();
      }
    };
    document.addEventListener('wheelbuilder:rules-loaded', this.rulesLoadedHandler);
    
    // Also listen for theme's cart update event
    if (window.FoxThemeEvents) {
      this.foxCartUnsubscribe = window.FoxThemeEvents.subscribe('cart-update', this.cartUpdateHandler);
    }
    
    // Initial rule-based constraint application
    this.applyRuleAvailabilityConstraints();
    this.$nextTick(() => this.syncMobileSummarySheetRows());
  },
  
  scrollToNavBar() {
    // Only scroll on desktop
    if (window.innerWidth < 769) return;
    
    const navSection = document.querySelector('.wheelbuilder-nav-section');
    if (!navSection) return;
    
    const header = document.querySelector('header, .header, [data-header]');
    const headerHeight = header ? header.offsetHeight : 80;
    
    const elementPosition = navSection.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20;
    
    window.scrollTo({
      top: Math.max(0, offsetPosition),
      behavior: 'smooth'
    });
  },
  
  onCartUpdate(event) {
    // Check if cart is empty and reset addedToCart
    const cart = event?.detail?.cart;
    if (cart && cart.item_count === 0) {
      this.addedToCart = false;
      this.notifyFormStateChanged();
    }
  },
  
  goToCheckout() {
    /* Self-debounce: if we already flipped `isCheckingOut` true on a
       previous tap and the navigation is still pending, every extra
       tap was previously firing another haptic + another rAF +
       another `location.href = '/checkout'` assignment, which on
       slow networks read as a haptic stutter and could even queue
       multiple history entries. One ask, one trip. */
    if (this.isCheckingOut) return;

    /* Order of operations matters here — all four steps run inside the
       same click-handler synchronous tick so iOS transient
       user-activation is still alive for the haptic:

       1. Fire haptic FIRST so it dispatches into the Taptic Engine
          while we're guaranteed to be inside the activation window.
          (Past this point any further DOM work or async boundary
          would risk iOS marking activation as consumed.)

       2. Flip `isCheckingOut` so `cartBtnBusyOverlayVisible` returns
          true → the existing spinner + "Going to checkout…" copy paints
          on the cart button. Without this visual feedback the tap feels
          unacknowledged during the ~200-1500ms it takes for the
          /checkout page to begin rendering.

       3. Force a state-change broadcast so Alpine re-renders any
          peek-CTA mirrors in the same frame (the closed-drawer button
          shares the same form-data getter; this keeps both buttons in
          sync, otherwise the peek mirror could stay reading "CHECKOUT"
          while the expanded one shows the spinner).

       4. Defer the actual navigation by one paint frame (rAF) so the
          loading state actually reaches the screen before the browser
          starts unloading the document. Without this, on fast networks
          the new /checkout page can begin painting before the busy
          overlay has a chance to flush — the user sees no feedback at
          all and the tap feels broken. One rAF is enough on every
          tested device; longer setTimeouts add perceptible lag.

       5. Arm a watchdog. If the navigation never actually happens
          (offline, popup blocker, browser intervention, /checkout
          returns a non-navigating response, etc.) we DO NOT want the
          button to be stuck on "Going to checkout…" forever. After
          8 seconds with no `pagehide`, we clear the flag and broadcast
          state so the user can try again. `pagehide` cancels the timer
          (set on `_checkoutWatchdogId` so a successful navigation
          path tears down cleanly even if Alpine re-evaluates first). */
    triggerHaptic('confirm', true);
    this.isCheckingOut = true;
    this.notifyFormStateChanged();
    /* Hold for ~200ms before navigating so the user actually SEES the
       spinner rotate for ~12-13 frames at 60fps. With a single rAF
       (~16ms) iOS started the document freeze before any motion was
       perceptible — the spinner showed up, but as a static circle.
       200ms is below the perceived-lag threshold (~300ms feels like
       lag; 200ms feels responsive) yet gives the wrapper-driven CSS
       rotation a real window to render. */
    setTimeout(() => {
      window.location.href = '/checkout';
    }, 200);
    if (this._checkoutWatchdogId) {
      clearTimeout(this._checkoutWatchdogId);
    }
    this._checkoutWatchdogId = setTimeout(() => {
      this._checkoutWatchdogId = null;
      if (this.isCheckingOut) {
        this.isCheckingOut = false;
        this.notifyFormStateChanged();
      }
    }, 8000);
    if (!this._checkoutPageHideHandler) {
      this._checkoutPageHideHandler = () => {
        if (this._checkoutWatchdogId) {
          clearTimeout(this._checkoutWatchdogId);
          this._checkoutWatchdogId = null;
        }
      };
      window.addEventListener('pagehide', this._checkoutPageHideHandler, { once: true });
    }
  },

  notifyFormStateChanged() {
    document.dispatchEvent(new CustomEvent('wheelbuilder:form-state-changed'));
  },

  startLoading() {
    this.loadingOperations++;
    this.loadingComplete = false;
    this.showProductUnavailableMessage = false;
    wbDebugSummaryForm(this, "startLoading", { loadingOperationsAfter: this.loadingOperations });
    this.notifyFormStateChanged();
  },

  endLoading() {
    if (this.loadingOperations > 0) {
      this.loadingOperations--;
    }
    wbDebugSummaryForm(this, "endLoading after decrement", { loadingOperationsAfter: this.loadingOperations });
    if (this.loadingOperations === 0) {
      setTimeout(() => {
        this.loadingComplete = true;
        wbDebugSummaryForm(this, "endLoading: loadingComplete set true (100ms defer)");
        this.notifyFormStateChanged();
      }, 100);
    } else {
      this.notifyFormStateChanged();
    }
  },

  /**
   * Format a numeric total in the visitor's active market currency.
   *
   * Resolution order for currency:
   *   1. `window.__WHEEL_BUILDER_CURRENCY__` — set by `wheelset-builder-v2.liquid` from
   *      `localization.country.currency.iso_code` so totals match Shopify Markets / catalog pricing.
   *   2. `window.Shopify.currency.active` — currency selector / Markets runtime fallback.
   *   3. `<html data-currency>` / `<meta property="og:price:currency">` — theme defaults.
   *   4. `USD` — last resort safe default.
   *
   * Locale uses `window.Shopify.locale` plus `window.__WHEEL_BUILDER_COUNTRY__` (or `Shopify.country`)
   * so number formatting (separator, symbol position) matches the market the customer is in.
   */
  formatPrice(value) {
    try {
      const currency =
        window.__WHEEL_BUILDER_CURRENCY__ ||
        window.Shopify?.currency?.active ||
        document.documentElement.getAttribute("data-currency") ||
        document.querySelector('meta[property="og:price:currency"]')?.getAttribute("content") ||
        "USD";

      const country =
        window.__WHEEL_BUILDER_COUNTRY__ ||
        window.Shopify?.country ||
        "";

      const baseLocale = window.Shopify?.locale || "en";
      const locale = country ? `${baseLocale}-${country}` : baseLocale;

      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency,
      }).format(value);
    } catch (error) {
      console.error("Price formatting error:", error);
      const fallbackCurrency =
        window.__WHEEL_BUILDER_CURRENCY__ ||
        window.Shopify?.currency?.active ||
        "USD";
      return `${fallbackCurrency} ${Number(value || 0).toFixed(2)}`;
    }
  },

  get validationHint() {
    const emptyOption = this.completeOptions.find((option) => option.value === "");
    return emptyOption ? emptyOption.id.replace(/_/g, " ") : "";
  },

  /** Locked-option hint: uses theme string `validation_hint` with {{ option }} = formatted label. */
  get validationLockMessage() {
    const emptyOption = this.completeOptions.find((option) => option.value === "");
    if (!emptyOption) return "";
    const label = this.formatLabel(emptyOption.id);
    const template = (this.wbCopy || WHEELBUILDER_DEFAULT_COPY).validationHintTemplate || "Please select {{ option }} first";
    return template.replace(/\{\{\s*option\s*\}\}/g, label);
  },

  /** Mirrors wheelBuilderTabs.isBuildResolving for markup inside this form scope (desktop + mobile CTAs). */
  get isBuildResolving() {
    if (!this.isEveryOptionSelected()) return false;
    /* `this.loading` is add-to-cart only — do not mix with config/price resolution. */
    const coreBusy = !!(
      this.isFetchingProducts ||
      this.loadingOperations > 0 ||
      this.pricing?.loading
    );
    return coreBusy || !this.isReadyForSummary();
  },

  /** Build/price pipeline + add-to-cart request (spinner). Check animation uses `.loading` only after submit succeeds. */
  get cartBtnBusyOverlayVisible() {
    if (this.addToCartSubmitting) return true;
    if (this.isFetchingProducts) return true;
    if (this.isBuildResolving) return true;
    /* While we're kicking off the /checkout navigation we share the same
       busy-overlay slot (spinner + "Going to checkout…" text) so the
       button feels uniformly responsive across every state transition. */
    if (this.isCheckingOut) return true;
    return false;
  },

  get pricing() {
    const selectedTab = this.selectedTab;
    const frontVariant = this.products.front?.variant;
    const rearVariant = this.products.rear?.variant;

    const isCompleteWheelset = selectedTab === "wheelset" || selectedTab === "complete";
    const needsBothWheels = isCompleteWheelset && (!frontVariant || !rearVariant);

    if (this.isFetchingProducts || needsBothWheels) {
      return {
        price: null,
        compareAtPrice: null,
        savings: null,
        loading: true,
      };
    }

    const frontPrice = Number(frontVariant?.price ?? 0);
    const rearPrice = Number(rearVariant?.price ?? 0);
    const frontCompareAtPrice = Number(frontVariant?.compare_at_price ?? 0);
    const rearCompareAtPrice = Number(rearVariant?.compare_at_price ?? 0);

    function calculateTotal(front, rear) {
      if (selectedTab === "front") return front;
      if (selectedTab === "rear") return rear;
      return front + rear;
    }

    const totalPrice = calculateTotal(frontPrice, rearPrice) / 100;
    const totalCompareAtPrice = calculateTotal(frontCompareAtPrice, rearCompareAtPrice) / 100;
    const isOnSale = totalCompareAtPrice > 0 && totalCompareAtPrice > totalPrice;
    const savingsDollars = isOnSale ? totalCompareAtPrice - totalPrice : 0;

    this.priceCalculated = true;

    if (totalPrice === 0) {
      return {
        currencyLabel: null,
        priceAmount: null,
        price: null,
        compareAtPriceAmount: null,
        compareAtPrice: null,
        savings: null,
        loading: false,
      };
    }

    const currency = wbResolveBuilderCurrency();
    const priceParts = wbBuildPayRowPriceFields(totalPrice, currency);
    const compareParts = isOnSale ? wbBuildPayRowPriceFields(totalCompareAtPrice, currency) : null;
    const savingsParts = savingsDollars > 0 ? wbBuildPayRowPriceFields(savingsDollars, currency) : null;

    return {
      currencyLabel: priceParts.currencyLabel,
      priceAmount: priceParts.priceAmount,
      price: priceParts.price,
      compareAtPriceAmount: compareParts?.priceAmount ?? null,
      compareAtPrice: compareParts
        ? compareParts.currencyLabel
          ? compareParts.priceAmount
          : compareParts.price
        : null,
      savings: savingsParts?.price ?? null,
      loading: false,
    };
  },

  watchOptions() {
    let previousState = this.completeOptions.map((option) => ({ ...option }));
    this.$watch("completeOptions", (newOptions) => {
      const changedOptions = newOptions.filter(
        (option, index) => option.value !== (previousState[index]?.value ?? "")
      );
      previousState = newOptions.map((option) => ({ ...option }));
      if (!changedOptions.every((option) => option.value === "")) {
        changedOptions.forEach((option) => this.handleValueSet(option));
      }
    });
  },

  activateOptionGroup(optionId, { scroll = true } = {}) {
    if (!optionId) return;
    if (this.isInputGroupDisabled(optionId)) return;

    this.completeOptions.forEach((entry) => {
      entry.expanded = entry.id === optionId;
    });

    if (!scroll) return;

    this.$nextTick(() => {
      requestAnimationFrame(() => {
        const panel = this.$root.querySelector(`[data-option-group="${optionId}"]`);
        if (panel) {
          this.scrollToPanel(panel, window.innerWidth > 768 ? "smooth" : "auto", 18);
        }
      });
    });
  },

  /**
   * Build summary (desktop or mobile sheet): jump back to configuring from this option onward.
   * Clears the clicked option and every step after it — same cascade as changing a choice mid-build.
   */
  editBuildFromSummaryRow(optionId) {
    if (!optionId) return;
    if (!this.isEveryOptionSelected?.()) return;
    if (this.isInputGroupDisabled(optionId)) return;

    const tabs = this._getTabsData();
    if (
      typeof window !== "undefined" &&
      window.innerWidth <= 768 &&
      tabs &&
      typeof wheelbuilderMobileWizardActive === "function" &&
      wheelbuilderMobileWizardActive(tabs) &&
      typeof tabs.closeMobileSummarySheet === "function"
    ) {
      tabs.closeMobileSummarySheet();
    }

    const idx = this.completeOptions.findIndex((o) => o && o.id === optionId);
    if (idx < 0) return;

    triggerHaptic();

    for (let i = idx; i < this.completeOptions.length; i++) {
      const opt = this.completeOptions[i];
      opt.value = "";
      const group = this.$root?.querySelector?.(`[data-option-group="${opt.id}"]`);
      if (group) {
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = false;
        });
      }
      opt.expanded = i === idx;
    }

    clearTimeout(this._applySelectionDebounceId);
    this._applySelectionDebounceId = null;
    this._debouncedApplySelection();

    document.dispatchEvent(new CustomEvent("wheelbuilder:update-progress"));
    this.notifyFormStateChanged();

    this.$nextTick(() => {
      requestAnimationFrame(() => {
        const panel = this.$root?.querySelector?.(`[data-option-group="${optionId}"]`);
        if (panel) {
          this.focusOptionGroup(panel);
        }
      });
    });
  },

  async handleValueSet(option) {
    if (this._presetApplying) return;

    /* NOTE: no haptic fires here. We previously fired a `confirm` haptic when
       this value-set completed the build (false → true on `isEveryOptionSelected`)
       to mark the "going to summary page" moment. In practice the last option
       in most builds is a color swatch, so users felt a light field-tap haptic
       (from the chrome.liquid touchend) immediately followed by this confirm —
       which read as a "weird" doubled vibration that was different from every
       other (non-last) field tap. Removing the haptic here keeps every field
       selection identical (single light tap), and the visual slide-in to the
       summary page provides the transition feedback. `confirm` haptic stays
       reserved for the cart button (add-to-cart / checkout) — explicit primary
       actions where the heavier pulse pairs with a single, deliberate tap. */
    document.dispatchEvent(new CustomEvent("wheelbuilder:update-progress", { bubbles: true }));
    
    const nextIndex = this.completeOptions.indexOf(option) + 1;
    const nextOption = this.completeOptions[nextIndex];
    const hasSelectionsAhead = this.completeOptions
      .slice(nextIndex)
      .some((opt) => opt.value !== "");

    if (nextOption) {
      if (hasSelectionsAhead) {
        for (let i = nextIndex; i < this.completeOptions.length; i++) {
          const subOption = this.completeOptions[i];
          subOption.value = "";
          subOption.expanded = i === nextIndex;
        }
      } else {
        this.activateOptionGroup(nextOption.id, { scroll: false });
      }
      
      if (option.value !== "") {
        // Wait for accordion expand to settle before measuring scroll target.
        // ~350ms matches body enter (~160–200ms) + layout; shorter delays
        // scrolled to stale positions and felt broken.
        this.$nextTick(() => {
          setTimeout(() => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const panel = this.$root.querySelector(`[data-option-group="${nextOption.id}"]`);
                if (panel) {
                  this.scrollToPanel(panel, window.innerWidth > 768 ? "smooth" : "auto", 18, {
                    allowScrollUp: false,
                  });
                } else {
                  let retries = 0;
                  const retryScroll = () => {
                    retries++;
                    const retryPanel = this.$root.querySelector(`[data-option-group="${nextOption.id}"]`);
                    if (retryPanel) {
                      this.scrollToPanel(retryPanel, window.innerWidth > 768 ? "smooth" : "auto", 18, {
                        allowScrollUp: false,
                      });
                    } else if (retries < 3) {
                      setTimeout(retryScroll, 100);
                    }
                  };
                  setTimeout(retryScroll, 100);
                }
              });
            });
          }, 350);
        });
      }
    } else if (option.value !== "" && typeof window !== "undefined" && window.innerWidth <= 768) {
      /* No following group (user finished the last row). Non–rim: collapse all so mobile doesn’t
         end with only the last accordion open above the CTA. Rim-only: keep the first row open with
         choice tiles (same as tapping Rim without cfg); collapsing both rows leaves summary-only headers. */
      if (this.selectedTab === "rim" && Array.isArray(this.completeOptions) && this.completeOptions.length) {
        this.completeOptions.forEach((o, i) => {
          if (o) o.expanded = i === 0;
        });
      } else {
        this.completeOptions.forEach((o) => {
          o.expanded = false;
        });
      }
    }

    // Options that affect rule availability (cascading narrow-down)
    const structuralOptions = [
      "spoke_type",
      "front_rim",
      "rear_rim",
      "rim_size",
      "hub_model",
      "brake_mount",
      "front_spacing",
      "rear_spacing",
      "freehub_driver",
      "spoke_color",
    ];

    // Re-apply rule constraints when structural options change
    if (structuralOptions.includes(option.id)) {
      this.applyRuleAvailabilityConstraints();
    }

    // When selection is complete, run immediately to show price. Otherwise debounce to avoid race conditions.
    if (this.isEveryOptionSelected?.()) {
      wbDebugSummaryForm(this, "last option selected → immediate _applySelectionAndFetchProducts", {
        lastTouchedOptionId: option?.id,
      });
      /* Sync busy + clear stale price before any await — stops one-frame ATC flash
         (old products still priced while resolve hasn't started). */
      this.resetProductsAndAvailability();
      if (!this.isFetchingProducts) {
        this.startLoading();
        this.isFetchingProducts = true;
        this.notifyFormStateChanged();
      }
      clearTimeout(this._applySelectionDebounceId);
      this._applySelectionDebounceId = null;
      this._applySelectionAndFetchProducts();
    } else {
      this._debouncedApplySelection();
    }
  },

  _debouncedApplySelection() {
    clearTimeout(this._applySelectionDebounceId);
    this._applySelectionDebounceId = setTimeout(() => {
      this._applySelectionDebounceId = null;
      this._applySelectionAndFetchProducts();
    }, 150);
  },

  async _applySelectionAndFetchProducts() {
    const gen = ++this._handleValueSetGen;
    wbDebugSummaryForm(this, "_applySelectionAndFetchProducts start", { gen });

    /* Keep busy if last-option path already flipped it; otherwise start here
       (debounced mid-build completions that become complete). */
    if (this.isEveryOptionSelected?.() && !this.isFetchingProducts) {
      this.resetProductsAndAvailability();
      this.startLoading();
      this.isFetchingProducts = true;
      this.notifyFormStateChanged();
    }

    const tabsData = this._getTabsData();
    const rulesIndex = tabsData?.wheelsetRulesIndex;
    const isWheelset = this.selectedTab === "wheelset" || this.selectedTab === "complete" || this.selectedTab === "front" || this.selectedTab === "rear";

    let validRules = [];
    let availableByOption = null;
    const idxNorm = rulesIndex?.configIndicesNorm || rulesIndex?.configIndices;
    if (isWheelset && idxNorm && rulesIndex?.configs) {
      const priorSelections = {};
      for (const o of this.completeOptions) {
        if (o.value) priorSelections[o.id] = o.value;
      }
      let indices = null;
      const intersect = (a, b) => {
        if (!a?.length) return b ? [...b] : [];
        if (!b?.length) return a ? [...a] : [];
        const setB = new Set(b);
        return a.filter((x) => setB.has(x));
      };
      for (const [key, val] of Object.entries(priorSelections)) {
        const n = this._norm(val);
        const slice = wheelbuilderRulesIndexPickKey(idxNorm, key);
        const indicesForVal = slice?.[n] ?? slice?.[val];
        if (!indicesForVal?.length) {
          indices = [];
          break;
        }
        indices = indices == null ? indicesForVal : intersect(indices, indicesForVal);
      }
      // Subtract out-of-stock configs for the current tab/region so disabled options
      // fall out of `validRules` naturally → updateAvailableOptions disables them.
      const tabKey = this.selectedTab === "front" ? "front_handle"
                  : this.selectedTab === "rear"  ? "rear_handle"
                  : "handle";
      const oosArr = rulesIndex?.unavailable?.[tabKey];
      if (Array.isArray(oosArr) && oosArr.length && indices?.length) {
        const oosSet = new Set(oosArr);
        indices = indices.filter((i) => !oosSet.has(i));
      }
      // Build per-option available-value map from ALL filtered indices (not the sliced
      // validRules) so chips reflect true availability. Fixes bug where slicing to 100
      // could hide option values that only appear in higher-indexed configs after the
      // OOS subtraction shrank the pool (e.g. Wanderer CR + region=us losing "Silver").
      if (indices?.length > 0) {
        availableByOption = Object.create(null);
        for (let n = 0; n < indices.length; n++) {
          const c = rulesIndex.configs[indices[n]];
          if (!c) continue;
          for (const k in c) {
            const v = c[k];
            if (v == null) continue;
            let set = availableByOption[k];
            if (!set) { set = availableByOption[k] = new Set(); }
            if (Array.isArray(v)) {
              for (let j = 0; j < v.length; j++) set.add(this._norm(v[j]));
            } else {
              set.add(this._norm(v));
            }
          }
        }
        const sortedIndices = [...indices].sort((a, b) => a - b);
        validRules = sortedIndices.slice(0, 100).map((i) => {
          const c = rulesIndex.configs[i] || {};
          const fields = Object.entries(c).map(([k, v]) => ({ key: k, value: String(v ?? "") }));
          return { fields, _fieldMap: Object.fromEntries(fields.map((f) => [f.key, f])) };
        });
      }
      if (this.isEveryOptionSelected?.() && indices?.length === 1) {
        const config = rulesIndex.configs[indices[0]];
        const ecomBase = tabsData?.ecomEngineUrl?.trim();
        const ecomPath = (tabsData?.ecomEngineApiPath || "").trim() || "/api/product-database/meta-objects";
        const ecomKey = (tabsData?.ecomEngineApiKey || "").trim();
        const ecomToken = (tabsData?.ecomEngineBearerToken || "").trim();
        const modelHandle = tabsData?.model ? (typeof tabsData.model === "string" ? tabsData.model : tabsData.model.handle || tabsData.model.value || tabsData.model) : "";
        if (ecomBase) {
          const params = { ...config, rim_model: modelHandle, model: modelHandle };
          const lookupRes = await fetchLookupFromEcomEngine(ecomBase, ecomKey, ecomPath, ecomToken, params);
          if (gen !== this._handleValueSetGen) return;
          if (lookupRes.metaObject) {
            validRules = [toRule(lookupRes.metaObject)];
          } else if (lookupRes.ambiguous) {
            this.error = "Multiple wheel builds match these options; please narrow your selection.";
          }
        }
      } else if (indices?.length > 1) {
        const firstIdx = [...indices].sort((a, b) => a - b)[0];
        const config = rulesIndex.configs[firstIdx];
        const ecomBase = tabsData?.ecomEngineUrl?.trim();
        const ecomPath = (tabsData?.ecomEngineApiPath || "").trim() || "/api/product-database/meta-objects";
        const ecomKey = (tabsData?.ecomEngineApiKey || "").trim();
        const ecomToken = (tabsData?.ecomEngineBearerToken || "").trim();
        const modelHandle = tabsData?.model ? (typeof tabsData.model === "string" ? tabsData.model : tabsData.model.handle || tabsData.model.value || tabsData.model) : "";
        if (ecomBase) {
          const params = { ...config, rim_model: modelHandle, model: modelHandle };
          const lookupRes = await fetchLookupFromEcomEngine(ecomBase, ecomKey, ecomPath, ecomToken, params);
          if (gen !== this._handleValueSetGen) return;
          if (lookupRes.metaObject) {
            validRules = [toRule(lookupRes.metaObject)];
            this.error = null;
          } else if (lookupRes.ambiguous) {
            this.error = "Multiple wheel builds match these options; please narrow your selection.";
          }
        }
      }
    } else {
      validRules = this.filterRules();
    }

    if (gen !== this._handleValueSetGen) return;

    this.updateAvailableOptions(validRules, availableByOption);
    this.applyRuleAvailabilityConstraints();

    const isCompleteBuild = this.selectedTab === "wheelset" || this.selectedTab === "complete";
    if (isCompleteBuild && validRules && validRules.length > 1 && !rulesIndex) {
      this.error = "Multiple wheel builds match these options; using the first match for product lookup.";
    } else if (isCompleteBuild && validRules?.length === 1 && this.error?.includes("Multiple wheel builds")) {
      this.error = null;
    } else if (!isCompleteBuild && this.error && this.error.includes("Multiple wheel builds")) {
      this.error = null;
    }
    wbDebugSummaryForm(this, "_applySelectionAndFetchProducts → await setSelectedProducts", {
      gen,
      validRulesCount: validRules?.length ?? 0,
    });
    await this.setSelectedProducts(validRules, gen);
  },

  focusOptionGroup(element) {
    if (!element) return;

    this.openDrawer();
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.scrollToPanel(element, window.innerWidth > 768 ? "smooth" : "auto", 18);
      });
    });
  },

  scrollToPanel(element, behavior = "smooth", topOffset = 30, scrollOptions = {}) {
    if (!element) return;

    const { nextGroupEnter = false } = scrollOptions;

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // On mobile, scroll within the drawer-content container
      if (window.innerWidth <= 768) {
        const tabs = this._getTabsData();
        if (tabs && typeof tabs.syncMobileDrawerContentScrollLayout === "function") {
          tabs.syncMobileDrawerContentScrollLayout();
        }
        // Find the scrollable drawer-content container closest to the element
        let scrollContainer = element.closest('.drawer-content');
        
        // If not found, try finding it via the visible tab panel
        if (!scrollContainer) {
          const tabsData = this._getTabsData();
          const section = tabsData?.getActivePanelSection?.();
          if (section) {
            scrollContainer = section.querySelector(".drawer-content");
          }
        }

        if (!scrollContainer) {
          const tabsData = this._getTabsData();
          const section = tabsData?.getPanelSectionForSelectedTab?.();
          if (section) {
            scrollContainer = section.querySelector(".drawer-content");
          }
        }

        if (!scrollContainer) {
          const tabsEl = this.$el?.closest?.('[x-data*="wheelBuilderTabs"]');
          const tabpanelsContainer =
            tabsEl && typeof tabsEl.querySelector === "function"
              ? tabsEl.querySelector('[role="tabpanels"]')
              : null;
          if (tabpanelsContainer) {
            const visiblePanel = Array.from(tabpanelsContainer.querySelectorAll(":scope > section")).find(
              (panel) => window.getComputedStyle(panel).display !== "none"
            );
            if (visiblePanel) {
              scrollContainer = visiblePanel.querySelector(".drawer-content");
            }
          }
        }
        
        if (scrollContainer) {
          const scrollPort = wheelbuilderResolveOptionsScrollContainer(scrollContainer);
          wheelbuilderMobileDrawerScrollGroupToViewportCenter(
            element,
            scrollPort || scrollContainer,
            behavior,
            topOffset
          );
        } else {
          element.scrollIntoView({
            behavior: behavior,
            block: "center",
            inline: "nearest",
          });
        }
    } else {
      element.scrollIntoView({
        behavior: behavior,
        block: "center",
        inline: "nearest",
      });
      if (behavior === "auto" && nextGroupEnter) {
        requestAnimationFrame(() => {
          element.classList.remove("wb-next-group-enter");
          void element.offsetWidth;
          element.classList.add("wb-next-group-enter");
          setTimeout(() => element.classList.remove("wb-next-group-enter"), 320);
        });
      }
    }
    });
  },

  filterRules() {
    const inScopeKeys = this.availableOptionKeysFromRules;
    const selectedOptions = this.completeOptions.filter(
      (option) =>
        option.value !== "" &&
        (inScopeKeys.size === 0 || inScopeKeys.has(option.id))
    );
    const relevantRules = this._getTabsData()?.getRelevantRules?.() || [];
    const matched = relevantRules.filter((rule) => {
      const fieldMap = Object.fromEntries(
        (rule.fields || []).map((f) => [f.key, f])
      );
      return selectedOptions.every((option) => {
        const field = fieldMap[option.id];
        if (!field || field.value == null) return false;
        const ruleValues = this._extractFieldValues(field);
        const userNorm = this._norm(option.value);
        return ruleValues.some((rv) => this._norm(rv) === userNorm);
      });
    });
    // rim_rule rows are already strictly scoped to this product line by
    // fetchBuilderRules (handle-prefix), so no line tiebreak is needed here.
    return matched;
  },

  updateAvailableOptions(validRules, availableByOption) {
    const allOptionElements = [...this.$el.querySelectorAll("[data-option]")];
    if (!validRules || validRules.length === 0) {
      allOptionElements.forEach((element) => {
        element.disabled = false;
        element.removeAttribute?.("disabled");
      });
      return;
    }
    allOptionElements.forEach((element) => {
      const { accordionPanelId } = Alpine.$data(element);
      const parentGroup = this.completeOptions.find((opt) => opt.id === accordionPanelId);
      // Always keep the current group's options enabled so the user can change their mind
      if (parentGroup && parentGroup.value) {
        element.disabled = false;
        element.removeAttribute?.("disabled");
        element.style.display = "";
        element.classList.remove("disabled", "unavailable");
        return;
      }
      let isAllowedByRules;
      if (availableByOption) {
        // Authoritative path: derived from ALL filtered (post-OOS) indices, not the sliced
        // validRules. Avoids false "sold out" when valid combos sit beyond the slice window.
        const set = availableByOption[accordionPanelId];
        isAllowedByRules = !!set && set.has(this._norm(element.value));
      } else {
        isAllowedByRules = validRules.some((rule) =>
          rule.fields.find(
            (field) => field.key === accordionPanelId && this._norm(field.value) === this._norm(element.value)
          )
        );
      }
      element.disabled = !isAllowedByRules;
    });
  },

  _getTabsData() {
    const tabsEl = this.$el?.closest?.('[x-data^="wheelBuilderTabs"]');
    return tabsEl ? getAlpineData(tabsEl) : null;
  },

  get selectedTab() {
    return this._getTabsData()?.selectedTab ?? "";
  },

  get progress() {
    return this._getTabsData()?.progress ?? 0;
  },

  get nextRequiredOptionLabel() {
    return this._getTabsData()?.nextRequiredOptionLabel ?? "";
  },

  get remainingOptionLabels() {
    return this._getTabsData()?.remainingOptionLabels ?? "";
  },

  get isBuildResolving() {
    return !!this._getTabsData()?.isBuildResolving;
  },

  openDrawer() {
    this._getTabsData()?.openDrawer?.();
  },

  // Get option keys that exist in wheelset/rim rules (normalize to snake_case for matching)
  get availableOptionKeysFromRules() {
    const formOptionIds = new Set(
      (Array.isArray(this.completeOptions) ? this.completeOptions : [])
        .map((o) => (o && o.id != null ? String(o.id) : ""))
        .filter(Boolean)
    );
    const toSnake = (k) => String(k || "").replace(/([A-Z])/g, (_, c) => `_${c.toLowerCase()}`);
    const tabsData = this._getTabsData();
    const isWheelset = this.selectedTab !== "rim";
    if (isWheelset && tabsData?.wheelsetRulesIndex?.options) {
      const keys = new Set();
      for (const k of Object.keys(tabsData.wheelsetRulesIndex.options)) {
        const sk = toSnake(k);
        if (sk === "rim_model") continue;
        keys.add(sk);
      }
      formOptionIds.forEach((id) => keys.add(id));
      return keys;
    }
    const relevantRules = tabsData?.getRelevantRules?.() || [];
    const keys = new Set();
    relevantRules.forEach((r) => {
      (r.fields || []).forEach((f) => {
        if (f.key) keys.add(toSnake(f.key));
      });
    });
    formOptionIds.forEach((id) => keys.add(id));
    return keys;
  },

  /** Same as meta-objects-api: alphanumeric-only key (15x110 vs 15x110rstc never collide) */
  _norm(val) {
    if (val == null) return "";
    if (typeof val === "string") {
      const s = val.normalize("NFKC").trim().toLowerCase();
      if (s.startsWith("[") && s.endsWith("]")) {
        try {
          const parsed = JSON.parse(val.trim());
          if (Array.isArray(parsed) && parsed.length > 0) {
            return this._norm(parsed[0]);
          }
        } catch (_) {}
      }
      return s.replace(/[^a-z0-9]/g, "");
    }
    if (typeof val === "object" && val.value != null) return this._norm(val.value);
    return String(val).normalize("NFKC").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  },

  _getRuleField(fieldMap, key) {
    if (fieldMap[key]) return fieldMap[key];
    const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    return fieldMap[camel] || fieldMap[key];
  },

  _extractFieldValues(field) {
    if (!field || field.value == null) return [];
    const v = field.value;
    if (Array.isArray(v)) return v.map((x) => this._norm(x)).filter(Boolean);
    if (typeof v === "string") {
      const s = v.trim();
      if (s.startsWith("[") && s.endsWith("]")) {
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) return parsed.map((x) => this._norm(x)).filter(Boolean);
        } catch (_) {}
      }
      return [this._norm(s)];
    }
    return [this._norm(String(v))];
  },

  _valueInSet(val, set) {
    const n = this._norm(val);
    if (!n) return false;
    return [...set].some((s) => this._norm(s) === n);
  },

  async _ensureRules() {
    let rules = this._getTabsData()?.getRelevantRules?.() || [];
    if (rules && rules.length > 0) return rules;
    return new Promise((resolve) => {
      const check = () => {
        const r = this._getTabsData()?.getRelevantRules?.() || [];
        if (r && r.length > 0) {
          document.removeEventListener("wheelbuilder:rules-loaded", handler);
          clearTimeout(t);
          resolve(r);
        }
      };
      const handler = () => {
        this.$nextTick(check);
      };
      const t = setTimeout(() => {
        document.removeEventListener("wheelbuilder:rules-loaded", handler);
        resolve(this._getTabsData()?.getRelevantRules?.() || []);
      }, 8000);
      document.addEventListener("wheelbuilder:rules-loaded", handler);
      this.$nextTick(check);
    });
  },

  /** Fast path: use rules index (configIndicesNorm) with normalized lookup. */
  _applyRuleConstraintsFromIndex(rulesIndex) {
    const inScopeKeys = this.availableOptionKeysFromRules;
    const options = rulesIndex.options || {};
    const idx = rulesIndex.configIndicesNorm || rulesIndex.configIndices || {};
    const intersect = (a, b) => {
      if (!a?.length) return b ? [...b] : [];
      if (!b?.length) return a ? [...a] : [];
      const setB = new Set(b);
      return a.filter((x) => setB.has(x));
    };

    for (let index = 0; index < this.completeOptions.length; index++) {
      const option = this.completeOptions[index];
      const container = (this.$root || this.$el)?.querySelector(`[data-option-group="${option.id}"]`);
      if (!inScopeKeys.has(option.id)) {
        if (container) container.style.display = "none";
        continue;
      }
      if (container) container.style.display = "";

      const priorSelections = {};
      for (let i = 0; i < index; i++) {
        const o = this.completeOptions[i];
        if (o.value) priorSelections[o.id] = o.value;
      }

      let indices = null;
      for (const [key, val] of Object.entries(priorSelections)) {
        const n = this._norm(val);
        const slice = wheelbuilderRulesIndexPickKey(idx, key);
        const indicesForVal = slice?.[n] ?? slice?.[val];
        if (!indicesForVal?.length) {
          indices = [];
          break;
        }
        indices = indices == null ? indicesForVal : intersect(indices, indicesForVal);
      }

      const uniqueValues = new Set();
      if (indices != null && indices.length > 0) {
        const idxSet = new Set(indices);
        const optionVals = wheelbuilderRulesIndexPickKey(options, option.id);
        const valueList = Array.isArray(optionVals) ? optionVals : [];
        const idSlice = wheelbuilderRulesIndexPickKey(idx, option.id);
        for (const v of valueList) {
          const n = this._norm(v);
          const indicesForVal = idSlice?.[n] ?? idSlice?.[v];
          if (indicesForVal?.some((i) => idxSet.has(i))) uniqueValues.add(v);
        }
      } else if (Object.keys(priorSelections).length === 0) {
        const optionVals = wheelbuilderRulesIndexPickKey(options, option.id);
        const valueList = Array.isArray(optionVals) ? optionVals : [];
        for (const v of valueList) uniqueValues.add(v);
      }

      /* Empty uniqueValues = index/rules couldn't narrow (race, transient indices [], etc.). Do not treat as
       * "invalid selection" — that cleared every value and emptied the mobile summary mid-session. */
      if (
        uniqueValues.size > 0 &&
        option.value &&
        !this._valueInSet(option.value, uniqueValues)
      ) {
        for (let i = index; i < this.completeOptions.length; i++) this.completeOptions[i].value = "";
      }

      if (!container) continue;
      const valueElements = container.querySelectorAll("[data-value]");
      valueElements.forEach((el) => {
        const v = el.getAttribute("data-value");
        const allowed = uniqueValues.size === 0 || this._valueInSet(v, uniqueValues);
        const isInput = el.tagName === "INPUT";
        if (allowed) {
          el.style.display = "";
          el.classList.remove("disabled", "unavailable");
          if (isInput) el.removeAttribute("disabled");
        } else {
          el.style.display = "none";
          el.classList.add("disabled", "unavailable");
          if (isInput) el.setAttribute("disabled", "true");
        }
      });
    }
  },

  /**
   * Rules from first fetch (all meta objects for rim model). Options show everything.
   * Rules define what's selectable: based on user selections, find unique values for
   * the next field — that's rule-controlled. Narrow progressively until products match.
   */
  async applyRuleAvailabilityConstraints() {
    try {
      const tabsData = this._getTabsData();
      const rulesIndex = tabsData?.wheelsetRulesIndex;
      const isWheelset = this.selectedTab !== "rim";

      if (
        isWheelset &&
        rulesIndex?.options &&
        (rulesIndex?.configIndicesNorm || rulesIndex?.configIndices)
      ) {
        this._applyRuleConstraintsFromIndex(rulesIndex);
        return;
      }

      const relevantRules = await this._ensureRules();
      if (!relevantRules || relevantRules.length === 0) return;

      const inScopeKeys = this.availableOptionKeysFromRules;

      for (let index = 0; index < this.completeOptions.length; index++) {
        const option = this.completeOptions[index];
        const container = (this.$root || this.$el)?.querySelector(
          `[data-option-group="${option.id}"]`
        );

        if (!inScopeKeys.has(option.id)) {
          if (container) container.style.display = "none";
          continue;
        }
        if (container) container.style.display = "";

        // 1. Filter rules by prior selections; 2. Get unique values (use pre-computed _fieldMap, memoize)
        const priorSelections = {};
        for (let i = 0; i < index; i++) {
          const o = this.completeOptions[i];
          if (o.value) priorSelections[o.id] = o.value;
        }
        const lineModel =
          this.selectedTab === "rim"
            ? wbGetTabsRimModelRaw(tabsData)
            : wbGetTabsModelRaw(tabsData);
        const modelKey = String(lineModel || "").toLowerCase().trim();
        const cacheKey = `${modelKey}:${option.id}:${JSON.stringify(priorSelections)}`;
        let uniqueValues = wheelBuilderFilterCache.get(cacheKey);
        if (!uniqueValues) {
          const ruleMatches = (rule) => {
            const fieldMap = rule._fieldMap || Object.fromEntries((rule.fields || []).map((f) => [f.key, f]));
            for (const [key, val] of Object.entries(priorSelections)) {
              const field = this._getRuleField(fieldMap, key);
              if (!field || field.value == null) return false;
              const ruleValues = this._extractFieldValues(field);
              if (!ruleValues.length) return false;
              const userNorm = this._norm(val);
              const matches = ruleValues.some((rv) => this._norm(rv) === userNorm);
              if (!matches) return false;
            }
            return true;
          };
          const matchingRules = [];
          for (let i = 0; i < relevantRules.length; i += WHEELBUILDER_CHUNK_SIZE) {
            matchingRules.push(...relevantRules.slice(i, i + WHEELBUILDER_CHUNK_SIZE).filter(ruleMatches));
            if (i + WHEELBUILDER_CHUNK_SIZE < relevantRules.length) await yieldToMain();
          }
          uniqueValues = new Set();
          for (let i = 0; i < matchingRules.length; i += WHEELBUILDER_CHUNK_SIZE) {
            const chunk = matchingRules.slice(i, i + WHEELBUILDER_CHUNK_SIZE);
            chunk.forEach((rule) => {
              const fieldMap = rule._fieldMap || Object.fromEntries((rule.fields || []).map((f) => [f.key, f]));
              const field = this._getRuleField(fieldMap, option.id);
              if (field && field.value != null) {
                this._extractFieldValues(field).forEach((v) => uniqueValues.add(v));
              }
            });
            if (i + WHEELBUILDER_CHUNK_SIZE < matchingRules.length) await yieldToMain();
          }
          if (wheelBuilderFilterCache.size > 200) wheelBuilderFilterCache.clear();
          wheelBuilderFilterCache.set(cacheKey, uniqueValues);
        }

        // 3. Validate: if option has a value but it's not in rules, clear it and all after
        if (
          uniqueValues.size > 0 &&
          option.value &&
          !this._valueInSet(option.value, uniqueValues)
        ) {
          for (let i = index; i < this.completeOptions.length; i++) {
            this.completeOptions[i].value = "";
          }
          continue;
        }

        // 4. Show only values in the filtered set (always filter - hide invalid options)
        if (!container) continue;
        const valueElements = container.querySelectorAll("[data-value]");
        valueElements.forEach((el) => {
          const v = el.getAttribute("data-value");
          const allowed = uniqueValues.size === 0 || this._valueInSet(v, uniqueValues);
          const isInput = el.tagName === "INPUT";
          if (allowed) {
            const wasHidden = el.style.display === "none" || window.getComputedStyle(el).display === "none";
            el.style.display = "";
            el.classList.remove("disabled", "unavailable");
            if (wasHidden) {
              el.classList.add("wb-option-appear");
              el.addEventListener("animationend", () => el.classList.remove("wb-option-appear"), { once: true });
            }
            if (isInput) el.removeAttribute("disabled");
          } else {
            el.style.display = "none";
            el.classList.add("disabled", "unavailable");
            if (isInput) el.setAttribute("disabled", "true");
          }
        });
        await yieldToMain();
      }
    } catch (error) {
      console.error("[WheelBuilder] applyRuleAvailabilityConstraints error:", error);
    }
  },

  resetProductsAndAvailability() {
    this.products.front = null;
    this.products.rear = null;
    this.unavailable = false;
    this.error = null;
    this.priceCalculated = false;
  },

  /** Drop early busy from `_applySelectionAndFetchProducts` when product fetch never runs. */
  _clearProductFetchBusyIfNeeded() {
    if (!this.isFetchingProducts && this.loadingOperations === 0) return;
    this.isFetchingProducts = false;
    this.endLoading();
    this.notifyFormStateChanged();
  },

  formatLabel(id) {
    if (id == null || id === "") return "";
    return String(id)
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  },

  /** Swatch image URL for hub / spoke (etc.) color rows; global map from `wheelbuilder-color-swatch-assets.liquid`. */
  summaryRowSwatchUrl(option) {
    if (typeof window === "undefined" || typeof window.wheelbuilderSummarySwatchUrl !== "function") {
      return "";
    }
    return window.wheelbuilderSummarySwatchUrl(option) || "";
  },

  syncMobileSummarySheetRows() {
    const co = this.completeOptions;
    if (!Array.isArray(co) || co.length === 0) {
      this.mobileSummarySheetRows = [];
      return;
    }
    this.mobileSummarySheetRows = co
      .filter((o) => o && String(o.value ?? "").trim() !== "")
      .map((o) => ({
        id: o?.id != null ? String(o.id) : "",
        value: o?.value != null ? String(o.value) : "",
      }));
  },

  async setSelectedProducts(matchedRules, gen) {
    if (!this.isEveryOptionSelected()) {
      wbDebugSummaryForm(this, "setSelectedProducts SKIP (not every option selected)", { gen });
      this.resetProductsAndAvailability();
      this._clearProductFetchBusyIfNeeded();
      return;
    }

    if (this.selectedTab === "driver") {
      wbDebugSummaryForm(this, "setSelectedProducts SKIP (driver tab)", { gen });
      this.resetProductsAndAvailability();
      this._clearProductFetchBusyIfNeeded();
      return;
    }

    if (!matchedRules || matchedRules.length === 0) {
      wbDebugSummaryForm(this, "setSelectedProducts SKIP (no matchedRules)", { gen });
      this.resetProductsAndAvailability();
      this._clearProductFetchBusyIfNeeded();
      return;
    }

    const isStale = () => gen != null && gen !== this._handleValueSetGen;

    this.priceCalculated = false;
    wbDebugSummaryForm(this, "setSelectedProducts: before startLoading + fetch", {
      gen,
      matchedRulesCount: matchedRules.length,
      tab: this.selectedTab,
    });
    /* Busy may already be on from `_applySelectionAndFetchProducts` (avoids ATC flash). */
    if (!this.isFetchingProducts) {
      this.startLoading();
      this.isFetchingProducts = true;
    }

    try {
      const selectedTab = this.selectedTab;
      const getVal = (k) => matchedRules[0]?.fields.find((f) => f.key === k)?.value;

      const getVariant = (product) => {
        // Always return first variant regardless of availability - no inventory checks
        if (!product?.variants?.length) {
          if (!product.variants) {
            // Product without variants array - return product as variant
            return {
              ...product,
              id: product.id,
              available: true, // Always treat as available - no inventory checks
              inventory_quantity: product.inventory_quantity,
              inventory_management: product.inventory_management,
              title: product.title,
              price: product.price,
              compare_at_price: product.compare_at_price,
            };
          }
          return null;
        }
        // First variant with available: true so we don't show "sold out" when store allows selling when out of stock
        const v = product.variants[0];
        return { ...v, available: true };
      };

      if (selectedTab === "rim") {
        const rimHandle = getVal("rim_product_handle") || getVal("rim_handle");
        if (!rimHandle) {
          throw new Error("No rim product handle found in rules");
        }
        const tabsData = this._getTabsData ? this._getTabsData() : null;
        const pageModel = wbGetTabsRimModelRaw(tabsData);
        // Defense-in-depth: the chosen rule must belong to this product line
        // (its handle must start with the product handle). Generic, no names.
        if (
          pageModel &&
          matchedRules?.[0]?.handle &&
          !wbRimRuleHandleMatchesLine(matchedRules[0].handle, pageModel)
        ) {
          throw new Error(
            "Rim configuration does not match this product line."
          );
        }

        // Cart uses ONLY the rule's rim_product_handle. Never fall back to the
        // page/product handle — that would add the wheelset PDP as a "rim".
        const rimCandidates = [String(rimHandle).trim()];

        let result = null;
        let lastRimError = null;
        for (let i = 0; i < rimCandidates.length; i++) {
          const candidate = rimCandidates[i];
          const candidateResult = await fetchProductByHandle(candidate);
          if (candidateResult?.product && !candidateResult?.errors) {
            result = candidateResult;
            break;
          }
          if (candidateResult?.errors) {
            lastRimError = Array.isArray(candidateResult.errors)
              ? candidateResult.errors[0]
              : candidateResult.errors;
          }
        }
        if (!result || !result.product) {
          throw new Error(lastRimError || "Rim product not found");
        }

        const variant = getVariant(result.product);
        if (!variant) {
          throw new Error("No variant found for rim product");
        }

        if (isStale()) return;
        this.products = {
          front: {
            ...result.product,
            variant,
          },
          rear: null,
        };
        wbDebugSummaryForm(this, "setSelectedProducts: rim product assigned", { gen });
      } else {
        const isCompleteBuild = selectedTab === "wheelset" || selectedTab === "complete";
        let frontResult = null;
        let rearResult = null;
        let frontHandle = null;
        let rearHandle = null;

        if (selectedTab === "front" || isCompleteBuild) {
          frontHandle = getVal("front_wheel_product_handle");
          if (!frontHandle) {
            throw new Error("No front wheel product handle found in rules (field: front_wheel_product_handle)");
          }
        }

        if (selectedTab === "rear" || isCompleteBuild) {
          rearHandle = getVal("rear_wheel_product_handle");
          if (!rearHandle) {
            throw new Error("No rear wheel product handle found in rules (field: rear_wheel_product_handle)");
          }
        }

        // Fetch sequentially to avoid 429 rate limits (Shopify theme dev throttles parallel requests)
        if (frontHandle) {
          frontResult = await fetchProductByHandle(frontHandle);
          if (frontResult.errors) {
            throw new Error(Array.isArray(frontResult.errors) ? frontResult.errors[0] : frontResult.errors);
          }
          wbDebugSummaryForm(this, "setSelectedProducts: front wheel fetch done", { gen, frontHandle });
          if (rearHandle) await new Promise((r) => setTimeout(r, 300));
        }
        if (rearHandle) {
          rearResult = await fetchProductByHandle(rearHandle);
          if (rearResult.errors) {
            throw new Error(Array.isArray(rearResult.errors) ? rearResult.errors[0] : rearResult.errors);
          }
          wbDebugSummaryForm(this, "setSelectedProducts: rear wheel fetch done", { gen, rearHandle });
        }

        if (isStale()) return;
        if (frontResult && frontResult.product) {
          const frontVariant = getVariant(frontResult.product);
          if (!frontVariant || !frontVariant.id) {
            throw new Error("No valid variant found for front wheel product");
          }
          this.products.front = {
            ...frontResult.product,
            variant: frontVariant,
          };
        }

        if (rearResult && rearResult.product) {
          const rearVariant = getVariant(rearResult.product);
          if (!rearVariant || !rearVariant.id) {
            throw new Error("No valid variant found for rear wheel product");
          }
          this.products.rear = {
            ...rearResult.product,
            variant: rearVariant,
          };
        }
      }

      await delay(300);
      // No inventory checks - always treat products as available
      this.unavailable = false;
      wbDebugSummaryForm(this, "setSelectedProducts: try block finished (before finally)", { gen });
    } catch (error) {
      wbDebugSummaryForm(this, "setSelectedProducts: CATCH", { gen, message: error?.message });
      this.error = toProductError(error.message);
      this.resetProductsAndAvailability();
    } finally {
      this.isFetchingProducts = false;
      wbDebugSummaryForm(this, "setSelectedProducts: FINALLY (before endLoading)", { gen });
      this.endLoading();
      this.setMessageVisibility();
      this.notifyFormStateChanged();
      wbDebugSummaryForm(this, "setSelectedProducts: FINALLY complete (after notifyFormStateChanged)", { gen });
    }
  },

  /** Split pay-row: tap on the price column runs the same cart CTA handler. */
  runPayRowCartClick(event) {
    const row = event?.currentTarget;
    if (!row?.classList?.contains("has-price")) return;
    if (event.target.closest(".wheelbuilder-cart-btn")) return;
    const btn = row.querySelector(".wheelbuilder-cart-btn");
    if (!btn || btn.disabled) return;
    btn.click();
  },

  buildMobileCartAddedLines() {
    const subtitle = this.completeOptions
      .filter((o) => o?.value)
      .map((o) => o.value)
      .join(", ");
    const selectedTab = this.selectedTab;
    const lineFromProduct = (product, variant, label = "") => {
      if (!product) return null;
      const title = label ? `${label}: ${product.title}` : product.title;
      const variantTitle = variant?.title && variant.title !== "Default Title" ? variant.title : "";
      const detail = [variantTitle, subtitle].filter(Boolean).join(" · ");
      return {
        title,
        image: wheelbuilderProductImageUrl(product),
        subtitle: detail,
      };
    };

    if (selectedTab === "rim" && this.products?.front) {
      return [lineFromProduct(this.products.front, this.products.front.variant)].filter(Boolean);
    }
    if (
      (selectedTab === "wheelset" || selectedTab === "complete") &&
      this.products?.front &&
      this.products?.rear
    ) {
      return [
        lineFromProduct(this.products.front, this.products.front.variant, "Front"),
        lineFromProduct(this.products.rear, this.products.rear.variant, "Rear"),
      ].filter(Boolean);
    }
    if (selectedTab === "front" && this.products?.front) {
      return [lineFromProduct(this.products.front, this.products.front.variant)].filter(Boolean);
    }
    if (selectedTab === "rear" && this.products?.rear) {
      return [lineFromProduct(this.products.rear, this.products.rear.variant)].filter(Boolean);
    }

    const fallbackTitle =
      this.$root?.querySelector?.(".mobile-options-intro__title")?.textContent?.trim() ||
      "Your build";
    return [{ title: fallbackTitle, image: "", subtitle }];
  },

  wbMobileCartAddedOpen() {
    // `this.$el` inside a teleported template points to the node in <body>, not
    // the form element, so wheelbuilderReadPanelTab(this.$el) returns null.
    // We use _mobileCartAddedTab stored at show-time instead.
    if (!this.mobileCartAddedOpen || !wheelbuilderMobileCartAddedEnabled()) return false;
    return true;
  },

  _setupMobileCartAddedA11y() {
    if (!wheelbuilderMobileCartAddedEnabled()) return;
    document.body.classList.add("wheelbuilder-mobile-cart-added-open");
    document.documentElement.classList.add("wheelbuilder-mobile-cart-added-open");
    if (this._mobileCartAddedEscapeHandler) {
      document.removeEventListener("keydown", this._mobileCartAddedEscapeHandler, true);
    }
    this._mobileCartAddedEscapeHandler = (e) => {
      if (e.key !== "Escape") return;
      if (!this.wbMobileCartAddedOpen()) return;
      e.preventDefault();
      e.stopPropagation();
      this.closeMobileCartAddedSheet();
    };
    document.addEventListener("keydown", this._mobileCartAddedEscapeHandler, true);
  },

  _teardownMobileCartAddedA11y() {
    document.body.classList.remove("wheelbuilder-mobile-cart-added-open");
    document.documentElement.classList.remove("wheelbuilder-mobile-cart-added-open");
    if (this._mobileCartAddedEscapeHandler) {
      document.removeEventListener("keydown", this._mobileCartAddedEscapeHandler, true);
      this._mobileCartAddedEscapeHandler = null;
    }
  },

  showMobileCartAddedSheet() {
    if (!wheelbuilderMobileCartAddedEnabled()) return;
    // Do NOT close the summary sheet — it hides behind the cart-added overlay
    // without needing mobileForceOptions reset (which would push options back
    // into view beneath an invisible sheet).
    this.mobileCartAddedLines = this.buildMobileCartAddedLines();
    this.mobileCartAddedOpen = true;
    this._setupMobileCartAddedA11y();
    this.notifyFormStateChanged();
  },

  closeMobileCartAddedSheet() {
    if (!this.mobileCartAddedOpen) return;
    this.mobileCartAddedOpen = false;
    this._teardownMobileCartAddedA11y();
    this.notifyFormStateChanged();
  },

  openCartDrawerFromBuilder() {
    // Prefer the theme's own CartDrawer reference (set by cart-drawer.js).
    if (window.FoxTheme?.CartDrawer && typeof window.FoxTheme.CartDrawer.openDrawer === "function") {
      window.FoxTheme.CartDrawer.openDrawer();
      return;
    }
    const drawer =
      document.getElementById("Drawer-Cart") ||
      document.querySelector("drawer-component");
    if (drawer && typeof drawer.openDrawer === "function") {
      drawer.openDrawer();
      return;
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    window.location.href = `${root}cart`;
  },

  checkoutFromMobileCartAdded() {
    this.closeMobileCartAddedSheet();
    this.goToCheckout();
  },

  async addToCart() {
    const selectedTab = this.selectedTab;

    if (!this.isEveryOptionSelected()) {
      this.error = "Please select all options before adding to cart";
      /* `force: true` — every deliberate confirm/error in this flow bypasses
         the module-level `isScrolling` gate inside `triggerHaptic`. That gate
         is only meant to suppress *incidental* taps that happen mid-scroll;
         these here are first-class system responses (validation rejection,
         success, fetch failure) and must always fire. */
      triggerHaptic('error', true);
      return;
    }
    if (this.loading || this.addToCartSubmitting) return;

    /* ============================================================
       Haptic at tap-start (synchronous, inside the click handler):

       iOS WebKit's haptic mechanism (via `ios-haptics` library
       clicking a hidden `<input switch>`) requires transient
       user-activation, which expires after ~1 second on iOS and
       reliably expires once any `await` boundary is crossed. Three
       prior attempts moved the haptic to AFTER the fetch / refresh
       / 2.1s settle — all of those pushed the haptic outside the
       activation window and iOS silently dropped them. The user's
       repeated "no haptic on add-to-cart" report is exactly that.

       Pragmatic resolution: fire `confirm` here, where we KNOW the
       activation window is alive. This means the haptic pairs with
       the user's TAP, not with the success tick — a trade-off the
       prior brainstorm flagged (H1 in the spec). iOS web cannot
       deliver a confirm haptic after async work, full stop. Native
       apps haptic-on-tap for the same reason.

       A "best effort" second pulse still fires later in the success
       branch; on Android it adds; on iOS it's a no-op but harmless. */
    triggerHaptic('confirm', true);

    this.error = null;

    try {
      const items = [];
      const buildId = crypto.randomUUID();

      const optionProperties = {
        _options: JSON.stringify(
          Object.fromEntries(
            this.completeOptions.map((option) => [this.formatLabel(option.id), option.value])
          )
        ),
      };

      const isCompleteBuild = selectedTab === "wheelset" || selectedTab === "complete";

      if (selectedTab === "rim") {
        const rimVariant = this.products.front?.variant;
        if (!rimVariant || !rimVariant.id) {
          throw new Error("No rim product selected. Please ensure all options are selected.");
        }
        items.push({
          id: rimVariant.id,
          quantity: 1,
          properties: optionProperties,
        });
      } else if (
        isCompleteBuild &&
        this.products.front?.variant?.id &&
        this.products.rear?.variant?.id
      ) {
        items.push(
          {
            id: this.products.front.variant.id,
            quantity: 1,
            properties: {
              _wheelset: buildId,
              ...optionProperties,
            },
          },
          {
            id: this.products.rear.variant.id,
            quantity: 1,
            properties: {
              _wheelset: buildId,
              ...optionProperties,
            },
          }
        );
      } else if (selectedTab === "front" && this.products.front?.variant?.id) {
        items.push({
          id: this.products.front.variant.id,
          quantity: 1,
          properties: {
            _wheelset: buildId,
            ...optionProperties,
          },
        });
      } else if (selectedTab === "rear" && this.products.rear?.variant?.id) {
        items.push({
          id: this.products.rear.variant.id,
          quantity: 1,
          properties: {
            _wheelset: buildId,
            ...optionProperties,
          },
        });
      }

      if (!items.length) {
        throw new Error("No products selected. Please ensure all options are selected and products are available.");
      }

      this.addToCartSubmitting = true;
      this.loading = false;
      /* Tabs-scope peek/footer CTAs need an immediate paint — same as hub. */
      this.notifyFormStateChanged();

      // Include sections for cart drawer - same as theme's product-form.js
      const sectionsToRender = ['cart-drawer'];
      
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ 
          items,
          sections: sectionsToRender,
          sections_url: window.location.pathname
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || "Failed to add to cart");
      }

      /* Perceived speed: cart/add already succeeded. Don't block UI on
         drawer HTML refresh — run it in the background. */
      void this.refreshCartDrawerSilently(result).catch(() => {});

      /* Optimistic badge bump on tabs scope — don't wait on cart.js. */
      try {
        const tabs = this._getTabsData && this._getTabsData();
        const addedQty = items.reduce((n, it) => n + (Number(it.quantity) || 1), 0);
        if (tabs && addedQty > 0) {
          tabs.cartItemCount = Math.max(0, Number(tabs.cartItemCount) || 0) + addedQty;
        }
      } catch (_) {}

      /* Start celebration immediately on success — before any other paint work. */
      triggerHaptic('confirm', true);
      this.addToCartSubmitting = false;
      this.error = null;
      this.mobileCartAddedLines = this.buildMobileCartAddedLines();
      this.loading = true;
      this.notifyFormStateChanged();

      await new Promise((resolve) => setTimeout(resolve, WHEELBUILDER_ADDTOCART_CHECK_MS));

      this.loading = false;
      this.addedToCart = true;
      this.notifyFormStateChanged();
    } catch (error) {
      this.error = toProductError(error.message) || "We couldn't add this to your cart. Please try again.";
      triggerHaptic('error', true);
    } finally {
      this.addToCartSubmitting = false;
      this.loading = false;
      this.notifyFormStateChanged();
    }
  },

  async refreshCartDrawerSilently(addToCartResult) {
    try {
      // Update cart count
      this.updateCartCount();
      
      // Use theme's native cart update if available (updates drawer without opening)
      // The result from /cart/add.js includes sections if we requested them
      if (addToCartResult.sections && addToCartResult.sections['cart-drawer']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(addToCartResult.sections['cart-drawer'], 'text/html');
        
        // Update cart items section
        const newItems = doc.querySelector('.f-cart-drawer__items');
        const currentItems = document.querySelector('.f-cart-drawer__items');
        if (newItems && currentItems) {
          currentItems.innerHTML = newItems.innerHTML;
        }
        
        // Update cart footer (totals, checkout button)
        const newFooter = doc.querySelector('.f-drawer__footer');
        const currentFooter = document.querySelector('.f-drawer__footer');
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        }
        
        // Remove is-empty class since we just added items
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.classList.remove('is-empty');
        }
      } else {
        // Fallback: fetch the section manually
        const response = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
        const html = await response.text();
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Update cart items
        const newItems = doc.querySelector('.f-cart-drawer__items');
        const currentItems = document.querySelector('.f-cart-drawer__items');
        if (newItems && currentItems) {
          currentItems.innerHTML = newItems.innerHTML;
        }
        
        // Update footer
        const newFooter = doc.querySelector('.f-drawer__footer');
        const currentFooter = document.querySelector('.f-drawer__footer');
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
        }
        
        // Remove is-empty class
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.classList.remove('is-empty');
        }
      }
      
      // Dispatch event to notify cart was updated (but don't open)
      document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: addToCartResult } }));
      
    } catch (error) {
      console.warn('Could not refresh cart drawer:', error);
    }
  },

  updateCartCount() {
    try {
      // Try theme's native method first
      if (window.Shopify && window.Shopify.updateCartInfo) {
        window.Shopify.updateCartInfo();
        return;
      }

      fetch(`${window.Shopify.routes.root}cart.js`)
        .then((response) => response.json())
        .then((cart) => {
          // Update cart count number elements (theme uses data-cart-count-number)
          const countNumbers = document.querySelectorAll('[data-cart-count-number]');
          countNumbers.forEach((el) => {
            el.textContent = cart.item_count < 100 ? cart.item_count : '99+';
          });
          
          // Update cart count bubbles visibility
          const countBubbles = document.querySelectorAll('[data-cart-count-bubble], .cart-link__count');
          countBubbles.forEach((el) => {
            if (cart.item_count > 0) {
              el.classList.remove('is-cart-empty');
            } else {
              el.classList.add('is-cart-empty');
            }
          });
          
          // Also update any generic cart count elements
          const genericCountElements = document.querySelectorAll('.cart-count-bubble, .cart-count');
          genericCountElements.forEach((element) => {
            if (element.tagName === 'SPAN') {
              element.textContent = cart.item_count;
            } else {
              const countSpan = element.querySelector('span');
              if (countSpan) countSpan.textContent = cart.item_count;
            }
            element.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
          });
          
          // Trigger theme's cart helpers if available
          if (window.FoxThemeCartHelpers && window.FoxThemeCartHelpers.updateCartCount) {
            window.FoxThemeCartHelpers.updateCartCount(cart.item_count);
          }
        })
        .catch((error) => console.warn('Could not update cart count:', error));
    } catch (error) {
      console.warn('Error updating cart count:', error);
    }
  },

  getOptionById(id) {
    return this.completeOptions.find((option) => option.id === id) || {};
  },

  summaryCardTintStyle() {
    const fn = typeof window !== "undefined" ? window.wheelbuilderSummaryTintVars : null;
    if (typeof fn !== "function") return {};
    return fn(this.completeOptions, "wheel");
  },

  isUnavailable(frontVariant, rearVariant) {
    return false;
  },

  isEveryOptionSelected() {
    return computeWheelBuilderFormEveryOptionSelected(this);
  },

  // Allow summary view during add-to-cart loading so we don't push user back to page 2
  isReadyForSummary() {
    return (
      this.isEveryOptionSelected() &&
      !this.isFetchingProducts &&
      this.loadingComplete &&
      this.loadingOperations === 0 &&
      !this.pricing.loading &&
      !!this.pricing.price
    );
  },

  /** Mobile summary sheet / wizard step 3 — options done + in-flight product fetches finished; pricing may still resolve.
   * Do NOT require `loadingComplete`: `endLoading()` sets `loadingOperations` to 0 immediately but flips
   * `loadingComplete` true only after a 100ms timeout, so gating on both left a permanent false window and
   * blocked `mobileStep === 'summary'` / the teleported sheet after every fetch. */
  canShowMobileSummary() {
    return (
      this.isEveryOptionSelected() &&
      !this.isFetchingProducts &&
      this.loadingOperations === 0
    );
  },

  /**
   * Full-screen mobile summary: use wheelBuilderTabs + this panel’s tab (data-wb-panel-tab), not the
   * wheelbuilder store — store.isMobileViewport / showSummaryPage can desync from window width and hide
   * the teleport while option groups stay hidden (blank drawer + footer).
   */
  wbMobileSummarySheetOpen() {
    // Cart-added step replaces the summary sheet — keep the sheet hidden.
    if (this.addedToCart) return false;
    const t = this._getTabsData();
    if (!t || typeof t.getActiveFormElement !== "function") return false;
    const mobile = wheelbuilderMobileWizardActive(t);
    const myTab = wheelbuilderReadPanelTab(this.$el);
    if (!myTab || myTab !== t.selectedTab) return false;
    if (!this.canShowMobileSummary()) return false;

    const activeForm = t.getActiveFormElement();
    const activeTab = activeForm ? wheelbuilderReadPanelTab(activeForm) : "";
    const sheetForThisPanel =
      (activeTab && activeTab === myTab) ||
      ((!activeForm || !activeTab) && myTab === t.selectedTab);
    return !!(
      mobile &&
      !t.mobileForceOptions &&
      sheetForThisPanel
    );
  },

  /** Mobile wizard page 3: list may stay visible (e.g. sheet dismissed) — block accordion / option edits. */
  wbMobileSummaryStepLocksOptions() {
    const t = this._getTabsData();
    if (typeof window === "undefined" || !wheelbuilderMobileWizardActive(t)) return false;
    return !!(t && t.mobileStep === "summary");
  },

  /**
   * Desktop (≥769px): hide option list when complete so summary can cross-fade in.
   * Mobile: preserve wizard — hide when summary page is showing and build is ready.
   */
  showWheelbuilderOptionGroups() {
    const t = this._getTabsData();
    if (wheelbuilderMobileWizardActive(t)) {
      // Cart-added step replaces the option groups inline (native drawer page).
      if (this.addedToCart) return false;
      if (!this.canShowMobileSummary()) return true;
      if (!this.wbMobileSummarySheetOpen()) return true;
      /* Hide only after tabs confirm the sheet painted (reactive); reading the DOM in a getter does not re-run Alpine. */
      return !t?.summarySheetPainted;
    }
    return !this.isEveryOptionSelected();
  },

  isInputGroupDisabled(key) {
    const option = this.getOptionById(key);
    const index = this.completeOptions.indexOf(option);
    if (index === 0) return false;
    return this.completeOptions[index - 1].value === "";
  },

  clearSelections(silent = false) {
    // Haptic feedback for clear (only when user clicks button, not on tab switch)
    if (!silent) {
      /* `force: true` — the user pressed a button; this is a deliberate
         confirmation, not an incidental scroll-time tap. */
      triggerHaptic('error', true);
    }

    // 1. Reset addedToCart state
    this.addedToCart = false;
    this.addToCartSubmitting = false;
    this.closeMobileCartAddedSheet();
    /* Defensive: if the user got stranded in "Going to checkout…" (failed
       nav, bfcache edge case before the global pageshow handler ran),
       Reset is their escape hatch — make sure it fully restores them
       to the build/configure state. Also clear the watchdog so a stale
       timer can't flip the flag back on later. */
    this.isCheckingOut = false;
    if (this._checkoutWatchdogId) {
      clearTimeout(this._checkoutWatchdogId);
      this._checkoutWatchdogId = null;
    }

    // 2. Uncheck ALL radio inputs in DOM
    const radios = this.$root.querySelectorAll('input[type="radio"]');
    radios.forEach(radio => {
      radio.checked = false;
    });

    // 3. Reset Alpine completeOptions - clear values and close all accordions except first
    this.completeOptions.forEach((option, index) => {
      option.value = "";
      option.expanded = index === 0;
    });

    // 4. Reset products and availability
    this.resetProductsAndAvailability();
    
    // 5. Force close all accordion option groups except first
    const optionGroups = this.$root.querySelectorAll('[data-option-group]');
    optionGroups.forEach((group, idx) => {
      const groupData = getAlpineData(group);
      if (groupData && typeof groupData.expanded !== 'undefined') {
        groupData.expanded = idx === 0;
      }
    });
    
    // 6. Dispatch event to update progress bar
    document.dispatchEvent(new CustomEvent('wheelbuilder:selections-cleared'));
    document.dispatchEvent(new CustomEvent('wheelbuilder:update-progress'));
    this.notifyFormStateChanged();
    
    // 7. Scroll to top of wheelbuilder and focus nav (only when user clicks button)
    if (!silent) {
      this.$nextTick(() => {
        /* Mobile page 2: reset scroll on .drawer-content */
        const drawerContent =
          this.$el && typeof this.$el.querySelector === 'function'
            ? this.$el.querySelector('.drawer-content')
            : null;
        const scrollRoot = wheelbuilderResolveOptionsScrollContainer(drawerContent);
        if (scrollRoot && typeof window !== 'undefined' && window.innerWidth <= 768) {
          scrollRoot.scrollTo({ top: 0, behavior: 'instant' });
        }

        // Find the nav section (tabs) - this is the primary target
        const navSection = document.querySelector('.wheelbuilder-nav-section');
        const tablist = document.querySelector('.wheelbuilder-tablist');
        
        // Use nav section as scroll target
        const scrollTarget = navSection || tablist;
        
        if (scrollTarget) {
          // Get header height for offset
          const header = document.querySelector('header, .header, [data-header]');
          const headerHeight = header ? header.offsetHeight : 80;
          
          const elementPosition = scrollTarget.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 20;
          
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
        
        // Focus first tab after scroll
        setTimeout(() => {
          const firstTab = document.querySelector('.wheelbuilder-tab');
          if (firstTab) firstTab.focus();
        }, 500);
      });
    }
  },

  setMessageVisibility() {
    this.showProductUnavailableMessage = false;
    const selectedTab = this.selectedTab;

    if (selectedTab === "rim") {
      if (this.products.front?.variant && !this.products.front.variant.available) {
        this.showProductUnavailableMessage = true;
      }
      return;
    }

    if (
      !this.loadingComplete ||
      this.loadingOperations > 0 ||
      this.isFetchingProducts
    ) {
      return;
    }

    if (selectedTab === "front" || selectedTab === "complete") {
      if (this.products.front?.variant && !this.products.front.variant.available) {
        this.showProductUnavailableMessage = true;
        return;
      }
    }

    if (selectedTab === "rear" || selectedTab === "complete") {
      if (this.products.rear?.variant && !this.products.rear.variant.available) {
        this.showProductUnavailableMessage = true;
        return;
      }
    }
  },
  };
};

// Register Alpine.js components and wheelbuilder store (for cross-component state e.g. mobile footer visibility)
function registerAlpineComponent(name, component) {
  if (window.Alpine) {
    Alpine.data(name, component);
  } else {
    document.addEventListener("alpine:init", () => {
      Alpine.data(name, component);
    });
  }
}

async function deriveOptionsFromRules(rules) {
  const options = {};
  const list = rules || [];
  for (let i = 0; i < list.length; i += WHEELBUILDER_CHUNK_SIZE) {
    const chunk = list.slice(i, i + WHEELBUILDER_CHUNK_SIZE);
    chunk.forEach((rule) => {
      (rule.fields || []).forEach((f) => {
        if (!f.key) return;
        const vals = Array.isArray(f.value) ? f.value : (f.value != null ? [String(f.value)] : []);
        vals.forEach((v) => {
          const s = String(v || "").trim();
          if (!s) return;
          if (!options[f.key]) options[f.key] = new Set();
          options[f.key].add(s);
        });
      });
    });
    if (i + WHEELBUILDER_CHUNK_SIZE < list.length) await yieldToMain();
  }
  return Object.fromEntries(
    Object.entries(options).map(([k, set]) => [k, [...set].sort()])
  );
}

function registerWheelbuilderStore() {
  const init = () => {
    if (typeof Alpine.store !== 'function') return;
    Alpine.store('wheelbuilder', {
      showMobileFooter: true,
      showSummaryPage: false,
      /** Synced from wheelBuilderTabs.resizeHandler for form visibility logic */
      isMobileViewport: window.innerWidth <= 768,
      derivedWheelOptions: null,
      derivedRimOptions: null,
      getKeysForTab(tab) {
        const wheel = this.derivedWheelOptions || {};
        const rim = this.derivedRimOptions || {};
        const canonicalWheel = ['spoke_type', 'hub_model', 'front_rim', 'rear_rim', 'front_spacing', 'rear_spacing', 'brake_mount', 'freehub_driver', 'hub_color', 'spoke_color'];
        const canonicalRim = ['rim_size', 'hole_count'];
        const wheelKeys = canonicalWheel.filter((k) => k in wheel);
        const frontKeys = canonicalWheel.filter((k) => k in wheel && !['rear_rim', 'rear_spacing', 'freehub_driver'].includes(k));
        const rearKeys = canonicalWheel.filter((k) => k in wheel && !['front_rim', 'front_spacing'].includes(k));
        const rimKeys = canonicalRim.filter((k) => k in rim);
        if (tab === 'rim') return rimKeys;
        if (tab === 'front') return frontKeys;
        if (tab === 'rear') return rearKeys;
        return wheelKeys;
      },
      getDerivedOptionsForTab(tab) {
        return tab === 'rim' ? (this.derivedRimOptions || {}) : (this.derivedWheelOptions || {});
      },
    });
  };
  if (window.Alpine) init();
  else document.addEventListener("alpine:init", init);
}
registerWheelbuilderStore();

/** Scales desktop summary (masthead + card + CTA) to stay within the browser viewport, centered in the config column. */
function wbSummaryDesktopFit() {
  return {
    _rAF: null,
    _ro: null,
    _onResize: null,
    _onProgress: null,
    init() {
      const schedule = () => {
        if (this._rAF) return;
        this._rAF = requestAnimationFrame(() => {
          this._rAF = null;
          this.$nextTick(() => {
            this._fit();
          });
        });
      };
      this._onResize = schedule;
      this._onProgress = schedule;
      window.addEventListener("resize", this._onResize, { passive: true });
      document.addEventListener("wheelbuilder:update-progress", this._onProgress);
      const inner = this.$refs?.wbSummaryFitInner;
      if (inner && typeof ResizeObserver !== "undefined") {
        this._ro = new ResizeObserver(this._onProgress);
        this._ro.observe(inner);
      }
      schedule();
    },
    _fit() {
      const el = this.$el;
      const inner = this.$refs?.wbSummaryFitInner;
      if (!el || !inner) return;
      if (window.innerWidth < 768) {
        el.style.removeProperty("height");
        inner.style.removeProperty("transform");
        inner.style.removeProperty("width");
        inner.style.removeProperty("max-width");
        return;
      }
      if (el.offsetWidth < 2) return;

      inner.style.transform = "none";
      inner.style.width = "100%";
      inner.style.removeProperty("max-width");

      const h = Math.ceil(inner.getBoundingClientRect().height);
      const w = Math.ceil(inner.getBoundingClientRect().width);
      if (h < 2 || w < 2) return;

      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;

      const t = Math.max(0, rect.top);
      const marginBottom = 20;
      let availH = window.innerHeight - t - marginBottom;
      const vvh = window.visualViewport?.height;
      if (vvh) {
        availH = Math.min(
          availH,
          vvh - Math.max(0, t - (window.visualViewport?.offsetTop || 0) - 8)
        );
      }
      if (!Number.isFinite(availH) || availH < 72) return;

      const kH = availH / h;
      const kW = (rect.width * 0.99) / w;
      const k0 = Math.min(1, kH, kW);

      if (k0 >= 0.999) {
        el.style.removeProperty("height");
        inner.style.removeProperty("transform");
        return;
      }

      const k = Math.max(0.42, Math.min(1, k0));
      inner.style.transformOrigin = "top center";
      inner.style.willChange = "transform";
      inner.style.transform = "scale(" + k + ")";
      el.style.height = h * k + "px";
    },
  };
}

registerAlpineComponent("wheelBuilderTabs", wheelBuilderTabs);
registerAlpineComponent("wheelBuilderForm", wheelBuilderForm);
registerAlpineComponent("wbSummaryDesktopFit", wbSummaryDesktopFit);
